import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import {
    TRAINSIM_CONFIG,
    TILE_DEFS,
    ROAD_TILE_DEFS,
    DIR_DELTA, OPPOSITE, getExitDir,
    rotateConnections,
    drawTrainsimTrack,
    drawTrainsimRoad,
    getTrackSlotKey, getTrackRotation,
    deriveSwitchStates,
    deriveSwitchStatesFromConnections,
    deriveSwitchRoutesFromConnections,
} from './trainsimConfig.js';
import { TrainSimCar } from './TrainSimCar.js';
import { TrainSimLeaves } from './TrainSimLeaves.js';
import {
    randomSignalAspect,
    computeBarrierClosures,
    chooseSignalPlacement,
} from './TrainSimSignals.js';
import { SceneAudio } from '../../core/SceneAudio.js';
import { t } from '../../config/i18n.js';

const ENTITY_LABEL_STYLE = new TextStyle({
    fontFamily: 'Arial', fontSize: 14, fontWeight: 'bold', fill: '#ffffff',
});

// Track-switch flip SFX. A flip plays one of these two single-throw
// samples at random so repeated toggles don't sound mechanical.
const SWITCH_SFX_URLS = [
    'assets/simulator/sounds/meta-switch-single-A.mp3',
    'assets/simulator/sounds/meta-switch-single-B.mp3',
];
// Level-crossing bell — LOOPS continuously while any automatic
// barrier (sorompó) is CLOSED, i.e. a train is crossing the road.
// It stops the instant every barrier has raised again. The barriers
// are car-only + fully automatic now, so this is purely the
// "danger, train passing" crossing alarm.
const CROSSING_BELL_SFX_URL = 'assets/simulator/sounds/crossing-bell.mp3';

// Distant train horn — a ONE-SHOT that fires when a train first comes
// within `TRAIN_HORN_RADIUS_CELLS` of any road cell (approaching a
// level crossing). Edge-triggered per train so it doesn't repeat every
// frame while the train lingers near the road.
const DISTANT_TRAIN_HORN_SFX_URL = 'assets/simulator/sounds/distant-train-horn.mp3';

const COLOR_NAME_TO_HEX = {
    red:    TRAINSIM_CONFIG.TRAIN_COLORS.t1,
    blue:   TRAINSIM_CONFIG.TRAIN_COLORS.t2,
    yellow: TRAINSIM_CONFIG.TRAIN_COLORS.t3,
    green:  TRAINSIM_CONFIG.TRAIN_COLORS.t4,
    purple: TRAINSIM_CONFIG.TRAIN_COLORS.t5,
};

// Default trainId → colour-name mapping used as a fallback when a spawn
// or goal entity doesn't carry an explicit colour prop.
const TRAIN_ID_TO_COLOR_NAME = { t1: 'red', t2: 'blue', t3: 'yellow', t4: 'green', t5: 'purple' };

// Station artwork is rendered 30 % larger than its grid footprint so
// the buildings read as proper landmarks even on dense maps. Grid
// reservation, hit-tests, and gameplay coords stay on the original
// footprint — this is a pure visual scale on the sprite box only,
// applied uniformly to spawn-station buildings and the small "station
// marker" entity. Touch this single constant to retune.
const STATION_VISUAL_SCALE = 1.3;

// Station-people "greeter" figures (shown when a train reaches its
// destination) are drawn at this fraction of their original PNG pixel
// size. Per client direction the art is rendered at 24 %; tweak this
// single constant to retune the crowd size.
const PEOPLE_NATIVE_SCALE = 0.24;
// Slots for the two station-people PNGs. A fresh arrival drops a short
// row of 3–4 figures picked at random from these two images.
const PEOPLE_SLOT_KEYS = [
    'trainsim.station.people.large',
    'trainsim.station.people.small',
];
// Extra vertical offset (board px) pushing the greeter row DOWN from the
// goal cell so the figures stand on the platform rather than on top of
// the station building. Tweak this single value to nudge them up/down.
const PEOPLE_Y_OFFSET_PX = 60;

// Origin station of each train, matched to the Hungarian station
// PNGs assigned in `data/trainsim.config.js` (spawn.t1 = Keleti …).
// Shown UNDER the destination STOP sign so the player can match
// "Train 3 came from SZOMBATHELY" with the SZOMBATHELY building
// on the other side of the map.
const TRAIN_ID_TO_ORIGIN_CITY = {
    t1: 'KELETI',
    t2: 'NYUGATI',
    t3: 'SZOMBATHELY',
    t4: 'SZOLNOK',
    t5: 'MISKOLC-TISZAI',
};

/**
 * Train Sim board renderer.
 *
 * Reads a parsed stage JSON and:
 *   - Creates one Pixi Container per layer (added in array order so the
 *     last entry sits on top — see plan's "z-order convention").
 *   - Paints each layer's `tiles[]` and `entities[]` into its container.
 *   - Tracks the live state of switches (active = 0..N-1 routing index) and lamps
 *     ('red' | 'green') in maps keyed by entity id, redrawing those
 *     entities when toggled.
 *   - Exposes a tiny `boardApi` for the train follower so it can ask
 *     "what are the connections at (c,r) right now?" without caring
 *     about whether the cell holds a regular track tile or a switch.
 *   - Exposes a `hitTestEntity(local)` for the scene to convert a tap on
 *     the board into the entity (if any) that was tapped.
 *
 * Tiles are rendered via the same SkinResolver slot keys Pipeline uses
 * for its track sprites — so when the dev uploads a tile atlas in
 * OptionsScene, both games pick up the art. Procedural fallback uses
 * the pipeline's `drawTileGraphics` helper.
 */
export class TrainSimBoard {
    container = new Container();

    cols = 0;
    rows = 0;
    cellSize = 0;

    /** @type {Map<string, Container>} layerId -> Container in container */
    _layerContainers = new Map();
    /** @type {Array<object>} ordered as in JSON (first = bottom) */
    _layers = [];

    /** Map from "c,r" (on the gameplay layer) to its tile object. */
    _trackTileGrid = new Map();
    /**
     * Map from "c,r" on the ROAD layer to its tile object. Parallel
     * to `_trackTileGrid`: cars will route on this grid the same way
     * trains route on the track grid. A cell can appear in BOTH maps
     * (a level crossing — track on top of road), and lamps placed
     * there stop both vehicles at once.
     */
    _roadTileGrid = new Map();
    /** Map from "c,r" to switch entity (lives on gameplay layer). */
    _switchByCell = new Map();
    /**
     * Per-switch animation refs (halo + flash ring + tap chevron
     * Graphics nodes + transient flashTime counter).
     *
     * IMPORTANT: kept in a SIDE MAP keyed by entity.id rather than
     * attached directly to the switch entity. The editor's
     * `_handleTestPlay` deep-clones the stage via JSON.stringify,
     * and Pixi Graphics nodes contain back-references to their
     * shared GraphicsContext / RenderPipe, which JSON.stringify
     * refuses to serialise (circular structure error). Storing
     * the refs off the entity keeps the entity a plain JSON-safe
     * object while still letting `tickSwitches` find the gfx
     * nodes via `_switchAnim.get(entity.id)`.
     */
    _switchAnim = new Map();
    /**
     * Lazy SFX player for switch-flip sounds. Created on the first
     * toggle (so a board that never gets a switch flip never spins
     * up an audio element) and released in `destroy()`.
     * @type {import('../../core/SceneAudio.js').SceneAudio|null}
     */
    _sfx = null;
    /**
     * True while the looping level-crossing bell is sounding. Flipped
     * by `_setCrossingBell` so the loop only starts/stops on a real
     * transition (never re-triggered per frame).
     */
    _crossingBellRinging = false;
    /**
     * Persistent Set of BARRIER ids that should be closed this frame
     * (a train is within range of the crossing). Recomputed every
     * frame by `_computeBarrierClosures` (called from `tickLamps`) and
     * read back in the same tick to drive the arm + alarm ring.
     * @type {Set<string>}
     */
    _closedBarrierIds = new Set();
    /**
     * Board-level linger timer (seconds) that keeps the crossing bell
     * ringing briefly after the last barrier raises, so the audio
     * doesn't stutter when a train hovers at the proximity radius.
     */
    _bellHold = 0;
    /**
     * Semi-transparent dark Graphics rectangle inserted between the
     * road layer and the track layer. Hidden by default; flipped on
     * by the scene via `setFocusMode(true)` when the player hits the
     * FOCUS tile on the HUD. With the overlay visible, everything
     * BELOW it in the board's child stack (background sprite, grid
     * lines, background layer, road layer) renders behind a dark
     * wash — the track layer (rails, route bands, switches, lamps,
     * trains) and everything z-stacked above it (path overlay
     * markers, aim lines, route highlight pulse, confetti) stay at
     * full brightness, popping out of the dimmed scene. We choose
     * "dim everything that isn't the gameplay layer" rather than
     * spot-lit holes around individual entities because (a) trains
     * move every frame so a punched-hole vignette would lag, and
     * (b) the visual goal is to mute distractions, not literally
     * spotlight one cell at a time — keeping the rails legible is
     * essential for the puzzle to remain playable.
     */
    _focusOverlay = null;
    _focusMode = false;
    /**
     * Per-lamp animation refs, populated by `_drawLampEntity` and
     * driven each frame by `tickLamps`. Each entry holds direct
     * Graphics handles for the bulb, bulb core, halo and barrier
     * arm pivot so the tick can flash the bulb (red→white→red…)
     * and tween the barrier between closed (red) and raised
     * (green) without re-issuing geometry.
     *   {
     *     halo, bulb, core, arm,
     *     armRot, armTargetRot,
     *     state, color, phase,
     *     bezelR, poleX, bezelCY,
     *   }
     */
    _lampAnim = new Map();
    _lampAnimTime = 0;
    /**
     * Drift-animated cloud decorations (every tile whose `type` starts
     * with `sky.cloud`). Each entry is `{ node, w, speed }` where
     * `node` is the Pixi sprite/Graphics painted by
     * `_paintDecorationTile`, `w` is its painted width in px (used
     * for off-screen wrap math), and `speed` is the per-cloud drift
     * in px / Pixi-ticker-frame (deterministic from the cloud's
     * spawn position so the same stage always animates identically).
     *
     * Driven by `tickClouds(delta)` — every cloud moves in the SAME
     * direction (rightward) but at slightly different speeds so the
     * sky reads as alive rather than mechanical. Empty (and the
     * tick is a cheap no-op) for stages without clouds and in
     * editor mode, where clouds must stay put for authoring.
     */
    _cloudAnims = [];

    /**
     * Wind-blown leaves drifting across the board. Lazily
     * created in `init()` only when the player has enabled the
     * `trainsim.settings → windLeavesEnabled` toggle, so stages
     * with the effect off pay zero cost (no Container, no tick
     * work). `null` while the effect is disabled or in editor
     * mode (leaves would just hide the tile being authored).
     *
     * @type {TrainSimLeaves | null}
     */
    _leaves = null;

    /**
     * Wind-driven foliage sway. Each entry wraps a tree / bush /
     * flowerbed sprite in a Container pivoted at its BOTTOM-CENTER
     * so a small per-frame rotation reads as the plant bending at
     * its base in the breeze (not pivoting through its middle).
     *
     * Entry shape:
     *   {
     *     container: Container,   // wrapper rotated each frame
     *     baseRot:   number,      // tile.rot in radians (preserved)
     *     amp:       number,      // sway amplitude (rad), ~0.03..0.06
     *     freqRad:   number,      // angular freq (rad/sec), slow
     *     phase:     number,      // per-tile offset so neighbours
     *                             // don't sway in lockstep
     *   }
     *
     * Same gate as `_leaves`: only populated when the wind toggle
     * is on AND the board isn't in editor mode (authors need
     * static art while placing tiles). Driven by `tickWobble`.
     */
    _wobbleAnims = [];
    _wobbleTime = 0;
    _windWobbleEnabled = false;
    /**
     * Reverse map populated alongside `_lampByCell`: every road cell
     * that's directly affected by a lamp's level-crossing barrier
     * points back at that lamp. Used by `isLampBarrierClosed(c, r)`
     * in the car board-API so a single grid lookup tells a car
     * whether it must brake for the barrier on its next cell.
     *
     * Real-crossing semantics:
     *   lamp.state === 'green' → barrier DOWN (closed), cars stop
     *   lamp.state === 'red'   → barrier UP   (open),   cars pass
     * (Trains still follow the opposite — red lamp = STOP — because
     * the track signal and the road barrier are inversely linked,
     * exactly like a real Hungarian SOROMPÓ.)
     */
    _barrierByRoadCell = new Map();
    /** Map from "c,r" to lamp entity. */
    _lampByCell = new Map();
    /** Map from "c,r" to goal entity. */
    _goalByCell = new Map();
    /** Map from "c,r" to spawn entity (for click → highlight-route). */
    _spawnByCell = new Map();
    /**
     * Pre-scan caches populated by `_buildRouteIndex()` before any
     * spawn/goal entity is painted. Lets the spawn renderer ask
     * "what city does T1's goal display as its destination?" and
     * the goal renderer ask "what city does T1's spawn say it
     * came from?" — without depending on paint order.
     */
    _spawnByTrainId = new Map();
    _goalByTrainId  = new Map();
    /**
     * Active "where does this train go?" highlight. When the player taps
     * a spawn or goal we briefly draw a fat coloured ring on the OTHER
     * endpoint (and re-emphasise the tapped end) so the route the train
     * will take is unmistakable on an otherwise homogeneous map. Shape:
     *   { trainId, gfx, ttl, totalDuration }
     * `gfx` lives on `_routeHighlightLayer` and gets cleared whenever a
     * new highlight starts or the TTL drains.
     */
    _routeHighlight = null;
    _routeHighlightLayer = null;
    /**
     * Active "yoyo" scale bounces on entity gfx nodes — used when the
     * player taps the HONNAN / HOVA badge on a station info card so
     * the matching origin / destination cell visibly springs in
     * place. Each entry: { gfx, t, duration, baseScale }. Ticked
     * every frame from `tickRouteHighlight` so the existing per-frame
     * driver covers it without a new scene-side wiring.
     */
    _endpointYoyos = [];
    /**
     * Per-anchor card refs (mini Container handles) for the HONNAN /
     * HOVA badges. Kept in a SIDE MAP keyed by entity.id — NEVER
     * directly on the entity — because the editor's `_handleTestPlay`
     * deep-clones the stage via JSON.stringify, and Pixi Containers
     * contain `renderGroup` back-references that JSON.stringify
     * refuses to serialise (circular structure error). The JSON-safe
     * rect data (`_fromCardRect`, `_toCardRect`) and `_cardTrainId`
     * stay on the entity itself, but the Container handles live
     * here so press-pulse can find them without leaking Pixi nodes
     * into the saved stage payload.
     */
    _cardMinisById = new Map();
    /**
     * Confetti particle system layered above everything else on the
     * board. Each entry is { gfx, vx, vy, vrot, life, maxLife }.
     * `spawnGoalConfetti(trainId)` seeds a burst on the train's goal
     * cell; `tickGoalConfetti(delta)` advances every live particle
     * (gravity + spin + alpha fade) and recycles dead ones. The
     * container is created in `init()` alongside `_routeHighlightLayer`.
     */
    _confettiLayer = null;    _confettiParticles = [];
    /**
     * Dedicated overlay for the per-switch STOP/GO signal lamps. The
     * lamp lens is the single most important interactive glyph at a
     * junction, so it lives ABOVE the trains (which otherwise paint
     * over it — most visibly via a train's floating destination chip).
     * Switch route bars + hub stay in the normal entity gfx (so a train
     * still rides ON TOP of the junction it's crossing); only the lamp
     * lens + glow are hoisted here. zIndex 20 sits above trains / cars /
     * crowd but below the route-highlight pulse (50) and confetti (55).
     */
    _signalOverlayLayer = null;
    /**
     * Big "HAS ARRIVED" banners spawned on a goal cell when a train
     * successfully completes its route. Each entry: { gfx, life,
     * maxLife }. Lives in the same `_confettiLayer` as the particles
     * so it's automatically above stations and tracks. Driven by
     * `_tickArrivalBanners`, called from `tickGoalConfetti` so the
     * scene only needs the existing per-frame hook.
     */
    _arrivalBanners = [];
    /**
     * Persistent "greeters" sprites that appear at a goal station each
     * time a train arrives there. Each call to `addArrivedCrowd(trainId)`
     * drops a short row of 3–4 station-people figures (the
     * `station.people.*` PNGs, sized at `PEOPLE_NATIVE_SCALE` of their
     * original pixels) into `_arrivedCrowdLayer`, so a busy stage
     * progressively fills the platforms with people. Cleared only on
     * `reset()` — the crowd is meant to stick around for the whole
     * stage as a visible scoreboard of "trains delivered".
     */
    _arrivedCrowdLayer = null;
    _arrivedCrowdSprites = [];
    /** Natural {w,h} pixel size of each station-people PNG, measured
     *  lazily (per src) on first arrival and reused afterwards so the
     *  crowd row can be laid out edge-to-edge without overlap and a
     *  rewind/replay reproduces the same layout instantly. */
    _peopleSizeCache = new Map();
    /**
     * Per-train boarding FX state. Map<trainId, {
     *   wrap, ring, countdown, w, h, lastSec, popPhase, ringPhase
     * }>. Populated lazily by `_tickBoardingFx` while a train is
     * inside its 10-s pre-departure window; disposed the moment
     * the train actually starts (or boarding is cancelled).
     */
    _boardingFxLayer = null;
    _boardingFxByTrainId = null;
    /** All spawn entities, kept in spawn order. */
    spawns = [];
    /** All goal entities. */
    goals = [];
    /** All stations. */
    stations = [];

    /** Map from entity id -> { entity, gfx, layerId } for re-render on toggle. */
    _entityRegistry = new Map();

    _themeManager = null;
    _gameplayLayerId = 'track';

    /**
     * Road traffic — autonomous cars that drive along the road layer.
     * The board owns spawn / despawn / update so any scene that hosts
     * the board (gameplay or editor preview) just calls `tickCars`.
     */
    /** @type {Array<import('./TrainSimCar.js').TrainSimCar>} */
    _cars = [];
    /** Pixi container that holds every car sprite (parented to the road layer). */
    _carContainer = null;
    /** Cached adapter passed to each car so we don't rebuild it per-frame. */
    _carBoardApi = null;
    /** Ms since the last spawn attempt — paced by `_carSpawnInterval`. */
    _carSpawnTimer = 0;
    /** Ms between spawn attempts (random skip if at cap or no valid slot). */
    _carSpawnInterval = 1800;
    /** Soft cap on simultaneous cars so big networks don't bog down. */
    _carMaxCount = 10;
    /** Master toggle — set to false to suspend all car simulation. */
    _carsEnabled = true;

    /**
     * When true, the board renders editor-only affordances on top of
     * the regular sprites: faded spawn artwork (so the user can see
     * the underlying track), a bold direction arrow on every spawn
     * showing where the train will leave, and a marker on the spawn's
     * anchor cell so the multi-cell station artwork doesn't hide
     * which cell the spawn is logically pinned to. Always false in
     * actual gameplay.
     */
    _editorMode = false;

    /**
     * Path-prediction overlay. A single Graphics layer sitting above
     * the tiles + decorations but BELOW the entity sprites, on which
     * we draw every spawn's predicted route as a faded coloured line.
     * Refreshed after init, on every switch toggle, and whenever the
     * stage rebuilds. Visible by default in both editor and live game
     * — the runtime hides each train's path once it reaches its goal
     * so the board doesn't stay cluttered after victory.
     */
    _pathOverlay = null;
    /**
     * Wide coloured "aura" that sits UNDER the track tiles. The rails
     * paint over it, leaving a fat band of the route's colour visible
     * on each side of the track — so the player can read both the
     * route AND the rail at the same time without the route obscuring
     * the track underneath. Parented INSIDE the gameplay layer
     * container at child index 0 so the layer's own tiles render on
     * top of it. The thin endpoint markers (discs, X, triangle) stay
     * on `_pathOverlay` where they ride above everything else.
     */
    _pathOverlayUnder = null;
    _pathOverlayVisible = true;
    /**
     * Pool-game-style trajectory preview. A single Graphics layer
     * sitting ABOVE the tiles and tile-level decorations but BELOW
     * the train sprites, on which `tickAimLines` redraws — every
     * frame — a short "dotted aim line" projecting AIM_AHEAD_CELLS
     * cells forward from each LIVE train's current position. Dots
     * shrink and fade with distance so the player reads them as
     * "this is where the train will go next" rather than as part
     * of the static map. Tied to `_pathOverlayVisible` so the
     * existing ROUTES toggle in the HUD turns the aim lines on
     * and off together with the destination markers.
     */
    _aimLineLayer = null;
    /**
     * How many cells ahead of the train the aim-line preview
     * extends. 5 is small enough that the dots only "telegraph"
     * the immediate next decision (switch / lamp) the player
     * needs to make, without flooding the screen with the entire
     * predicted route.
     */
    static AIM_AHEAD_CELLS = 5;
    /**
     * Phase clock for the "wrong destination" pulse. Whenever a
     * predicted route ends on another train's goal cell we want the
     * red warning stroke + endpoint marker to BREATHE, so the player
     * notices it even amid the colourful path-overlay clutter.
     * `tickPathOverlayPulse` advances this and triggers a refresh
     * each frame that any wrong-goal route is in play.
     */
    _wrongGoalPulse = 0;
    _hasWrongGoal = false;

    /**
     * @param {object} themeManager
     * @param {{ editorMode?: boolean }} [options]
     */
    constructor(themeManager = null, options = {}) {
        this._themeManager = themeManager;
        this._editorMode = !!options.editorMode;
        this.container.sortableChildren = true;
    }

    /**
     * @param {object} stage parsed stage JSON
     * @param {string} [gameplayLayerId='track']
     */
    init(stage, gameplayLayerId = 'track') {
        this._gameplayLayerId = gameplayLayerId;
        this.cols = stage.grid?.cols ?? 26;
        this.rows = stage.grid?.rows ?? 13;
        this.cellSize = stage.grid?.cellSize ?? 72;
        this._layers = Array.isArray(stage.layers) ? stage.layers : [];

        // Decoration animation registries must start empty for every
        // re-init — even if the previous stage didn't go through
        // destroy(), `_paintDecorationTile` is about to repopulate
        // them from scratch.
        this._cloudAnims.length = 0;
        this._wobbleAnims.length = 0;
        this._wobbleTime = 0;
        // Wind toggle is read once per init and cached so the inner
        // loop in `_paintDecorationTile` doesn't hit `getSetting`
        // for every tile. Editor mode forces it OFF — the author
        // needs static foliage to place tiles accurately.
        this._windWobbleEnabled = !this._editorMode
            && !!this._themeManager?.getSetting?.('trainsim.settings', 'windLeavesEnabled', true);

        this._drawGridBackground();

        for (const layer of this._layers) {
            const c = new Container();
            c.label = `layer:${layer.id}`;
            c.visible = layer.visible !== false;
            this._layerContainers.set(layer.id, c);
            this.container.addChild(c);
        }

        // ── Two-pass paint ─────────────────────────────────────────
        // Pass 1: tiles only (across ALL layers). This guarantees the
        // road grid is fully populated BEFORE any entity is painted,
        // so a lamp's `_findLampBarrierCell` lookup can always see
        // the roads — regardless of whether the road layer appears
        // before or after the track layer in the stage file.
        // Pass 2: entities, which can now safely query other grids.
        for (const layer of this._layers) {
            this._paintLayerTiles(layer);
        }
        // Pre-scan spawn + goal entities so each one's render can
        // look up the OTHER end of its route ("this spawn's train
        // ends at X city", "this goal receives the train from Y
        // city") before any drawing happens. Without this the
        // spawn-vs-goal paint order would race and one side would
        // miss the other.
        this._buildRouteIndex();
        for (const layer of this._layers) {
            this._paintLayerEntities(layer);
        }

        // ── Focus-mode dimmer (between non-gameplay layers + track) ─
        // Painted ONCE here as a full-board black rectangle at low
        // alpha, then inserted at the index immediately BEFORE the
        // gameplay layer so it dims everything below it (background
        // sprite, grid lines, background layer, road layer) while
        // letting the gameplay layer + every overlay added AFTER it
        // (path markers, aim lines, route highlight, confetti) render
        // on top at full brightness.
        //
        // Hidden by default; the scene flips `visible = true` when
        // the player toggles the HUD's FOCUS tile. We paint it once
        // and reuse the same Graphics across the whole session
        // because the board dimensions don't change after init.
        this._focusOverlay = new Graphics();
        this._focusOverlay.rect(0, 0, this.cols * this.cellSize, this.rows * this.cellSize);
        this._focusOverlay.fill({ color: 0x000000, alpha: 0.55 });
        this._focusOverlay.visible = !!this._focusMode;
        this._focusOverlay.label = 'focus-overlay';
        const gameplayContainer = this.getLayerContainer(this._gameplayLayerId);
        if (gameplayContainer && gameplayContainer.parent === this.container) {
            const trackIdx = this.container.getChildIndex(gameplayContainer);
            this.container.addChildAt(this._focusOverlay, trackIdx);
        } else {
            // Degenerate stage with no gameplay layer — park the
            // overlay at the END so toggling focus still gives the
            // player visual feedback (it'll dim the whole board
            // including the few tiles painted above).
            this.container.addChild(this._focusOverlay);
        }

        // ── Path-prediction overlay (two layers) ───────────────────
        // The user can't read the rails when a thick coloured stroke
        // sits ON TOP of them, so we split the overlay in two:
        //
        //   • `_pathOverlayUnder` — a WIDE coloured band parented
        //     INSIDE the gameplay layer at child index 0, so the
        //     rail tiles paint OVER it. Result: the route's colour
        //     shows as a fat halo on each side of the rail without
        //     ever covering the rail markings themselves.
        //   • `_pathOverlay` — narrow endpoint markers (✓ / X / ⚠ /
        //     ∞), still at zIndex 5 above the world so the
        //     destination glyph is never hidden by track,
        //     decorations, or any other graphic.
        this._pathOverlayUnder = new Graphics();
        const gameplayLayer = this.getLayerContainer(this._gameplayLayerId);
        if (gameplayLayer) {
            gameplayLayer.addChildAt(this._pathOverlayUnder, 0);
        } else {
            // No gameplay layer (degenerate stage) — park the under-
            // aura at z=1 so it at least renders behind the markers.
            this._pathOverlayUnder.zIndex = 1;
            this.container.addChild(this._pathOverlayUnder);
        }

        this._pathOverlay = new Graphics();
        this._pathOverlay.zIndex = 5; // endpoint markers above tiles + entities
        this.container.addChild(this._pathOverlay);
        this._refreshPathOverlay();

        // Aim-line preview layer — sits at zIndex 6, above the
        // endpoint markers (5) but below the entity sprites (10)
        // and the route-highlight pulse (50). That way the dotted
        // forecast reads OVER the tracks (so it's never hidden by
        // a rail) but UNDER the train graphic itself (so the
        // sprite always overdraws its own preview as it advances).
        this._aimLineLayer = new Graphics();
        this._aimLineLayer.zIndex = 6;
        this.container.addChild(this._aimLineLayer);

        // Route-highlight layer sits ABOVE everything (zIndex 50, well
        // above entity sprites at 10) so the bright "this train goes
        // HERE" pulse pops over stations, switches and the path
        // overlay. Its contents are drawn by `flashTrainRoute` and
        // cleared by `tickRouteHighlight` once the TTL drains.
        this._routeHighlightLayer = new Container();
        this._routeHighlightLayer.label = 'route-highlight';
        this._routeHighlightLayer.zIndex = 50;
        this.container.addChild(this._routeHighlightLayer);

        // Confetti layer — even higher so the "you delivered the train!"
        // celebration showers down on top of every other graphic without
        // anything occluding it.
        this._confettiLayer = new Container();
        this._confettiLayer.label = 'confetti';
        this._confettiLayer.zIndex = 55;
        this.container.addChild(this._confettiLayer);
        this._confettiParticles = [];

        // Arrived-crowd layer — sprites of greeters that accumulate at
        // each goal station as trains arrive. Lives just above entity
        // sprites (zIndex 12 vs 10) so the people stand IN FRONT of
        // the station artwork, but well below confetti (55) and the
        // arrival banner so the celebration overlays still pop on
        // top. Sprites are placed in board (cell-grid) coordinates.
        this._arrivedCrowdLayer = new Container();
        this._arrivedCrowdLayer.label = 'arrived-crowd';
        this._arrivedCrowdLayer.zIndex = 12;
        this.container.addChild(this._arrivedCrowdLayer);
        this._arrivedCrowdSprites = [];

        // Boarding FX layer — pulsing yellow ring around the origin
        // station + a centred "5/4/3/2/1" countdown that pops above
        // the roof during the final 5 seconds. Sits one notch above
        // the arrived crowd so the ring/number reads on top of any
        // greeters that have already piled up at the same station.
        // Per-train state is lazy-built on first boarding tick and
        // disposed when the train actually departs.
        this._boardingFxLayer = new Container();
        this._boardingFxLayer.label = 'boarding-fx';
        this._boardingFxLayer.zIndex = 13;
        this.container.addChild(this._boardingFxLayer);
        this._boardingFxByTrainId = new Map();

        // Signal-lamp overlay — see the field doc. Sits at zIndex 20 so
        // every switch's stop/go lens floats ABOVE the trains (a train's
        // body + destination chip used to cover the lamp at junctions),
        // while the rest of the switch furniture stays down in the
        // entity layer. Board-absolute coords (this.container == board
        // space). `_drawSwitchEntity` parents each switch's lamp group
        // here; `clear()` drops the whole layer with the rest.
        this._signalOverlayLayer = new Container();
        this._signalOverlayLayer.label = 'signal-overlay';
        this._signalOverlayLayer.zIndex = 20;
        this.container.addChild(this._signalOverlayLayer);

        // Wind & falling leaves — opt-in cosmetic effect controlled by
        // the `trainsim.settings → windLeavesEnabled` toggle. Disabled
        // unconditionally in editor mode so the author isn't fighting
        // tumbling sprites while placing tiles. zIndex 12 puts the
        // leaves above tracks, decorations and trains (zIndex 10) but
        // BELOW the route highlight (50) and confetti (55), so a
        // celebrating route or arrival burst still reads through.
        const leavesEnabled = !this._editorMode
            && !!this._themeManager?.getSetting?.('trainsim.settings', 'windLeavesEnabled', true);
        if (leavesEnabled) {
            this._leaves = new TrainSimLeaves({
                x: 0, y: 0,
                w: this.cols * this.cellSize,
                h: this.rows * this.cellSize,
            });
            this._leaves.container.zIndex = 12;
            this.container.addChild(this._leaves.container);
        }

        // Road traffic container — parented to the road layer so it
        // inherits the layer's visibility toggle (turning off "Road"
        // hides the cars too). zIndex 12 puts cars above the asphalt
        // and dashed centreline but BELOW lamps / signals (which
        // typically use zIndex 20+) so signal heads still read clearly
        // even when a car is sitting at the stop bar.
        this._carContainer = new Container();
        this._carContainer.label = 'road:cars';
        this._carContainer.zIndex = 12;
        this._carContainer.sortableChildren = true;
        const roadLayer = this._layerContainers.get('road');
        if (roadLayer) {
            roadLayer.sortableChildren = true;
            roadLayer.addChild(this._carContainer);
        } else {
            // No road layer in this stage — park the container on the
            // main board container so it still exists (cars just won't
            // spawn because `_roadTileGrid` will be empty).
            this.container.addChild(this._carContainer);
        }
    }

    /** Returns the Pixi container for a given layer id (for adding trains, etc). */
    getLayerContainer(layerId) {
        return this._layerContainers.get(layerId) || null;
    }

    /** The gameplay layer container, where trains live in front of tiles. */
    getGameplayLayer() {
        return this.getLayerContainer(this._gameplayLayerId);
    }

    /**
     * Toggle Focus mode on the board. When ON, the `_focusOverlay`
     * rectangle becomes visible — it sits at the child index right
     * before the gameplay layer, so every child below (background +
     * road + scenery) renders through a 55%-opacity black wash while
     * the gameplay layer (rails, route bands, switches, lamps,
     * trains) and every overlay added afterwards (path markers, aim
     * lines, route highlight, confetti) keep full brightness.
     *
     * Cheap: just flips one display object's `visible` flag — no
     * filter graphs, no tinting passes, no per-frame work. Idempotent
     * so the scene can call it on every restart without bookkeeping.
     */
    setFocusMode(enabled) {
        this._focusMode = !!enabled;
        if (this._focusOverlay) this._focusOverlay.visible = this._focusMode;
    }

    /** Whether Focus mode is currently engaged on this board. */
    isFocusMode() {
        return !!this._focusMode;
    }

    /** Click hit-area is the full board rect; the scene installs a listener. */
    getHitArea() {
        if (this._hitArea) return this._hitArea;
        const hit = new Graphics();
        hit.rect(0, 0, this.cols * this.cellSize, this.rows * this.cellSize);
        hit.fill({ color: 0x000000, alpha: 0.001 });
        hit.eventMode = 'static';
        hit.cursor = 'pointer';
        hit.zIndex = 9999;
        this.container.addChild(hit);
        this._hitArea = hit;
        return hit;
    }

    /**
     * Public API the train follower uses. Resolves switch state into
     * the live "connections" the train should route by.
     */
    getBoardApi() {
        return {
            isInside: (c, r) => c >= 0 && c < this.cols && r >= 0 && r < this.rows,
            getConnections: (c, r) => this._getConnectionsAt(c, r),
            getSwitchExit: (c, r, entryDir) => this._getSwitchExit(c, r, entryDir),
            // A train stops when its NEXT cell is a switch whose signal
            // is RED — the player toggles these per-switch signals to
            // hold / release trains. Keys off the switch cell so it
            // gates entry from ANY approach direction.
            isSwitchSignalRed: (c, r) => {
                const sw = this._switchByCell.get(`${c},${r}`);
                return !!(sw && sw.signal === 'red');
            },
            // Legacy: lamps used to stop trains. They are now car-only
            // automatic barriers and never hold a train, so this always
            // returns false. Kept so any older caller resolves cleanly.
            isLampRed: () => false,
            isGoalFor: (c, r, trainId) => {
                const goal = this._goalByCell.get(`${c},${r}`);
                return !!(goal && goal.props?.trainId === trainId);
            },
            // True when (c,r) is ANY train's goal cell (used by the
            // train update to detect "wrong destination" arrivals —
            // landing on another train's goal counts as a crash with
            // a more helpful "rossz célállomás" warning message).
            isAnyGoalCell: (c, r) => this._goalByCell.has(`${c},${r}`),
            goalAt: (c, r) => this._goalByCell.get(`${c},${r}`) || null,
            // Absolute last-mile collision guard. Called by each
            // train the instant `_progress` wraps to 1 (i.e. it's
            // about to physically step into `_nextCol/_nextRow`).
            // Returns true if ANY other live, started, alive,
            // not-yet-arrived train currently sits on `(c, r)`.
            // The train clamps its progress to 0.999 when this
            // returns true, so two trains can NEVER end up on the
            // same cell regardless of switch races or per-frame
            // iteration order.
            isCellOccupiedByOther: (c, r, excludeTrainId) => {
                const live = this._liveTrains;
                if (!live) return false;
                for (const tr of live) {
                    if (!tr || tr.trainId === excludeTrainId) continue;
                    if (!tr._started || !tr._alive || tr._reachedGoal) continue;
                    if (tr._col === c && tr._row === r) return true;
                }
                return false;
            },
        };
    }

    /**
     * Map a local click position (relative to the board container) to
     * an interactive entity (lamp or switch). Returns null if the tap
     * landed on a non-interactive cell.
     */
    hitTestEntity(localX, localY) {
        // Station info-card badges (HONNAN / HOVA) win the hit-test
        // FIRST when the click lands inside one of their rects —
        // they sit above the station building and the cards' rects
        // intentionally extend outside the spawn cell, so a normal
        // cell-based check would never find them. Each card stores
        // its board-local rect when drawn (`_fromCardRect` /
        // `_toCardRect` on the anchor entity), and the scene routes
        // the resulting hit through `flashStationEndpoint(...)` so
        // the matching endpoint flashes + yoyos.
        if (this._cardAnchors) {
            for (const anchor of this._cardAnchors) {
                const fr = anchor._fromCardRect;
                if (fr && localX >= fr.x && localX <= fr.x + fr.w
                       && localY >= fr.y && localY <= fr.y + fr.h) {
                    return { kind: 'info-card', entity: anchor, which: 'from' };
                }
                const tr = anchor._toCardRect;
                if (tr && localX >= tr.x && localX <= tr.x + tr.w
                       && localY >= tr.y && localY <= tr.y + tr.h) {
                    return { kind: 'info-card', entity: anchor, which: 'to' };
                }
            }
        }

        // Switches carry TWO separate controls:
        //   • a SIGNAL LAMP that sits OUTSIDE the cell → toggles stop/go
        //   • the cell itself → cycles the routing
        // Because the lamp lives outside its own cell, a plain per-cell
        // lookup would miss it (and could even fall on a neighbour), so
        // we scan every switch's board-absolute signal rect FIRST. A hit
        // out here is always a stop/go toggle, never a routing cycle —
        // which is exactly why the two can no longer be mis-tapped.
        for (const sw of this._switchByCell.values()) {
            const sr = sw._signalRect;
            if (sr && localX >= sr.x && localX <= sr.x + sr.w
                   && localY >= sr.y && localY <= sr.y + sr.h) {
                return { kind: 'switch-signal', entity: sw };
            }
        }

        const c = Math.floor(localX / this.cellSize);
        const r = Math.floor(localY / this.cellSize);
        if (c < 0 || c >= this.cols || r < 0 || r >= this.rows) return null;
        // A tap anywhere inside a switch's own cell cycles its routing
        // (its signal head was already handled by the pass above).
        const sw = this._switchByCell.get(`${c},${r}`);
        if (sw) {
            return { kind: 'switch', entity: sw };
        }
        // Spawn / goal cells aren't toggleable but tapping them is the
        // user's "where does this train go?" gesture — surface them so
        // the scene can drive `flashTrainRoute(trainId)`.
        const spawn = this._spawnByCell.get(`${c},${r}`);
        if (spawn) return { kind: 'spawn', entity: spawn };
        const goal = this._goalByCell.get(`${c},${r}`);
        if (goal) return { kind: 'goal', entity: goal };
        return null;
    }

    /**
     * Flip a SWITCH's signal between red (stop) and green (go) and
     * redraw it. This is the new train-stop control — the player taps
     * the switch's signal head on the board (or its LÁMPA tile in the
     * HUD) to hold or release trains. Routing is a separate control
     * (`toggleSwitch`).
     */
    toggleSwitchSignal(switchEntity) {
        if (!switchEntity) return;
        switchEntity.signal = switchEntity.signal === 'red' ? 'green' : 'red';
        this._redrawEntity(switchEntity);
        // Re-arm the switch click-flash so the toggle reads as a
        // deliberate action even though the routing didn't change.
        const anim = this._switchAnim.get(switchEntity.id);
        if (anim) {
            anim.flashTime = 0.35;
            if (anim.flash && !anim.flash.destroyed) anim.flash.alpha = 0.85;
        }
        // A held / released train changes every reachability
        // prediction, so refresh the path overlay immediately.
        this._refreshPathOverlay();
    }

    /**
     * Randomize every SWITCH SIGNAL on the board to a roughly even mix
     * of red and green at game start. Runs every time the scene loads
     * the stage (incl. on restart) so the player gets fresh variety
     * instead of opening every game with every signal the same.
     */
    randomizeSwitchSignals() {
        for (const sw of this._switchByCell.values()) {
            sw.signal = randomSignalAspect();
            this._redrawEntity(sw);
        }
    }

    /**
     * Legacy no-ops kept so older call sites (and the rewind
     * snapshot path) resolve cleanly. Lamps are now car-only,
     * fully automatic barriers — the player can't toggle them and
     * they hold no red/green state of their own.
     */
    toggleLamp() { /* barriers are automatic now — no manual toggle */ }
    randomizeLamps() { /* barriers are automatic now — nothing to randomize */ }

    /**
     * Advance a switch to the next routing in its `_routes` list and
     * redraw the underlay + badge. Wraps around so the last state
     * cycles back to the first — a player can tap repeatedly to
     * audition every possible direction.
     *
     * Single-state switches (e.g. a degenerate 2-port "junction"
     * with only one valid routing) are no-ops: the click-flash
     * still fires for tactile feedback, but the route doesn't
     * change because there's nowhere else for it to go.
     */
    toggleSwitch(switchEntity) {
        const count = switchEntity._routes?.length || 0;
        if (count > 0) {
            const cur = TrainSimBoard._normalizeSwitchIndex(switchEntity.active, count);
            switchEntity.active = (cur + 1) % count;
        }
        // Re-render the badge / spokes / ghosts with the new active
        // colour, then arm the click-flash overlay so `tickSwitches`
        // fades it out over the next ~350 ms. The flash makes the
        // route swap unmistakable — without it the spokes just
        // change colour and a busy player might not register that
        // their tap did anything.
        this._redrawEntity(switchEntity);
        const anim = this._switchAnim.get(switchEntity.id);
        if (anim) {
            anim.flashTime = 0.35;
            if (anim.flash && !anim.flash.destroyed) anim.flash.alpha = 0.85;
        }
        // Predicted paths depend on the live switch state, so a toggle
        // makes every train's route re-route in real time — the faded
        // colour line shows the new direction immediately.
        this._refreshPathOverlay();

        // Audible feedback — randomly one of the two single-throw
        // samples so repeated flips don't sound mechanical. Skipped
        // in the editor preview (no gameplay audio there).
        if (!this._editorMode) this._playSwitchSound();
    }

    /**
     * Play a random track-switch flip sample. Lazily spins up the
     * SFX player on first use. Best-effort via SceneAudio, which
     * swallows any error (missing file / autoplay block) so a flip
     * never fails because of audio.
     */
    _playSwitchSound() {
        if (!this._sfx) this._sfx = new SceneAudio();
        const url = SWITCH_SFX_URLS[Math.floor(Math.random() * SWITCH_SFX_URLS.length)];
        this._sfx.playOneShot(url, 0.85);
    }

    /**
     * Determine which automatic barriers (sorompó) should be CLOSED
     * this frame: a barrier closes when a live train is on, or within
     * `BARRIER_CLOSE_RADIUS_CELLS` of, its crossing track cell. The
     * level-crossing bell rings while any barrier is closed.
     *
     * Delegates the proximity scan to the pure `computeBarrierClosures`
     * helper (TrainSimSignals.js) and reuses one persistent Set to
     * avoid per-frame allocation.
     *
     * @returns {Set<string>} barrier entity ids that should be closed
     */
    _computeBarrierClosures() {
        const closed = this._closedBarrierIds || (this._closedBarrierIds = new Set());
        if (this._editorMode) { closed.clear(); return closed; }
        return computeBarrierClosures(
            this._barrierByRoadCell ? this._barrierByRoadCell.values() : null,
            this._liveTrains,
            TRAINSIM_CONFIG.BARRIER_CLOSE_RADIUS_CELLS,
            closed,
        );
    }

    /**
     * Fire the distant train-horn one-shot when a train FIRST comes
     * within `TRAIN_HORN_RADIUS_CELLS` of any road cell (i.e. it's
     * approaching a level crossing). Edge-triggered per train: the horn
     * sounds once on entry and only re-arms after the train pulls back
     * past the radius + 1 cell, so a train idling near a road never
     * blasts it every frame. Skipped in the editor preview.
     */
    _updateTrainHorns() {
        if (this._editorMode) return;
        const trains = this._liveTrains;
        if (!trains || trains.length === 0) return;

        // Cache the parsed road-cell coordinates once (rebuilt lazily if
        // the grid was cleared). Cheap brute-force nearest-road scan —
        // boards are small and there are at most a handful of trains.
        if (!this._roadCellsCache) {
            this._roadCellsCache = [];
            if (this._roadTileGrid) {
                for (const key of this._roadTileGrid.keys()) {
                    const [c, r] = key.split(',').map(Number);
                    this._roadCellsCache.push({ c, r });
                }
            }
        }
        const roads = this._roadCellsCache;
        if (roads.length === 0) return;

        const radius = TRAINSIM_CONFIG.TRAIN_HORN_RADIUS_CELLS;
        for (const train of trains) {
            if (!train || !train._started || !train._alive || train._reachedGoal) {
                if (train) train._hornNearRoad = false;
                continue;
            }
            let nearest = Infinity;
            for (const cell of roads) {
                const d = Math.abs(train._col - cell.c) + Math.abs(train._row - cell.r);
                if (d < nearest) {
                    nearest = d;
                    if (nearest === 0) break;
                }
            }
            if (nearest <= radius) {
                if (!train._hornNearRoad) {
                    train._hornNearRoad = true;
                    if (!this._sfx) this._sfx = new SceneAudio();
                    this._sfx.playOneShot(DISTANT_TRAIN_HORN_SFX_URL, 0.7);
                }
            } else if (nearest > radius + 1) {
                // Hysteresis so a train hovering at the edge doesn't
                // re-trigger the horn frame after frame.
                train._hornNearRoad = false;
            }
        }
    }

    /**
     * Start or stop the continuously-looping level-crossing bell.
     * Idempotent — only acts on the on→off / off→on transition so the
     * looping element isn't restarted (or re-`play()`-ed) every frame.
     * Skipped entirely in the editor preview.
     *
     * @param {boolean} on
     */
    _setCrossingBell(on) {
        if (this._editorMode) return;
        if (!!on === !!this._crossingBellRinging) return;
        this._crossingBellRinging = !!on;
        if (!this._sfx) this._sfx = new SceneAudio();
        if (on) this._sfx.playLoop(CROSSING_BELL_SFX_URL, 0.55);
        else this._sfx.stopLoop();
    }

    /**
     * Hard-stop the looping level-crossing bell and clear every gate
     * pulse. The scene calls this whenever the sim isn't actively
     * rolling (paused, rewinding, game over, all trains arrived) —
     * those frames skip `tickLamps`, so without this an in-progress
     * bell (a train frozen at a red gate) would be stranded ringing.
     * Live play re-arms the bell on the next `tickLamps`.
     */
    silenceCrossingBell() {
        this._setCrossingBell(false);
        this._bellHold = 0;
        if (this._closedBarrierIds) this._closedBarrierIds.clear();
        // Drop every barrier-closed flag so cars don't stay stuck
        // waiting at a crossing while the sim is frozen, and hide the
        // alarm rings. Arms re-tween on the next live `tickLamps`.
        if (this._lampByCell) {
            for (const lamp of this._lampByCell.values()) lamp._barrierClosed = false;
        }
        if (this._lampAnim) {
            for (const anim of this._lampAnim.values()) {
                if (anim.ring && !anim.ring.destroyed) anim.ring.visible = false;
                anim.closeHold = 0;
            }
        }
    }

    /**
     * Snapshot every switch's `active` index and every lamp's `state`
     * into a plain object. Used by the scene's REWIND feature so a
     * later `restoreEntityStates(snap)` brings the player's switch
     * / lamp configuration back to exactly what it was N seconds ago.
     */
    snapshotEntityStates() {
        const switches = [];
        for (const [cellKey, sw] of this._switchByCell) {
            switches.push({ id: sw.id, cellKey, active: sw.active, signal: sw.signal });
        }
        const lamps = [];
        for (const [cellKey, lamp] of this._lampByCell) {
            lamps.push({ id: lamp.id, cellKey, state: lamp.state });
        }
        return { switches, lamps };
    }

    /**
     * Inverse of `snapshotEntityStates`. Walks the captured switch /
     * lamp arrays and writes their state back onto the live entities,
     * triggering a redraw on each so the visuals match. Path overlay
     * is refreshed once at the end so every train's predicted route
     * picks up the rewound switch config in a single pass.
     *
     * Missing entries (entities that have been deleted between
     * snapshot + restore — e.g. an undo in the editor) are silently
     * skipped: an obsolete snapshot should never break a live game.
     */
    restoreEntityStates(snap) {
        if (!snap) return;
        if (Array.isArray(snap.switches)) {
            for (const rec of snap.switches) {
                const sw = this._switchByCell.get(rec.cellKey);
                if (!sw) continue;
                sw.active = rec.active;
                if (rec.signal === 'red' || rec.signal === 'green') sw.signal = rec.signal;
                this._redrawEntity(sw);
            }
        }
        if (Array.isArray(snap.lamps)) {
            for (const rec of snap.lamps) {
                const lamp = this._lampByCell.get(rec.cellKey);
                if (!lamp) continue;
                lamp.state = rec.state;
                this._redrawEntity(lamp);
            }
        }
        this._refreshPathOverlay();
    }

    /**
     * Normalise a raw `entity.active` value (string 'A'/'B' from
     * legacy stages, missing/undefined, or out-of-range integer)
     * into a clean 0..count-1 integer. Centralised so the toggle,
     * routing + rendering agree on the
     * same canonical form.
     */
    static _normalizeSwitchIndex(active, count) {
        if (count <= 0) return 0;
        if (typeof active === 'string') {
            if (active === 'A') return 0;
            if (active === 'B') return Math.min(1, count - 1);
            const parsed = Number(active);
            if (Number.isFinite(parsed)) return ((Math.floor(parsed) % count) + count) % count;
            return 0;
        }
        if (typeof active === 'number' && Number.isFinite(active)) {
            return ((Math.floor(active) % count) + count) % count;
        }
        return 0;
    }

    /**
     * Per-frame switch animation step. Animates:
     *   - The pulsing halo ring around every switch (subtle sine
     *     pulse on scale + alpha so the player's eye gets drawn
     *     to interactable cells even on a crowded board).
     *   - The transient click-flash overlay that fades after each
     *     `toggleSwitch`, giving immediate visual feedback that
     *     the route just swapped.
     *
     * `delta` is in Pixi ticker units (1 ≈ 16.67 ms at 60 FPS),
     * matching what `tickCars` already uses. Cheap no-op when the
     * stage has no switches.
     */
    tickSwitches(delta) {
        if (!this._switchByCell || this._switchByCell.size === 0) return;
        // Advance the shared phase clock. We use a small per-switch
        // offset (based on cell coords) so haloes don't pulse in
        // lockstep — every switch breathes on its own beat, which
        // reads as "alive" rather than "machine-gun".
        const dtSec = (delta || 1) / 60;
        this._switchAnimTime = (this._switchAnimTime || 0) + dtSec;
        const t = this._switchAnimTime;
        for (const sw of this._switchByCell.values()) {
            const anim = this._switchAnim.get(sw.id);
            if (!anim) continue;
            const phase = (sw.c * 0.37 + sw.r * 0.53);
            const pulse = 0.5 + 0.5 * Math.sin(t * 2.4 + phase);
            if (anim.halo && !anim.halo.destroyed) {
                const scale = 1 + 0.18 * pulse;
                anim.halo.scale.set(scale, scale);
                anim.halo.alpha = 0.45 + 0.45 * pulse;
            }
            if (anim.tap && !anim.tap.destroyed) {
                anim.tap.alpha = 0.55 + 0.45 * pulse;
            }
            // ── Per-switch signal head: red aspect STROBES white-hot
            //   (~1.6 Hz) so a "stop" signal grabs the eye; green
            //   aspect glows with a soft steady breath.
            const sigGreen = sw.signal === 'green';
            if (anim.signalGlow && !anim.signalGlow.destroyed) {
                if (sigGreen) {
                    const breath = 0.5 + 0.5 * Math.sin(t * 1.8 + phase);
                    anim.signalGlow.alpha = 0.28 + 0.18 * breath;
                } else {
                    const blink = 0.5 + 0.5 * Math.sin(t * Math.PI * 2 * 1.6 + phase);
                    anim.signalGlow.alpha = 0.35 + 0.45 * blink;
                }
            }
            if (anim.signalCore && !anim.signalCore.destroyed) {
                if (sigGreen) {
                    anim.signalCore.alpha = 0;
                } else {
                    const blink = 0.5 + 0.5 * Math.sin(t * Math.PI * 2 * 1.6 + phase);
                    anim.signalCore.alpha = blink;
                }
            }
            if (anim.flashTime > 0) {
                anim.flashTime -= dtSec;
                if (anim.flash && !anim.flash.destroyed) {
                    // Quick ease-out: hot white at t=0.35, gone at t=0.
                    const k = Math.max(0, Math.min(1, anim.flashTime / 0.35));
                    anim.flash.alpha = 0.85 * k;
                    const fs = 1 + (1 - k) * 0.35;
                    anim.flash.scale.set(fs, fs);
                }
                if (anim.flashTime <= 0) {
                    anim.flashTime = 0;
                    if (anim.flash && !anim.flash.destroyed) {
                        anim.flash.alpha = 0;
                        anim.flash.scale.set(1, 1);
                    }
                }
            }
        }
    }

    /**
     * Drift every registered cloud sprite slowly rightward at its own
     * per-cloud pace. All clouds share the SAME direction (so the
     * sky reads as a coherent breeze rather than chaos) but each
     * one's speed is a deterministic spread of ~0.18..0.45 px/frame,
     * which gives the parallax cue the player asked for — small,
     * faster clouds gradually overtake the large slow drifters.
     *
     * When a cloud's left edge passes the right side of the board it
     * wraps to just outside the left edge so the sky never empties
     * mid-session. Runs at REAL time (no speed-multiplier) — the
     * weather should look identical at 1× and 4× tempo.
     *
     * Cheap no-op for stages without clouds and in editor mode.
     */
    tickClouds(delta) {
        if (!this._cloudAnims || this._cloudAnims.length === 0) return;
        const boardW = this.cols * this.cellSize;
        const step = (delta || 1);
        for (const c of this._cloudAnims) {
            const node = c.node;
            if (!node || node.destroyed) continue;
            node.x += c.speed * step;
            // Sprites are anchor-centred, so `node.x - w/2` is the
            // visible LEFT edge. Once that crosses the right edge of
            // the board the cloud is fully off-screen and we recycle
            // it just outside the left edge for a seamless loop.
            if (node.x - c.w / 2 > boardW) {
                node.x = -c.w / 2;
            }
        }
    }

    /**
     * Drive the wind-blown leaves emitter (continuous spawn + drift +
     * tumble + despawn). Cheap no-op when the player has the
     * `windLeavesEnabled` toggle off — `_leaves` is `null` and the
     * call short-circuits before doing any per-frame work.
     *
     * Runs at REAL time, not the speed-multiplier — the wind keeps a
     * consistent feel regardless of how fast the trains are running.
     */
    tickLeaves(delta) {
        if (!this._leaves) return;
        this._leaves.update(delta);
    }

    /**
     * Sway every registered tree / bush / flowerbed wrapper by a tiny
     * sine-driven angle. The wrapper pivots from the tile's bottom-
     * centre, so the result reads as the plant bending at its base —
     * what the eye expects from a real tree in a breeze.
     *
     * Per-tile amplitude / frequency / phase were assigned at paint
     * time (deterministic from cell coords) so nothing here has to
     * branch per-tile beyond the simple sin() call.
     *
     * Real-time tick (no speed-multiplier) — wind tempo shouldn't
     * change with the train's fast-forward setting. Cheap no-op when
     * the wind toggle is OFF (registry stays empty in that case).
     */
    tickWobble(delta) {
        if (!this._wobbleAnims || this._wobbleAnims.length === 0) return;
        const dtSec = (delta || 1) / 60;
        this._wobbleTime += dtSec;
        const t = this._wobbleTime;
        for (const a of this._wobbleAnims) {
            const c = a.container;
            if (!c || c.destroyed) continue;
            c.rotation = a.baseRot + a.amp * Math.sin(t * a.freqRad + a.phase);
        }
    }

    /**
     * Drive every AUTOMATIC level-crossing barrier (sorompó): drop the
     * arm when a train nears the crossing track cell, raise it after
     * the train has passed (plus a short linger so it doesn't chatter),
     * flash the post stop-light + alarm ring while it's down, and ring
     * the looping crossing bell whenever ANY barrier is closed.
     *
     * Barriers are car-only + fully automatic now — they never stop a
     * train and the player can't toggle them. The train-stop control
     * moved onto per-switch signals (see `tickSwitches`).
     *
     * Called by the scene's update loop on EVERY frame and runs at
     * REAL time, not the speed-multiplier, so the crossing reacts
     * instantly regardless of how fast trains are running.
     *
     * Cheap no-op when there are no barriers on the board.
     */
    tickLamps(delta) {
        const dtSec = (delta || 1) / 60;

        // Which barriers should be closed RIGHT NOW (train within
        // range of the crossing). Drive the looping bell off this with
        // a board-level linger so the audio doesn't stutter when a
        // train straddles the radius edge.
        const closures = this._computeBarrierClosures();
        if (closures.size > 0) {
            this._bellHold = TRAINSIM_CONFIG.BARRIER_LINGER_SEC;
        } else if (this._bellHold > 0) {
            this._bellHold = Math.max(0, this._bellHold - dtSec);
        }
        this._setCrossingBell(closures.size > 0 || this._bellHold > 0);

        // Sound the distant horn whenever a train first nears a road.
        this._updateTrainHorns();

        if (!this._lampAnim || this._lampAnim.size === 0) return;
        this._lampAnimTime += dtSec;
        const t = this._lampAnimTime;
        // Per-barrier arm tween speed: ~0.5 s for a full close→open.
        const armRate = (Math.PI * 0.42) / 0.5; // rad/sec
        for (const lamp of this._lampByCell.values()) {
            const anim = this._lampAnim.get(lamp.id);
            if (!anim) continue;

            // Per-barrier phase offset (so neighbouring crossings don't
            // flash in lockstep — busy boards stay "alive" instead of
            // looking like one synchronised strobe).
            const phaseOff = (lamp.c * 0.31 + lamp.r * 0.47);
            anim.phase = t + phaseOff;

            // Effective closed state = proximity NOW, held open for a
            // short linger after the train clears so the arm doesn't
            // bounce on a train hovering at the radius edge.
            const rawClosed = closures.has(lamp.id);
            if (rawClosed) {
                anim.closeHold = TRAINSIM_CONFIG.BARRIER_LINGER_SEC;
            } else if (anim.closeHold > 0) {
                anim.closeHold = Math.max(0, anim.closeHold - dtSec);
            }
            const closed = rawClosed || anim.closeHold > 0;
            lamp._barrierClosed = closed;

            // ── Alarm ring: a bright amber halo that throbs around the
            //   crossing while the barrier is DOWN (train passing).
            if (anim.ring && !anim.ring.destroyed) {
                if (closed) {
                    const p = 0.5 + 0.5 * Math.sin(anim.phase * Math.PI * 2 * 2.2); // ~2.2 Hz
                    anim.ring.visible = true;
                    anim.ring.alpha = 0.45 + 0.55 * p;
                    const s = 1.0 + 0.40 * p;
                    anim.ring.scale.set(s, s);
                } else if (anim.ring.visible) {
                    anim.ring.visible = false;
                }
            }

            // ── Barrier arm tween: closed → arm DOWN across the lane,
            //   open → arm raised. Closed/open rotations were baked at
            //   draw time based on the road axis so the arm always
            //   falls perpendicular to traffic.
            const closedRot = anim.closedRot ?? 0;
            const openRot   = anim.openRot   ?? (closedRot - Math.PI * 0.42);
            anim.armTargetRot = closed ? closedRot : openRot;
            if (anim.arm && !anim.arm.destroyed) {
                const diff = anim.armTargetRot - anim.armRot;
                if (Math.abs(diff) < 1e-4) {
                    anim.armRot = anim.armTargetRot;
                } else {
                    const step = Math.sign(diff) * Math.min(Math.abs(diff), armRate * dtSec);
                    anim.armRot += step;
                }
                anim.arm.rotation = anim.armRot;
            }

            // ── Stop-light on the barrier post: flashes hot red while
            //   the barrier is DOWN so drivers see the universal
            //   "STOP" signal at the crossing; fades out as the arm
            //   rises.
            if (anim.stopLight && !anim.stopLight.destroyed) {
                if (closed) {
                    const blink = 0.5 + 0.5 * Math.sin(anim.phase * Math.PI * 2 * 2.0); // ~2 Hz
                    anim.stopLight.alpha = 0.45 + 0.55 * blink;
                    const s = 1 + 0.10 * blink;
                    anim.stopLight.scale.set(s, s);
                } else {
                    // Fade out smoothly as the arm rises.
                    const range = Math.abs(closedRot - openRot) || 1;
                    const closedness = 1 - Math.min(1, Math.abs(anim.armRot - closedRot) / range);
                    anim.stopLight.alpha = Math.max(0, closedness * 0.6);
                }
            }
            anim.closed = closed;
        }
    }

    /**
     * Light up the FROM cell + the TO cell for one train so the
     * player can instantly see "this start belongs to that end".
     * Called by the scene's pointertap handler whenever the player
     * taps either endpoint. The pulse fades out automatically after
     * `durationSec` real seconds (default ≈ 2.5 s), and tapping a
     * different train cancels the previous highlight so only one
     * pair is ever pulsing.
     *
     * @param {string} trainId  e.g. 't1'
     * @param {number} [durationSec=2.5]
     */
    flashTrainRoute(trainId, durationSec = 2.5) {
        if (!this._routeHighlightLayer || !trainId) return;

        // Find the matching endpoints. Spawns + goals are small lists,
        // a linear scan is more than fast enough and avoids extra maps.
        const spawn = this.spawns.find((s) => (s.props?.trainId || 't1') === trainId);
        const goal = this.goals.find((g) => (g.props?.trainId || 't1') === trainId);
        if (!spawn && !goal) return;

        // Wipe any previous highlight FIRST — a new tap always wins.
        this._clearRouteHighlight();

        const cs = this.cellSize;
        const colorName = spawn?.props?.color
            || goal?.props?.color
            || TRAIN_ID_TO_COLOR_NAME[trainId]
            || 'red';
        const colorHex = COLOR_NAME_TO_HEX[colorName] || 0xffffff;

        const layer = new Container();
        layer.label = `route-flash:${trainId}`;
        layer.sortableChildren = false;

        // Helper: a fat ring + a thin inner ring + an "X-marks-the-spot"
        // crosshair. We rebuild this each tick (via Graphics) so we can
        // pulse + breathe its radius. For the initial draw we just stamp
        // it at scale 1 — `tickRouteHighlight` animates from there.
        const makeMarker = (label) => {
            const c = new Container();
            c.label = label;
            const ring = new Graphics();
            const inner = new Graphics();
            const cross = new Graphics();
            c.addChild(ring);
            c.addChild(inner);
            c.addChild(cross);
            return { container: c, ring, inner, cross };
        };

        const markers = [];
        if (spawn) {
            const m = makeMarker('spawn');
            m.container.x = spawn.c * cs + cs / 2;
            m.container.y = spawn.r * cs + cs / 2;
            layer.addChild(m.container);
            markers.push({ ...m, role: 'spawn' });
        }
        if (goal) {
            const m = makeMarker('goal');
            m.container.x = goal.c * cs + cs / 2;
            m.container.y = goal.r * cs + cs / 2;
            layer.addChild(m.container);
            markers.push({ ...m, role: 'goal' });
        }

        // A bright connector line drawn straight between the two
        // endpoints — NOT the routing path (that's what the existing
        // path overlay is for), just a "these two belong together"
        // visual leash. Only drawn if we have both ends.
        let leash = null;
        if (spawn && goal) {
            leash = new Graphics();
            leash.zIndex = -1; // sit behind the markers
            layer.addChild(leash);
        }

        this._routeHighlightLayer.addChild(layer);
        this._routeHighlight = {
            trainId,
            gfx: layer,
            markers,
            leash,
            spawnCell: spawn ? { c: spawn.c, r: spawn.r } : null,
            goalCell: goal ? { c: goal.c, r: goal.r } : null,
            colorHex,
            ttl: durationSec,
            totalDuration: durationSec,
            t: 0,
        };
        // Paint the very first frame so the user sees something even
        // before the next ticker tick fires.
        this._renderRouteHighlightFrame();
    }

    /** Internal: tear down the active route-highlight overlay. */
    _clearRouteHighlight() {
        if (!this._routeHighlight) return;
        const { gfx } = this._routeHighlight;
        if (gfx) {
            if (gfx.parent) gfx.parent.removeChild(gfx);
            gfx.destroy?.({ children: true });
        }
        this._routeHighlight = null;
    }

    /**
     * Per-frame update for the active route highlight: breathes the
     * rings + crosshair, fades alpha as TTL runs out, and tears the
     * overlay down once it expires. Cheap no-op when no highlight is
     * active.
     */
    tickRouteHighlight(delta) {
        const dtSec = (delta || 1) / 60;
        this._tickEndpointYoyos(dtSec);
        if (!this._routeHighlight) return;
        this._routeHighlight.t += dtSec;
        this._routeHighlight.ttl -= dtSec;
        if (this._routeHighlight.ttl <= 0) {
            this._clearRouteHighlight();
            return;
        }
        this._renderRouteHighlightFrame();
    }

    /**
     * Trigger the standard route-flash on a train AND a one-shot
     * "yoyo" scale-bounce on the specific endpoint the player tapped
     * (HONNAN badge → origin spawn; HOVA badge → destination goal).
     * Lets the player visually confirm "this card belongs to THAT
     * building over there" without having to scan the whole map.
     *
     * `which`: 'from' bounces the spawn, 'to' bounces the goal.
     * Safe to call with no matching entity — silently no-ops.
     */
    flashStationEndpoint(trainId, which, anchorEntity = null) {
        // Existing route flash paints rings on BOTH endpoints + a
        // connecting leash, so the eye gets the full from/to story.
        this.flashTrainRoute(trainId);

        // Press-pulse the actual HONNAN / HOVA badge the player
        // tapped (when we know which card hosted the tap), so the
        // feedback at the finger is instant even before the cell
        // bounce starts on the other side of the map.
        const minis = anchorEntity && this._cardMinisById?.get?.(anchorEntity.id);
        const mini = minis && (which === 'to' ? minis.to : minis.from);
        if (mini) this._pulseInfoMini(mini);

        const list = which === 'to' ? this.goals : this.spawns;
        const target = (list || []).find(
            (e) => (e?.props?.trainId || 't1') === trainId,
        );
        if (!target) return;

        // Yoyo the target itself first. For goals that's a small
        // pin; for spawns that's the whole station building.
        this._yoyoEntityGfx(target.id, 0.65);

        // When the destination cell happens to sit inside ANOTHER
        // train's spawn-building footprint (the dual-purpose
        // station pattern documented on `_drawGoalEntity`), the
        // visible artwork there is the OTHER spawn's sprite — the
        // goal entity is just a pin painted on top. Bouncing the
        // pin alone is easy to miss across a busy board, so we
        // also kick off a yoyo on whatever spawn building
        // currently covers the cell. Origin-side ('from') needs
        // no equivalent because the spawn's own gfx already
        // owns the building sprite.
        if (which === 'to') {
            for (const spawn of (this._spawnByTrainId?.values?.() || [])) {
                if (!spawn || spawn.id === target.id) continue;
                if (this._spawnFootprintContains?.(spawn, target.c, target.r)) {
                    this._yoyoEntityGfx(spawn.id, 0.65);
                    break;
                }
            }
        }
    }

    /**
     * Internal: queue a single one-shot yoyo bounce on the gfx of
     * the entity identified by `entityId`. Cancels any in-flight
     * yoyo on the same gfx first so rapid taps don't compound the
     * scale. No-op when the entity has no registered gfx (e.g.
     * destroyed mid-frame).
     */
    _yoyoEntityGfx(entityId, duration = 0.65, amplitude = 0.30) {
        const reg = this._entityRegistry?.get?.(entityId);
        const gfx = reg?.gfx;
        if (!gfx) return;
        for (let i = this._endpointYoyos.length - 1; i >= 0; i--) {
            if (this._endpointYoyos[i].gfx === gfx) {
                const old = this._endpointYoyos[i];
                if (old.gfx && !old.gfx.destroyed) {
                    old.gfx.scale.set(old.baseScale.x, old.baseScale.y);
                }
                this._endpointYoyos.splice(i, 1);
            }
        }
        this._endpointYoyos.push({
            gfx,
            t: 0,
            duration,
            amplitude,
            baseScale: { x: gfx.scale.x || 1, y: gfx.scale.y || 1 },
        });
    }

    /**
     * Advance every active endpoint yoyo by `dtSec` seconds. The
     * scale curve is a damped cosine — amplitude 0.30 at t=0 falling
     * to 0 at t=duration — so the cell pops twice (overshoot +
     * settle) and lands cleanly back on its starting scale. Pure
     * visual; never mutates the entity itself.
     */
    _tickEndpointYoyos(dtSec) {
        if (!this._endpointYoyos.length) return;
        for (let i = this._endpointYoyos.length - 1; i >= 0; i--) {
            const y = this._endpointYoyos[i];
            y.t += dtSec;
            const prog = Math.min(1, y.t / y.duration);
            if (!y.gfx || y.gfx.destroyed) {
                this._endpointYoyos.splice(i, 1);
                continue;
            }
            if (prog >= 1) {
                y.gfx.scale.set(y.baseScale.x, y.baseScale.y);
                this._endpointYoyos.splice(i, 1);
                continue;
            }
            const peak = (typeof y.amplitude === 'number') ? y.amplitude : 0.30;
            const amp = peak * Math.cos(prog * Math.PI * 3) * (1 - prog);
            y.gfx.scale.set(
                y.baseScale.x * (1 + amp),
                y.baseScale.y * (1 + amp),
            );
        }
    }

    /** Internal: redraw the highlight rings for the current `t`/`ttl`. */
    _renderRouteHighlightFrame() {
        const h = this._routeHighlight;
        if (!h) return;
        const cs = this.cellSize;
        // Pulse 0..1..0 at ~2 Hz, eased; provides the "breathing" feel.
        const pulse = 0.5 + 0.5 * Math.sin(h.t * 5.5);
        // Linear-ish fade in the last 0.6 s so the marker doesn't pop
        // out — full alpha for most of the highlight, then ease away.
        const remaining = Math.max(0, h.ttl);
        const fade = Math.min(1, remaining / 0.6);

        const colorHex = h.colorHex;
        const baseR = cs * 0.62;
        const ringR = baseR + 4 * pulse;
        const ringWidth = Math.max(4, cs * 0.18);

        for (const m of h.markers) {
            // Big outer ring — the "where" cue. Thick coloured stroke
            // with a dark halo for contrast on any background sprite.
            m.ring.clear();
            m.ring.circle(0, 0, ringR + 2);
            m.ring.stroke({ color: 0x000000, width: ringWidth + 4, alpha: 0.35 * fade });
            m.ring.circle(0, 0, ringR);
            m.ring.stroke({ color: colorHex, width: ringWidth, alpha: (0.85 + 0.1 * pulse) * fade });

            // Inner thin ring tightens the focal point.
            m.inner.clear();
            m.inner.circle(0, 0, baseR * 0.55);
            m.inner.stroke({ color: 0xffffff, width: Math.max(2, cs * 0.06), alpha: (0.55 + 0.35 * pulse) * fade });

            // Crosshair — short arms outside the inner ring that point
            // at the cell centre, so even at full zoom-out the player
            // can tell exactly which cell is the FROM / TO.
            m.cross.clear();
            const armOut = ringR + 6;
            const armIn = ringR - 2;
            const armW = Math.max(2, cs * 0.07);
            const drawArm = (x1, y1, x2, y2) => {
                m.cross.moveTo(x1, y1);
                m.cross.lineTo(x2, y2);
            };
            drawArm(0, -armOut, 0, -armIn);
            drawArm(0,  armIn, 0,  armOut);
            drawArm(-armOut, 0, -armIn, 0);
            drawArm( armIn,  0,  armOut, 0);
            m.cross.stroke({ color: 0xffffff, width: armW, alpha: 0.95 * fade, cap: 'round' });
        }

        // Connector "leash" between the two endpoints — a single
        // straight bright line under the markers. Dashed by alternating
        // moveTo/lineTo segments so it doesn't get confused with the
        // actual routing line from the path overlay.
        if (h.leash && h.spawnCell && h.goalCell) {
            const x1 = h.spawnCell.c * cs + cs / 2;
            const y1 = h.spawnCell.r * cs + cs / 2;
            const x2 = h.goalCell.c * cs + cs / 2;
            const y2 = h.goalCell.r * cs + cs / 2;
            h.leash.clear();
            const dx = x2 - x1;
            const dy = y2 - y1;
            const len = Math.hypot(dx, dy);
            if (len > 1) {
                const dashLen = Math.max(10, cs * 0.45);
                const gapLen = dashLen * 0.6;
                const step = dashLen + gapLen;
                // Animated offset so dashes appear to "march" from
                // spawn → goal, reinforcing the direction.
                const offset = (h.t * cs * 1.8) % step;
                const ux = dx / len;
                const uy = dy / len;
                for (let d = -offset; d < len; d += step) {
                    const ds = Math.max(0, d);
                    const de = Math.min(len, d + dashLen);
                    if (de <= ds) continue;
                    h.leash.moveTo(x1 + ux * ds, y1 + uy * ds);
                    h.leash.lineTo(x1 + ux * de, y1 + uy * de);
                }
                h.leash.stroke({
                    color: h.colorHex,
                    width: Math.max(3, cs * 0.12),
                    alpha: 0.75 * fade,
                    cap: 'round',
                });
            }
        }
    }

    /** Toggle the path-prediction overlay on/off (e.g. via a Help key). */
    setPathOverlayVisible(visible) {
        this._pathOverlayVisible = !!visible;
        if (this._pathOverlay) this._pathOverlay.visible = this._pathOverlayVisible;
        // Re-draw whenever we bring the overlay back so any switch
        // toggles that happened while it was hidden are reflected
        // immediately.
        if (this._pathOverlayVisible) this._refreshPathOverlay();
    }

    /** Manually refresh the predicted paths — useful when the caller
     *  (editor) mutates the stage in a way that doesn't go through
     *  `toggleSwitch` (e.g. changing a switch's `initial` prop). */
    refreshPathOverlay() { this._refreshPathOverlay(); }

    // ── Road traffic (cars) ────────────────────────────────────
    //
    // The car simulator runs entirely inside the board: the host scene
    // (gameplay or editor preview) just calls `tickCars(delta)` each
    // frame and the board takes care of spawning, despawning, collision
    // avoidance and rendering. `delta` is in Pixi ticker units (1.0 ≈
    // 16.67 ms at 60 FPS), matching what `update()` already receives.

    /**
     * Enable / disable the car simulation. While disabled, no cars
     * spawn and existing cars freeze in place (instant resume on
     * re-enable). Useful for editor "stop preview" or game pause.
     */
    setCarsEnabled(enabled) {
        this._carsEnabled = !!enabled;
        if (!this._carsEnabled) {
            for (const car of this._cars) car.destroy();
            this._cars = [];
        }
    }

    /**
     * Per-frame car simulation step.
     * - Drops any cars whose previous update marked them dead.
     * - Advances each live car (driving / braking / cell transitions).
     * - Periodically attempts to spawn a new car from a network-edge
     *   port until we hit `_carMaxCount`.
     *
     * Safe to call even when there are no road tiles — it returns
     * immediately.
     */
    tickCars(delta) {
        if (!this._carsEnabled) return;
        if (!this._carContainer || this._carContainer.destroyed) return;
        if (this._roadTileGrid.size === 0) {
            // Stage has no roads (or roads were removed) — flush any
            // leftover cars so they don't dangle on the next stage.
            if (this._cars.length > 0) {
                for (const car of this._cars) car.destroy();
                this._cars = [];
            }
            return;
        }

        // Drive every car. Iterate backwards so a despawn during the
        // step (off-network exit, network mismatch, etc.) splices out
        // cleanly without skipping the next car.
        for (let i = this._cars.length - 1; i >= 0; i--) {
            const car = this._cars[i];
            car.update(delta);
            if (!car._alive) this._cars.splice(i, 1);
        }

        // Spawn pacing — `delta` is in 60ths of a second, so we convert
        // back to ms for a frame-rate-independent interval.
        this._carSpawnTimer += delta * (1000 / 60);
        if (this._carSpawnTimer >= this._carSpawnInterval && this._cars.length < this._carMaxCount) {
            this._carSpawnTimer = 0;
            this._spawnRandomCar();
        }
    }

    /**
     * Try to spawn one car at a randomly chosen network-edge port. A
     * "network-edge port" is any open port on a road tile whose neighbour
     * in that direction is either off-grid or non-road — i.e. a place
     * where a car could naturally enter from "outside the world". If no
     * edge ports exist (the road network is a fully closed loop) we
     * fall back to a random open port on a random tile.
     *
     * The spawn is rejected (silently — we'll retry next interval) if
     * the target cell+lane is already occupied so two cars can never
     * appear stacked on top of each other.
     */
    _spawnRandomCar() {
        const edgePorts = [];
        for (const tile of this._roadTileGrid.values()) {
            const conns = tile._connections;
            if (!conns) continue;
            for (const d of [0, 2, 4, 6]) {
                if (!conns[d]) continue;
                const nc = tile.c + DIR_DELTA[d].dc;
                const nr = tile.r + DIR_DELTA[d].dr;
                const onGrid = nc >= 0 && nc < this.cols && nr >= 0 && nr < this.rows;
                if (!onGrid || !this._roadTileGrid.has(`${nc},${nr}`)) {
                    edgePorts.push({ c: tile.c, r: tile.r, entryDir: d });
                }
            }
        }

        let pick;
        if (edgePorts.length > 0) {
            pick = edgePorts[Math.floor(Math.random() * edgePorts.length)];
        } else {
            // Fully closed network — spawn on any tile, any open port.
            const tiles = [...this._roadTileGrid.values()];
            const tile = tiles[Math.floor(Math.random() * tiles.length)];
            if (!tile || !tile._connections) return;
            const openPorts = [0, 2, 4, 6].filter((d) => tile._connections[d]);
            if (openPorts.length === 0) return;
            pick = { c: tile.c, r: tile.r, entryDir: openPorts[Math.floor(Math.random() * openPorts.length)] };
        }

        // Direction of travel is opposite the entry port (entering from
        // the north edge means heading south).
        const travelDir = OPPOSITE[pick.entryDir];
        // A spawning car defaults to lane=+1 (right of travel), so check
        // that physical lane for occupancy before instantiating.
        if (this._isCellLaneOccupiedRaw(pick.c, pick.r, travelDir, +1, null)) return;

        const api = this._getCarBoardApi();
        const car = new TrainSimCar({
            col: pick.c,
            row: pick.r,
            dir: travelDir,
            cs: this.cellSize,
            // Random base speed (cells/sec). Trains now run at 1.20
            // cells/sec by default, so cars are deliberately tuned to
            // be SLOWER — town traffic that the express train zips
            // past. The big spread (slow ~0.47, fast ~1.07) is what
            // makes overtaking interesting: faster drivers actually
            // catch up to slower ones and have a reason to pull out.
            speed: 0.47 + Math.random() * 0.60,
            container: this._carContainer,
            board: api,
            // Passed through so the car can pick a random sprite
            // (vehicle-car-red/blue/green/purple/truck/bus) from
            // the SkinResolver instead of drawing the old procedural
            // pixel-art body. Falls back to Graphics if the theme
            // doesn't have the slot loaded (e.g. asset 404).
            themeManager: this._themeManager,
        });
        if (car._alive) this._cars.push(car);
    }

    /**
     * Two cars occupy the same physical lane when their lane-offset
     * vectors point in the same direction off the centreline. Because
     * each direction's "right" is a different side of the road, this
     * means a northbound car in lane +1 (east) shares its physical lane
     * with a southbound car in lane −1 (also east — that southbound car
     * is overtaking into oncoming traffic). Used by every collision and
     * overtake-safety check below.
     *
     * Returns +1 or −1 — the sign of the perpendicular offset relative
     * to the road's natural axis (+1 = east on NS roads / south on EW
     * roads; −1 = the other side).
     */
    _physicalLaneSign(dir, lane) {
        // N (0) and E (2): "right" already points in the +axis direction.
        // S (4) and W (6): "right" points in the −axis direction, so we
        // flip the lane sign to recover the physical side.
        return (dir === 0 || dir === 2) ? lane : -lane;
    }

    /** Cardinal axis the car is moving along — 'NS' or 'EW'. */
    _axisOf(dir) {
        return (dir === 0 || dir === 4) ? 'NS' : 'EW';
    }

    /**
     * True when `car` is currently claiming the physical lane `physSign`.
     * A car always claims its TARGET lane (the committed decision). While
     * mid-transition (its visual `_lane` is still sliding toward
     * `_targetLane`) it ALSO claims the lane it's leaving — otherwise
     * cars behind would see the source lane as instantly free and drive
     * through the overtaking car's visual position.
     */
    _carClaimsLane(car, physSign) {
        const targetSign = this._physicalLaneSign(car._dir, car._targetLane);
        if (targetSign === physSign) return true;
        if (Math.abs(car._lane - car._targetLane) >= 0.05) {
            const sourceSign = this._physicalLaneSign(car._dir, -car._targetLane);
            if (sourceSign === physSign) return true;
        }
        return false;
    }

    /**
     * Lane occupancy check used by spawn + the car's last-mile guard.
     * The lane semantics now match real physical lanes so an overtaking
     * car (going N in lane −1) is correctly seen as blocking an oncoming
     * car (going S in lane +1) — they're in the same physical lane.
     *
     * Junction cells keep the hard "one car at a time" rule so
     * cross-traffic can never collide regardless of direction.
     */
    _isCellLaneOccupiedRaw(c, r, dir, lane, excludeCar) {
        const conns = this._roadTileGrid.get(`${c},${r}`)?._connections;
        if (!conns) return false;
        const cardCount = (conns[0] ? 1 : 0) + (conns[2] ? 1 : 0) + (conns[4] ? 1 : 0) + (conns[6] ? 1 : 0);
        const isJunction = cardCount >= 3;
        const myAxis = this._axisOf(dir);
        const mySign = this._physicalLaneSign(dir, lane);
        for (const other of this._cars) {
            if (other === excludeCar || !other._alive) continue;
            if (other._col !== c || other._row !== r) continue;
            if (isJunction) return true;
            if (this._axisOf(other._dir) !== myAxis) continue;
            if (this._carClaimsLane(other, mySign)) return true;
        }
        return false;
    }

    /**
     * Look ahead along the car's planned path and report whether the
     * OVERTAKE lane (left of the car's current target lane) is clear
     * across `cellsAhead` straight tiles. Returns false the moment any
     * blocker is found OR the road turns / junctions before we've
     * checked enough cells — overtaking is only safe on long straight
     * stretches.
     */
    _isOvertakeLaneClear(car, cellsAhead) {
        const myAxis = this._axisOf(car._dir);
        // The lane the car would move INTO is the OPPOSITE of its
        // current physical lane sign. Any car claiming that physical
        // lane along the lookahead path blocks the overtake.
        const overtakeSign = -this._physicalLaneSign(car._dir, car._targetLane);

        let c = car._col, r = car._row;
        let dir = car._dir;
        for (let step = 0; step <= cellsAhead; step++) {
            const conns = this._roadTileGrid.get(`${c},${r}`)?._connections;
            if (!conns) return false;
            const cardCount = (conns[0] ? 1 : 0) + (conns[2] ? 1 : 0) + (conns[4] ? 1 : 0) + (conns[6] ? 1 : 0);
            if (step > 0) {
                if (cardCount !== 2 || !conns[dir] || !conns[OPPOSITE[dir]]) return false;
            }
            for (const other of this._cars) {
                if (other === car || !other._alive) continue;
                if (other._col !== c || other._row !== r) continue;
                if (this._axisOf(other._dir) !== myAxis) return false;
                if (this._carClaimsLane(other, overtakeSign)) return false;
            }
            if (step >= cellsAhead) break;
            const nc = c + DIR_DELTA[dir].dc;
            const nr = r + DIR_DELTA[dir].dr;
            if (!this._roadTileGrid.has(`${nc},${nr}`)) return false;
            c = nc;
            r = nr;
        }
        return true;
    }

    /**
     * True if the overtaking car can safely tuck back into its RIGHT
     * (natural) lane right now. Two checks, both required:
     *
     *   1. CURRENT cell — no oncoming car claims the right lane, and
     *      no SAME-direction car in the right lane is still AHEAD of
     *      us (we're not done passing them yet).
     *   2. NEXT cell — no same-direction car is sitting in the right
     *      lane there either. This is the bit that kills the zig-zag:
     *      if the slow car we set out to overtake is still one cell
     *      ahead in the right lane, returning now would put us right
     *      back behind it and trigger another overtake → another
     *      return → another overtake forever.
     */
    _isReturnLaneClear(car) {
        const rightSign = this._physicalLaneSign(car._dir, +1);
        const myAxis = this._axisOf(car._dir);

        // Current cell — block only on real conflicts.
        for (const other of this._cars) {
            if (other === car || !other._alive) continue;
            if (other._col !== car._col || other._row !== car._row) continue;
            if (this._axisOf(other._dir) !== myAxis) return false;
            if (!this._carClaimsLane(other, rightSign)) continue;
            if (other._dir === car._dir) {
                // Same direction: only block if they're at or ahead of
                // our progress. If they're behind us in the cell we've
                // already passed them, the return is safe.
                if (other._progress >= car._progress - 0.01) return false;
            } else {
                // Oncoming car claiming our right lane (head-on threat).
                return false;
            }
        }

        // Next cell — same-direction car in right lane = blocker
        // because returning would force an immediate re-overtake.
        if (car._exitDir >= 0) {
            const nc = car._col + DIR_DELTA[car._exitDir].dc;
            const nr = car._row + DIR_DELTA[car._exitDir].dr;
            for (const other of this._cars) {
                if (other === car || !other._alive) continue;
                if (other._col !== nc || other._row !== nr) continue;
                if (this._axisOf(other._dir) !== myAxis) return false;
                if (other._dir !== car._dir) continue;
                if (this._carClaimsLane(other, rightSign)) return false;
            }
        }

        return true;
    }

    /**
     * Build (once) the tiny board adapter each car uses to query its
     * environment. Lazily constructed because `_paintLayer` runs
     * before any car is spawned — there's no benefit to building this
     * on every spawn but we also can't build it in `init()` if `_cars`
     * isn't initialised yet.
     */
    _getCarBoardApi() {
        if (this._carBoardApi) return this._carBoardApi;
        const board = this;
        this._carBoardApi = {
            getRoadConnectionsAt(c, r) {
                const tile = board._roadTileGrid.get(`${c},${r}`);
                return tile ? tile._connections : null;
            },
            hasRoadTile(c, r) {
                return board._roadTileGrid.has(`${c},${r}`);
            },
            isLampRed(c, r) {
                const lamp = board._lampByCell.get(`${c},${r}`);
                return !!(lamp && lamp.state === 'red');
            },
            /**
             * Returns true when the automatic level-crossing barrier
             * covering road cell (c, r) is CLOSED — i.e. a barrier is
             * on this cell AND a train is currently within range of
             * the crossing (`lamp._barrierClosed`, driven each frame
             * by `tickLamps`). Cars wait while the arm is down and
             * roll once the train has passed and the arm raises.
             */
            isBarrierClosedForCars(c, r) {
                const lamp = board._barrierByRoadCell.get(`${c},${r}`);
                return !!(lamp && lamp._barrierClosed);
            },
            /** Physical-lane occupancy — see `_isCellLaneOccupiedRaw`. */
            isCellLaneOccupied(c, r, dir, lane, excludeCar) {
                return board._isCellLaneOccupiedRaw(c, r, dir, lane, excludeCar);
            },
            /**
             * Nearest car ahead in the SAME physical lane (same axis,
             * same lane-offset side). This now catches a head-on threat
             * too: if I'm overtaking and an oncoming car is in the lane
             * I just pulled into, they share a physical lane and I'll
             * brake for them.
             */
            getCarAhead(car) {
                let best = null;
                let bestDist = Infinity;
                const myAxis    = board._axisOf(car._dir);
                const mySign    = board._physicalLaneSign(car._dir, car._targetLane);
                // Use the dual-claim helper so a car mid-overtake is
                // still visible as "in my lane" from both sides during
                // the slide — prevents the follower from rear-ending an
                // overtaker that just committed.
                const sameLane  = (other) =>
                    board._axisOf(other._dir) === myAxis &&
                    board._carClaimsLane(other, mySign);
                // Same cell, same physical lane, further along
                for (const other of board._cars) {
                    if (other === car || !other._alive) continue;
                    if (other._col !== car._col || other._row !== car._row) continue;
                    if (!sameLane(other)) continue;
                    // For cars heading our way, "ahead" = greater progress.
                    // For oncoming cars in our overtake lane, ANY position
                    // ahead of where we're heading is a hazard — treat
                    // them as immediate blockers.
                    if (other._dir === car._dir) {
                        if (other._progress <= car._progress) continue;
                        const d = other._progress - car._progress;
                        if (d < bestDist) { best = other; bestDist = d; }
                    } else {
                        const d = Math.max(0.01, 1 - car._progress - other._progress);
                        if (d < bestDist) { best = other; bestDist = d; }
                    }
                }
                // Next cell along our planned exit, same physical lane
                if (car._exitDir >= 0) {
                    const nc = car._col + DIR_DELTA[car._exitDir].dc;
                    const nr = car._row + DIR_DELTA[car._exitDir].dr;
                    for (const other of board._cars) {
                        if (other === car || !other._alive) continue;
                        if (other._col !== nc || other._row !== nr) continue;
                        if (!sameLane(other)) continue;
                        const d = (other._dir === car._exitDir)
                            ? (1 - car._progress) + other._progress
                            : (1 - car._progress) + (1 - other._progress);
                        if (d < bestDist) { best = other; bestDist = d; }
                    }
                }
                return best ? { car: best, dist: bestDist } : null;
            },
            /** True if the LEFT (overtake) lane is clear over `cellsAhead` straight cells. */
            isOvertakeSafe(car, cellsAhead) {
                return board._isOvertakeLaneClear(car, cellsAhead);
            },
            /** True if the RIGHT (natural) lane is clear in the car's current cell. */
            isReturnSafe(car) {
                return board._isReturnLaneClear(car);
            },
        };
        return this._carBoardApi;
    }

    /**
     * Hide one train's predicted path (or restore it). The runtime
     * hides a path the moment the train reaches its goal so completed
     * routes don't keep cluttering the board.
     */
    setTrainPathVisible(trainId, visible) {
        if (visible === false) this._hiddenPathTrains.add(trainId);
        else this._hiddenPathTrains.delete(trainId);
        this._refreshPathOverlay();
    }

    _hiddenPathTrains = new Set();

    destroy() {
        // Cars first — they hold Graphics that need explicit cleanup
        // before the parent container is torn down.
        for (const car of this._cars) car.destroy();
        this._cars = [];
        if (this._carContainer && !this._carContainer.destroyed) {
            this._carContainer.destroy();
        }
        this._carContainer = null;
        this._carBoardApi = null;

        // Release the switch-flip / crossing-bell SFX player (if one
        // was created). `destroy()` also stops the looping bell.
        if (this._sfx) { this._sfx.destroy(); this._sfx = null; }
        this._crossingBellRinging = false;
        this._bellHold = 0;
        if (this._closedBarrierIds) this._closedBarrierIds.clear();

        this.container.removeChildren();
        this._layerContainers.clear();
        this._trackTileGrid.clear();
        this._roadTileGrid.clear();
        this._roadCellsCache = null;
        this._switchByCell.clear();
        this._switchAnim.clear();
        if (this._lampAnim) this._lampAnim.clear();
        this._lampAnimTime = 0;
        if (this._cloudAnims) this._cloudAnims.length = 0;
        if (this._wobbleAnims) this._wobbleAnims.length = 0;
        this._wobbleTime = 0;
        this._windWobbleEnabled = false;
        if (this._leaves) { this._leaves.destroy(); this._leaves = null; }
        if (this._barrierByRoadCell) this._barrierByRoadCell.clear();
        this._lampByCell.clear();
        this._goalByCell.clear();
        this._spawnByCell.clear();
        this._spawnByTrainId.clear();
        this._goalByTrainId.clear();
        this._entityRegistry.clear();
        if (this._cardAnchors) this._cardAnchors.clear();
        if (this._cardMinisById) this._cardMinisById.clear();
        this._endpointYoyos = [];
        this.spawns = [];
        this.goals = [];
        this.stations = [];
        this._pathOverlay = null;
        this._pathOverlayUnder = null;
        this._aimLineLayer = null;
        this._routeHighlightLayer = null;
        this._routeHighlight = null;
        this._confettiLayer = null;
        this._confettiParticles = [];
        this._arrivalBanners = [];
        this._arrivedCrowdLayer = null;
        this._arrivedCrowdSprites = [];
        this._boardingFxLayer = null;
        this._signalOverlayLayer = null;
        if (this._boardingFxByTrainId) this._boardingFxByTrainId.clear();
        this._boardingFxByTrainId = null;
        this._hasWrongGoal = false;
        this._wrongGoalPulse = 0;
        this._hiddenPathTrains.clear();
        this._liveTrains = null;
    }

    // ── private ────────────────────────────────────────

    _getConnectionsAt(c, r) {
        const key = `${c},${r}`;

        // Switches no longer close any port — they route. Returning
        // the underlying junction's FULL connection mask means a
        // train can enter the switch from any open direction; the
        // exit is picked separately via `_getSwitchExit`.
        const sw = this._switchByCell.get(key);
        if (sw) {
            if (Array.isArray(sw._underlyingConn)) return sw._underlyingConn;
            // Legacy fallback: pre-routing stages may only have
            // `_stateA` / `_stateB`. OR them so the cell stays
            // traversable from every port the junction ever opens.
            const a = sw._stateA || [];
            const b = sw._stateB || [];
            const out = [];
            for (let i = 0; i < 8; i++) out.push(!!a[i] || !!b[i]);
            return out;
        }

        const tile = this._trackTileGrid.get(key);
        if (tile) return tile._connections;

        return null;
    }

    /**
     * If a switch lives at (c, r), return its preferred exit direction
     * for a train arriving via `entryDir`, based on the switch's live
     * `active` state. Returns -1 if no switch is here, or the entry
     * direction has no defined exit (e.g. the train entered through a
     * port the underlying junction doesn't actually open — geometry
     * error in the stage).
     */
    _getSwitchExit(c, r, entryDir) {
        const sw = this._switchByCell.get(`${c},${r}`);
        if (!sw) return -1;
        const route = TrainSimBoard._switchActiveRoute(sw);
        if (!route) return -1;
        const exit = route.get(entryDir);
        return (exit == null) ? -1 : exit;
    }

    /**
     * Resolve the currently-active route Map for a switch. Defensive
     * wrapper used by every code path that reads switch routing —
     * normalises the live `active` value (handles legacy string
     * states and out-of-range integers gracefully) and falls back to
     * the legacy `_routeA` if the route array is missing.
     */
    static _switchActiveRoute(sw) {
        if (!sw) return null;
        const routes = sw._routes;
        if (Array.isArray(routes) && routes.length > 0) {
            const idx = TrainSimBoard._normalizeSwitchIndex(sw.active, routes.length);
            return routes[idx] || null;
        }
        return sw._routeA || null;
    }

    /**
     * Per-train switch routing — what exit WILL this specific train
     * take when it reaches (c, r) from `entryDir`?
     *
     * Now that the autopilot is gone, "per-train" is essentially
     * the same as "live" — every train sees whatever state the
     * player most recently clicked the switch into. The signature
     * kept the `forTrainId` parameter so existing call sites
     * (path prediction, train update) don't have to change.
     */
    // eslint-disable-next-line no-unused-vars
    _getSwitchExitFor(c, r, entryDir, forTrainId) {
        const sw = this._switchByCell.get(`${c},${r}`);
        if (!sw) return -1;
        const route = TrainSimBoard._switchActiveRoute(sw);
        if (!route) return -1;
        const exit = route.get(entryDir);
        return (exit == null) ? -1 : exit;
    }

    _drawGridBackground() {
        const cs = this.cellSize;
        const w = this.cols * cs;
        const h = this.rows * cs;

        // If the `trainsim.background` slot has a PNG configured (the
        // default registered in `data/trainsim.config.js`), use it as
        // the underlay instead of a flat colour. Stretched to cover
        // the full grid so what you see in the editor matches what
        // the game renders. Grid lines stay on top for cell clarity.
        const bgSprite = this._themeManager?.createSprite?.('trainsim.background', w, h, { fitMode: 'cover' });
        if (bgSprite) {
            bgSprite.x = w / 2;
            bgSprite.y = h / 2;
            bgSprite.zIndex = -2;
            this.container.addChildAt(bgSprite, 0);
        }

        // Solid fallback fill (only when no sprite — visible until the
        // PNG loads, then hidden underneath the sprite).
        const bg = new Graphics();
        if (!bgSprite) {
            bg.rect(0, 0, w, h);
            bg.fill(TRAINSIM_CONFIG.COLOR_GRID_BG);
        }

        // Grid lines — drawn at a low alpha when a background sprite
        // is present so they don't visually fight the artwork. Only
        // rendered in EDITOR mode: in actual gameplay the grid is
        // pure visual noise (the player doesn't author tiles, just
        // taps signals/switches), and hiding it lets the background
        // art + decorations read as a continuous landscape instead
        // of a graph-paper diagram.
        if (this._editorMode) {
            const lineAlpha = bgSprite ? 0.25 : 1;
            for (let c = 1; c < this.cols; c++) {
                bg.rect(c * cs, 0, 1, h);
                bg.fill({ color: TRAINSIM_CONFIG.COLOR_GRID_LINE, alpha: lineAlpha });
            }
            for (let r = 1; r < this.rows; r++) {
                bg.rect(0, r * cs, w, 1);
                bg.fill({ color: TRAINSIM_CONFIG.COLOR_GRID_LINE, alpha: lineAlpha });
            }
        }

        bg.zIndex = -1;
        this.container.addChildAt(bg, bgSprite ? 1 : 0);
    }

    /**
     * Combined paint (kept for callers that don't care about the
     * two-pass split). Internally just delegates to the tiles+entities
     * helpers in sequence.
     */
    _paintLayer(layer) {
        this._paintLayerTiles(layer);
        this._paintLayerEntities(layer);
    }

    _paintLayerTiles(layer) {
        const layerC = this._layerContainers.get(layer.id);
        if (!layerC) return;
        if (Array.isArray(layer.tiles)) {
            for (const tile of layer.tiles) {
                this._paintTile(layer, tile, layerC);
            }
        }
    }

    _paintLayerEntities(layer) {
        const layerC = this._layerContainers.get(layer.id);
        if (!layerC) return;
        if (Array.isArray(layer.entities)) {
            for (const ent of layer.entities) {
                this._paintEntity(layer, ent, layerC);
            }
        }
    }

    _paintTile(layer, tile, parent) {
        // The road layer paints asphalt + dashed centreline via the
        // road drawer. Road tile types deliberately overlap with track
        // type strings ('straight', 'curve', 't', 'cross', 'deadend')
        // because the 8-port routing geometry is identical; the layer
        // id is what disambiguates road tiles from track tiles. A road
        // tile lives in `_roadTileGrid` so it never accidentally feeds
        // train routing.
        if (layer.id === 'road') {
            if (!ROAD_TILE_DEFS[tile.type]) {
                this._paintDecorationTile(tile, parent);
                return;
            }
            const conns = rotateConnections(ROAD_TILE_DEFS[tile.type], tile.rot || 0);
            const gfx = new Graphics();
            gfx.x = tile.c * this.cellSize;
            gfx.y = tile.r * this.cellSize;
            // Pass the tile's world position so the dashed centreline
            // is phase-aligned across cell boundaries — without this
            // each tile restarts its dash pattern at local x=0/y=0
            // and the road looks visibly seamed every `cellSize`px.
            drawTrainsimRoad(gfx, this.cellSize, conns, {
                worldX: tile.c * this.cellSize,
                worldY: tile.r * this.cellSize,
            });
            parent.addChild(gfx);
            const stored = { ...tile, _connections: conns, _gfx: gfx };
            this._roadTileGrid.set(`${tile.c},${tile.r}`, stored);
            return;
        }

        // Tile types that aren't in TILE_DEFS are decoration sprites
        // (buildings, trees, clouds, …). They're rendered via the
        // SkinResolver using slot key `trainsim.<tile.type>` and never
        // participate in train routing — even on the gameplay layer
        // they're cosmetic.
        if (!TILE_DEFS[tile.type]) {
            this._paintDecorationTile(tile, parent);
            return;
        }

        const conns = rotateConnections(TILE_DEFS[tile.type], tile.rot || 0);
        const gfx = new Graphics();
        gfx.x = tile.c * this.cellSize;
        gfx.y = tile.r * this.cellSize;
        this._drawTileGraphics(gfx, tile.type, conns);
        parent.addChild(gfx);

        // Only gameplay layer track tiles participate in routing.
        if (layer.kind === 'gameplay') {
            const stored = { ...tile, _connections: conns, _gfx: gfx };
            this._trackTileGrid.set(`${tile.c},${tile.r}`, stored);
        }
    }

    /**
     * Paint a decoration tile (a SkinResolver-backed PNG sprite).
     *
     * The sprite is sized to the slot's `footprint` (declared in
     * `data/trainsim.config.js`), so a 4×2 cloud occupies 4 × cellSize
     * across and 2 × cellSize tall, anchored at the tile's top-left
     * cell. `fitMode: 'contain'` preserves the source aspect ratio —
     * mismatch with the footprint just shows extra empty padding around
     * the art.
     *
     * Footprint defaults to 1×1 when the slot has no element entry
     * (graceful fallback for stages authored ahead of the config).
     */
    _paintDecorationTile(tile, parent) {
        const cs = this.cellSize;
        const slotKey = `trainsim.${tile.type}`;
        const element = this._themeManager?.getElement?.(slotKey) || null;
        const fw = Math.max(1, element?.footprint?.w || 1);
        const fh = Math.max(1, element?.footprint?.h || 1);
        const w = fw * cs;
        const h = fh * cs;

        const sprite = this._themeManager?.createSprite?.(slotKey, w, h, { fitMode: 'contain' });
        const node = sprite || this._buildDecorationFallback(element, w, h);
        if (!node) return;

        const baseRot = tile.rot ? (tile.rot * Math.PI) / 2 : 0;
        const isWobble = this._windWobbleEnabled
            && typeof tile.type === 'string'
            && TrainSimBoard._isWindWobbleType(tile.type);

        if (isWobble) {
            // Wrap the sprite in a Container PIVOTED AT ITS BOTTOM-CENTRE
            // so the wobble looks like the plant flexing at its base —
            // a centre-anchor rotation would tilt the whole sprite
            // through its middle and read as "tree falling over". The
            // wrapper sits at the footprint's bottom-centre in world
            // coords; the node lives at (0, -h/2) inside, so its
            // centre stays exactly where a non-wobbling tile's would.
            const wrapper = new Container();
            wrapper.x = tile.c * cs + w / 2;
            wrapper.y = tile.r * cs + h;
            // tile.rot stays on the inner node, NOT the wrapper —
            // that way the sway always pivots from the visual base
            // even when the author rotated the tile 90°.
            node.x = 0;
            node.y = -h / 2;
            if (baseRot) node.rotation = baseRot;
            wrapper.addChild(node);
            parent.addChild(wrapper);
            this._registerWobbleAnim(wrapper, tile, baseRot);
        } else {
            // Center the sprite inside its footprint rect, anchored to
            // the tile cell's top-left. (createSprite returns an
            // anchor-0.5 Sprite; placeholder Graphics is also
            // center-anchored.)
            node.x = tile.c * cs + w / 2;
            node.y = tile.r * cs + h / 2;
            if (baseRot) node.rotation = baseRot;
            parent.addChild(node);
        }

        // Clouds drift slowly across the sky. Skip in editor mode
        // so the author can place them precisely without the art
        // sliding out from under the cursor.
        if (!this._editorMode && typeof tile.type === 'string'
            && tile.type.startsWith('sky.cloud')) {
            this._registerCloudAnim(node, w);
        }
    }

    /**
     * Decoration tile types that should sway in the wind. Trees, bushes
     * and flowerbeds — anything organic with visible foliage — qualifies.
     * Buildings, lamps, signs, vehicles and rocks stay rock-still
     * because moving stone or street furniture would look broken.
     */
    static _isWindWobbleType(type) {
        return type.startsWith('obstacle.tree.')
            || type.startsWith('obstacle.bush.')
            || type === 'obstacle.flowerbed'
            || type.startsWith('obstacle.flowerbed.');
    }

    /**
     * Register a foliage wobble. Per-tile amplitude/frequency/phase are
     * derived deterministically from the tile's grid coordinates via
     * the same `fract(sin(dot)·43758)` hash the cloud drift uses, so:
     *   - the same stage looks identical on every load,
     *   - neighbouring tiles never sway in lockstep (which would read
     *     as one big board-level rotation, not "trees in the wind").
     *
     * Amplitude stays tiny (~0.03..0.06 rad ≈ 1.7..3.4°) — anything
     * larger feels like an earthquake on a busy board. Frequency
     * spans ~0.4..0.7 Hz (gentle breeze tempo, not a storm).
     */
    _registerWobbleAnim(container, tile, baseRot) {
        const seed1 = tile.c * 12.9898 + tile.r * 78.233;
        const seed2 = tile.c * 39.346  + tile.r * 11.135;
        const seed3 = tile.c * 23.140  + tile.r * 53.711;
        const r1 = Math.sin(seed1) * 43758.5453; const f1 = r1 - Math.floor(r1);
        const r2 = Math.sin(seed2) * 43758.5453; const f2 = r2 - Math.floor(r2);
        const r3 = Math.sin(seed3) * 43758.5453; const f3 = r3 - Math.floor(r3);

        const amp     = 0.030 + f1 * 0.030;            // 0.030..0.060 rad
        const freqHz  = 0.40  + f2 * 0.30;             // 0.40..0.70 Hz
        const freqRad = freqHz * Math.PI * 2;
        const phase   = f3 * Math.PI * 2;

        this._wobbleAnims.push({ container, baseRot, amp, freqRad, phase });
    }

    /**
     * Push a cloud sprite into `_cloudAnims` with a deterministic
     * per-cloud drift speed (so the same stage animates identically
     * on every load and clouds don't share a single lockstep pace).
     *
     * Speed range ≈ 0.18..0.45 px / Pixi-ticker-frame, i.e. ~11..27 px/s
     * at 60 fps — slow, dreamy drift. Every cloud moves in the SAME
     * direction (rightward); the spread just means a few small ones
     * gradually overtake the larger drifters, exactly the parallax
     * cue the user asked for.
     *
     * Hash is the classic `fract(sin(dot) * 43758)` 1-line PRNG —
     * stable across runs, no allocation, no Math.random() jitter.
     */
    _registerCloudAnim(node, w) {
        const seed = node.x * 12.9898 + node.y * 78.233;
        const r = Math.sin(seed) * 43758.5453;
        const r01 = r - Math.floor(r);
        const speed = 0.18 + r01 * 0.27;
        this._cloudAnims.push({ node, w, speed });
    }

    _buildDecorationFallback(element, w, h) {
        const g = new Graphics();
        const color = element?.fallbackColor ?? 0x475569;
        g.roundRect(-w / 2, -h / 2, w, h, Math.min(w, h) * 0.12);
        g.fill({ color, alpha: 0.65 });
        g.stroke({ color: 0x0f172a, width: 2, alpha: 0.85 });
        return g;
    }

    _drawTileGraphics(target, type, connections) {
        target.clear();
        target.removeChildren();

        const cs = this.cellSize;
        const slotKey = getTrackSlotKey(type, connections);
        if (slotKey && this._themeManager?.createSprite) {
            const sprite = this._themeManager.createSprite(slotKey, cs - 2, cs - 2, { fitMode: 'contain' });
            if (sprite) {
                sprite.x = cs / 2;
                sprite.y = cs / 2;
                sprite.rotation = getTrackRotation(type, connections);
                target.addChild(sprite);
                return;
            }
        }
        // Snake-game style: amber bed → cream rails → brown ties, drawn
        // directly over whatever the board background is (sky / grass /
        // water artwork) so the rails read like real track laid on the
        // landscape rather than on a dark mat.
        drawTrainsimTrack(target, cs, connections);
    }

    _paintEntity(layer, entity, parent) {
        const cs = this.cellSize;
        const cx = entity.c * cs + cs / 2;
        const cy = entity.r * cs + cs / 2;
        const gfx = new Container();
        gfx.x = cx;
        gfx.y = cy;
        gfx.zIndex = 10;

        // Side-channel state we carry on the entity object itself so
        // toggling a lamp/switch can mutate without breaking the source
        // stage JSON (we treat the JSON as immutable until save).
        // In the editor we ALWAYS resync the runtime state from the
        // entity's `initial` prop so changing the dropdown actually
        // shows up in the preview. In the live game `active` /
        // `state` are mutated by player taps and must persist across
        // re-renders, so we only set them on first paint.
        if (entity.type === 'lamp') {
            if (this._editorMode || entity.state == null) {
                const initial = entity.props?.initial;
                if (initial === 'red' || initial === 'green') {
                    // Designer picked an explicit colour — honour it
                    // verbatim so a hand-crafted starting layout
                    // stays consistent in editor and game.
                    entity.state = initial;
                } else {
                    // 'auto' / missing / legacy: deterministic red in
                    // the editor preview, random 50/50 at game start.
                    entity.state = this._editorMode
                        ? 'red'
                        : (Math.random() < 0.5 ? 'red' : 'green');
                }
            }
        }
        if (entity.type === 'switch') {
            // SWITCHES NOW ROUTE — they do NOT close ports. The
            // underlying tile's full connections stay open at the
            // switch cell, and the switch just picks WHICH exit a
            // train takes given the entry direction. That way a
            // train can never crash *at* a switch — it just gets
            // routed.
            //
            // We keep per-state visual masks (`_routeMasks[i]`)
            // around for the underlay highlight + the HUD switch
            // tile preview. Legacy `_stateA` / `_stateB` mirror the
            // first two routes for any code path that still asks
            // for them directly.
            const underTile = this._trackTileGrid.get(`${entity.c},${entity.r}`);
            let connSource;
            if (underTile && Array.isArray(underTile._connections)) {
                connSource = underTile._connections;
            } else {
                // Legacy / no underlying tile: synthesize a connection
                // mask from the switch's own baseType + rot so old
                // stages still load.
                const legacy = deriveSwitchStates(entity.props?.baseType || 't', entity.props?.rot || 0);
                // Both states OR'ed together gives "every port the
                // junction can ever use" — equivalent to the full
                // underlying tile's connections.
                connSource = legacy.stateA.map((v, i) => v || legacy.stateB[i]);
            }
            entity._underlyingConn = connSource;
            const derived = deriveSwitchRoutesFromConnections(connSource);
            const routes = derived?.routes && derived.routes.length > 0
                ? derived.routes
                : (derived?.routeA ? [derived.routeA] : []);
            entity._routes = routes;
            // Back-compat aliases for any external code still asking
            // for the binary A/B form.
            entity._routeA = routes[0] || null;
            entity._routeB = routes[1] || routes[0] || null;

            // Per-route visual masks: highlight only the ports that
            // the route actually uses, so the underlay reads as
            // "active lanes" without claiming any port is closed.
            const masksFromRoute = (route) => {
                const mask = [false, false, false, false, false, false, false, false];
                if (!route || route.size === 0) return connSource.slice();
                for (const [entry, exit] of route.entries()) {
                    if (entry >= 0) mask[entry] = true;
                    if (exit >= 0) mask[exit] = true;
                }
                return mask;
            };
            entity._routeMasks = routes.map(masksFromRoute);
            entity._stateA = entity._routeMasks[0] || connSource.slice();
            entity._stateB = entity._routeMasks[1] || entity._stateA;

            // Initial state — accept legacy 'A'/'B' strings, numeric
            // indices, or a missing prop. Editor previews always
            // start at the configured initial; live play preserves
            // whatever the player has cycled to so a re-paint (e.g.
            // a redraw caused by another nearby change) doesn't
            // snap the switch back to state 0.
            const initialRaw = entity.props?.initial;
            const initialIdx = TrainSimBoard._normalizeSwitchIndex(
                initialRaw == null ? 0 : initialRaw,
                routes.length || 1,
            );
            if (this._editorMode || entity.active == null) {
                entity.active = initialIdx;
            } else {
                entity.active = TrainSimBoard._normalizeSwitchIndex(entity.active, routes.length || 1);
            }

            // Per-switch SIGNAL (the new train-stop). Editor preview is
            // deterministic green so authored routes read cleanly; live
            // play randomizes red/green at stage start (the scene calls
            // `randomizeSwitchSignals`) but we seed a value here too so
            // a redraw before that call never leaves it undefined.
            if (this._editorMode) {
                entity.signal = entity.props?.signal === 'red' ? 'red' : 'green';
            } else if (entity.signal !== 'red' && entity.signal !== 'green') {
                entity.signal = randomSignalAspect();
            }
        }

        switch (entity.type) {
            case 'spawn':   this._drawSpawnEntity(gfx, entity);   this.spawns.push(entity); this._spawnByCell.set(`${entity.c},${entity.r}`, entity); break;
            case 'goal':    this._drawGoalEntity(gfx, entity);    this.goals.push(entity);  this._goalByCell.set(`${entity.c},${entity.r}`, entity); break;
            case 'lamp':    this._drawLampEntity(gfx, entity);    this._lampByCell.set(`${entity.c},${entity.r}`, entity); break;
            case 'switch':  this._drawSwitchEntity(gfx, entity);  this._switchByCell.set(`${entity.c},${entity.r}`, entity); break;
            case 'station': this._drawStationEntity(gfx, entity); this.stations.push(entity); break;
            default:
                console.warn(`[TrainSimBoard] Unknown entity type: ${entity.type}`);
        }

        parent.addChild(gfx);
        this._entityRegistry.set(entity.id, { entity, gfx, layerId: layer.id });
    }

    _redrawEntity(entity) {
        const reg = this._entityRegistry.get(entity.id);
        if (!reg) return;
        reg.gfx.removeChildren();
        switch (entity.type) {
            case 'lamp':    this._drawLampEntity(reg.gfx, entity);   break;
            case 'switch':  this._drawSwitchEntity(reg.gfx, entity); break;
            default: break;
        }
    }

    _drawSpawnEntity(gfx, entity) {
        const cs = this.cellSize;
        const trainId = entity.props?.trainId || 't1';
        const slotKey = `trainsim.spawn.${trainId}`;
        // Station spawn artwork is registered with a multi-cell footprint
        // in `data/trainsim.config.js` (e.g. 4×2 for the Hungarian station
        // PNGs). Size the sprite to that footprint so the artwork reads
        // as a building, not a 30 px postage stamp. `gfx` is already
        // centred on the spawn cell and createSprite returns an anchor
        // 0.5 sprite, so a 4×2 station simply overhangs into neighbours
        // visually while the spawn point itself stays at the single (c,r).
        const element = this._themeManager?.getElement?.(slotKey) || null;
        const fw = Math.max(1, element?.footprint?.w || 1);
        const fh = Math.max(1, element?.footprint?.h || 1);
        const w = fw * cs * STATION_VISUAL_SCALE;
        const h = fh * cs * STATION_VISUAL_SCALE;
        const sprite = this._themeManager?.createSprite?.(slotKey, w, h, { fitMode: 'contain' });
        const colorHex = COLOR_NAME_TO_HEX[entity.props?.color]
            || COLOR_NAME_TO_HEX[TRAIN_ID_TO_COLOR_NAME[trainId]]
            || 0xffffff;
        const dir = Number(entity.props?.dir ?? 1);

        if (sprite) {
            sprite.x = 0;
            sprite.y = 0;
            // In the editor we want the user to see THROUGH the station
            // sprite to the underlying track so they can verify that
            // the spawn is actually on a usable piece of track. The
            // direction arrow + anchor pip drawn below then live on
            // TOP of the faded sprite where they read unambiguously.
            if (this._editorMode) sprite.alpha = 0.55;
            gfx.addChild(sprite);
            if (this._editorMode) {
                // Editor: small directional arrow + pip on the spawn
                // cell tells the designer WHICH cell the spawn is
                // pinned to and which way the train will depart.
                gfx.addChild(this._drawSpawnEditorOverlay(cs, colorHex, dir, trainId));
                // Same compact dark "FROM origin → TO destination"
                // card the player sees during gameplay, so the
                // editor matches what the stage looks like at
                // runtime. Designer scans the board, reads the
                // route off each spawn's card, no inspector
                // round-trip needed. Anchored at the same
                // gameplay offset so it overlaps the roofline
                // instead of floating somewhere above it.
                const sprH = Math.max(1, fh);
                const cardCenterY = -cs * (sprH / 2) * STATION_VISUAL_SCALE + cs * 0.2;
                gfx.addChild(this._drawStationInfoCard(cs, colorHex, {
                    from:    this._spawnDisplayCity(entity),
                    to:      this._goalDisplayCity(this._goalByTrainId.get(trainId)),
                    centerY: cardCenterY,
                    trainId,
                    anchorEntity: entity,
                }));
            } else {
                // Game: clean timetable-style info card mounted ON
                // TOP of the station roof. Two lines —
                //
                //   1) origin station (where the train is parked)
                //   2) "→ destination city" (looked up from the
                //      matching goal entity's destinationCity prop)
                //
                // This replaces the old "huge number + START
                // ribbon" badge, which the player couldn't decode.
                const sprH = Math.max(1, fh);
                // Plant the FROM card just below the building's top
                // edge so it visually "reaches" the roofline and a
                // couple of pixels of the card overlap the front of
                // the station artwork — looks like a real signpost
                // bolted to the building rather than a label floating
                // detached in the sky. Card is anchored to the
                // visually-scaled sprite, not the grid footprint.
                //
                // Per client direction: gameplay shows ONLY the
                // current-location name on station roofs. The
                // destination ("WHERE") moved off the station and
                // onto the train itself so the player can identify
                // each train by its goal city even when several
                // are running around the board simultaneously.
                const cardCenterY = -cs * (sprH / 2) * STATION_VISUAL_SCALE + cs * 0.2;
                gfx.addChild(this._drawStationInfoCard(cs, colorHex, {
                    from:    this._spawnDisplayCity(entity),
                    centerY: cardCenterY,
                    trainId,
                    anchorEntity: entity,
                    mode:    'origin-only',
                }));
            }
            return;
        }
        // Procedural fallback: a coloured triangle pointing in the spawn dir.
        const g = new Graphics();
        const r = cs * 0.32;
        // Triangle pointing in dir; rotate around 0,0.
        const tip = { x: 0, y: -r };
        const base1 = { x: -r * 0.7, y: r * 0.5 };
        const base2 = { x:  r * 0.7, y: r * 0.5 };
        g.moveTo(tip.x, tip.y);
        g.lineTo(base1.x, base1.y);
        g.lineTo(base2.x, base2.y);
        g.closePath();
        g.fill(colorHex);
        g.stroke({ color: 0xffffff, width: 2 });
        g.rotation = (dir * Math.PI) / 4; // 0=N (up) ... 4=S (down), 8-way
        gfx.addChild(g);

        const label = new Text({ text: entity.props?.trainId?.toUpperCase() || 'T?', style: ENTITY_LABEL_STYLE });
        label.anchor.set(0.5);
        label.x = 0;
        label.y = cs * 0.32;
        gfx.addChild(label);
    }

    /**
     * Build the editor-only spawn overlay: a coloured arrow centred on
     * the anchor cell pointing the way the train will leave, plus a
     * tiny outlined ring on the anchor cell so the multi-cell station
     * sprite never makes you guess WHICH cell the spawn is pinned to.
     * Returns a Container ready to be added.
     *
     * The arrow's rotation honours the 8-way trainsim direction model
     * (0=N, 1=NE, …, 7=NW) so diagonal spawns are drawn diagonally.
     *
     * The train-id + city label is drawn separately by
     * `_drawEditorEntityBadge` and floated above the building so it
     * never gets hidden by the station artwork or the arrow.
     */
    _drawSpawnEditorOverlay(cs, colorHex, dir, _trainId) {
        const wrap = new Container();

        // Anchor pip — a small white-bordered coloured ring on the
        // exact spawn cell. Sits ABOVE the faded station sprite so it
        // remains visible even when the artwork overhangs.
        const pip = new Graphics();
        pip.circle(0, 0, cs * 0.18);
        pip.fill({ color: 0xffffff, alpha: 0.55 });
        pip.circle(0, 0, cs * 0.18);
        pip.stroke({ color: colorHex, width: Math.max(2, cs * 0.07) });
        wrap.addChild(pip);

        // Direction arrow — a bold chevron jutting out from the pip
        // toward the heading. Drawn at unit-N then rotated; rot of
        // dir*45° matches the 8-way DIR_DELTA model used by the runtime.
        const arrow = new Graphics();
        const armLen = cs * 0.55;
        const halfWide = cs * 0.18;
        const tipY = -armLen;
        const baseY = -armLen * 0.45;
        arrow.moveTo(0, tipY);
        arrow.lineTo(halfWide, baseY);
        arrow.lineTo(halfWide * 0.45, baseY);
        arrow.lineTo(halfWide * 0.45, baseY + cs * 0.18);
        arrow.lineTo(-halfWide * 0.45, baseY + cs * 0.18);
        arrow.lineTo(-halfWide * 0.45, baseY);
        arrow.lineTo(-halfWide, baseY);
        arrow.closePath();
        arrow.fill({ color: colorHex, alpha: 0.95 });
        arrow.stroke({ color: 0xffffff, width: Math.max(2, cs * 0.07) });
        arrow.rotation = (Math.PI / 4) * dir;
        wrap.addChild(arrow);

        return wrap;
    }


    // ── Path prediction overlay ─────────────────────────

    /**
     * Walk the track graph from a spawn until the train either
     * reaches its goal, crashes (no valid continuation), or loops.
     * Returns an array of `{ c, r }` cells in order — the spawn cell
     * first, the terminal cell last.
     *
     * The walk mirrors `TrainSimTrain`'s exit-picking exactly: first
     * step forces the spawn's chosen direction (so the prediction
     * agrees with what the runtime will actually do on frame one),
     * subsequent steps use `getExitDir` which is heading-aware so
     * Y / T junctions follow their straightest exit by default.
     *
     * Live switch state is read via `_getConnectionsAt`, so toggling
     * a switch changes the predicted path instantly.
     */
    _predictTrainPath(spawn, opts = {}) {
        // Allow callers (live HUD, solver) to override the start cell
        // and exit direction so we can predict from a train's CURRENT
        // position once it's running, not just its spawn cell.
        const startCol = opts.c ?? spawn?.c;
        const startRow = opts.r ?? spawn?.r;
        const startDir = opts.exitDir ?? Number(spawn?.props?.dir ?? 0);
        const trainId  = opts.trainId ?? spawn?.props?.trainId;

        if (startCol == null || startRow == null) return { path: [], outcome: 'crash' };
        if (startDir < 0 || startDir > 7) {
            return { path: [{ c: startCol, r: startRow }], outcome: 'crash' };
        }

        const goal = this.goals.find((g) => g.props?.trainId === trainId);
        const path = [{ c: startCol, r: startRow }];

        let c = startCol;
        let r = startRow;
        let exitDir = startDir;

        const visited = new Set();
        const MAX_STEPS = (this.cols * this.rows) + 8;
        let outcome = 'crash';
        for (let step = 0; step < MAX_STEPS; step++) {
            const nc = c + DIR_DELTA[exitDir].dc;
            const nr = r + DIR_DELTA[exitDir].dr;
            path.push({ c: nc, r: nr });
            c = nc; r = nr;

            if (goal && c === goal.c && r === goal.r) { outcome = 'reach'; break; }
            // Wrong destination — the predicted route lands on ANOTHER
            // train's goal cell. We surface this as a distinct outcome
            // so the path overlay can flash red and warn the player
            // BEFORE the crash actually happens, giving them time to
            // toggle a switch on the route.
            const otherGoal = this._goalByCell.get(`${c},${r}`);
            if (otherGoal && otherGoal.props?.trainId !== trainId) { outcome = 'wrong-goal'; break; }
            if (c < 0 || r < 0 || c >= this.cols || r >= this.rows) { outcome = 'crash'; break; }

            const conns = this._getConnectionsAt(c, r);
            if (!conns) { outcome = 'crash'; break; }

            const entryDir = OPPOSITE[exitDir];
            if (!conns[entryDir]) { outcome = 'crash'; break; }

            const key = `${c},${r}|${entryDir}`;
            if (visited.has(key)) { outcome = 'loop'; break; }
            visited.add(key);

            let nextExit = this._getSwitchExitFor(c, r, entryDir, trainId);
            if (nextExit === -1) nextExit = getExitDir(conns, entryDir);
            if (nextExit === -1) { outcome = 'crash'; break; }
            exitDir = nextExit;
        }
        return { path, outcome };
    }

    /**
     * Public lookup the HUD uses: "if I let train X depart right now,
     * does its current route REACH the goal, CRASH, or LOOP?". Returns
     * an object `{ outcome, path }` so callers can highlight rows or
     * draw their own indicators without re-implementing the trace.
     *
     * When the optional `liveTrain` is passed (a live TrainSimTrain
     * that's already mid-route), the prediction starts from the
     * train's CURRENT cell + heading instead of its spawn — so once
     * a train has crossed a switch the indicator updates to "OK" as
     * soon as the remaining route is clear, even if the spawn-based
     * route is blocked.
     */
    predictTrainOutcome(trainId, liveTrain = null) {
        const spawn = this.spawns.find((s) => s.props?.trainId === trainId);
        if (!spawn && !liveTrain) return { outcome: 'crash', path: [] };
        if (liveTrain && liveTrain._reachedGoal) return { outcome: 'reach', path: [] };
        if (liveTrain && !liveTrain._alive)      return { outcome: 'crash', path: [] };
        if (liveTrain && liveTrain._started) {
            return this._predictTrainPath(spawn, {
                c: liveTrain._col,
                r: liveTrain._row,
                exitDir: liveTrain._exitDir,
                trainId,
            });
        }
        return this._predictTrainPath(spawn);
    }

    /**
     * Return the next `count` cells the live train is predicted to
     * roll into, starting from the cell IMMEDIATELY AFTER its
     * current head cell. This is exactly the slice
     * (`path.slice(1, count+1)`) that the magenta aim-line dots
     * ride on, exposed publicly so the scene can run pairwise
     * overlap checks for the proactive danger-rewind trigger.
     *
     * Returns `[]` for trains that aren't currently rolling
     * (not-yet-started, dead, already at goal) so callers don't
     * need to re-implement those guards.
     *
     * @param {import('./TrainSimTrain.js').TrainSimTrain} train
     * @param {number} [count] — defaults to `AIM_AHEAD_CELLS` so
     *   the returned cells line up exactly with the visible dots.
     * @returns {{c: number, r: number}[]}
     */
    getTrainAheadCells(train, count = TrainSimBoard.AIM_AHEAD_CELLS) {
        if (!train || !train._alive || !train._started || train._reachedGoal) {
            return [];
        }
        const spawn = this._spawnByTrainId.get(train.trainId)
            || this.spawns.find((s) => s.props?.trainId === train.trainId);
        const { path } = this._predictTrainPath(spawn, {
            c: train._col,
            r: train._row,
            exitDir: train._exitDir,
            trainId: train.trainId,
        });
        return path.slice(1, count + 1);
    }

    /**
     * Publish the current `_trains` snapshot to the board so the
     * `isCellOccupiedByOther` helper on the board API has a live
     * list to scan. Replaces the old per-frame side-effect of the
     * autopilot (REMOVED): the scene calls this once per
     * `update()` tick BEFORE iterating the train loop, so the
     * collision-guard still sees every alive train.
     */
    setLiveTrains(trains) {
        this._liveTrains = trains || [];
    }

    // ── NOTE ───────────────────────────────────────────────────
    //   The legacy SOLVE / AUTOPILOT machinery (`solveAllRoutes`,
    //   `tickAutopilot`, `_simulateAutopilotTimelines`,
    //   `_findAllAutopilotConflicts`, `_apply/_releaseAutopilotLamps`,
    //   `_computeAutopilotHolds`, etc. — ~1000 lines of brute-force
    //   search + timeline simulation + lamp arbitration) was
    //   REMOVED. The player now drives the puzzle entirely by
    //   hand with the RESTART + REWIND buttons.
    // ──────────────────────────────────────────────────────────


    /**
     * Erase + redraw every spawn's predicted path on the overlay.
     * Each train's line is tinted in its own colour (so player can
     * tell five paths apart on a busy board), drawn as a thick faded
     * stroke through the cell centres of the predicted route.
     *
     * Trains that have already reached their goal (added to
     * `_hiddenPathTrains` via `setTrainPathVisible(false)`) are
     * silently skipped, leaving the board uncluttered after a win.
     */
    _refreshPathOverlay() {
        if (!this._pathOverlay) return;
        this._pathOverlay.clear();
        if (this._pathOverlayUnder) this._pathOverlayUnder.clear();
        if (!this._pathOverlayVisible) return;
        const cs = this.cellSize;
        // ── The wide coloured under-band that used to halo each
        //   spawn's full route is INTENTIONALLY no longer painted.
        //   The map was getting busy with 5 trains' bands crossing
        //   at every junction, so we replaced it with a moving
        //   pool-style "5 cells ahead" aim line (see
        //   `_aimLineLayer` + `tickAimLines`) that only previews
        //   the IMMEDIATE near-future of each running train.
        //
        //   The endpoint markers below (disc / X / triangle / ∞)
        //   are still useful — they give a static "this train's
        //   final destination" indicator the player can scan at
        //   a glance — so we keep drawing those on `_pathOverlay`.
        const lineWidth = Math.max(6, cs * 0.48);

        // Reset the "any wrong-goal route?" flag so `tickPathOverlayPulse`
        // can keep itself idle on stages where every route is fine.
        let hasWrongGoal = false;

        for (const spawn of this.spawns) {
            const trainId = spawn.props?.trainId || 't1';
            if (this._hiddenPathTrains.has(trainId)) continue;
            const { path, outcome } = this._predictTrainPath(spawn);
            if (path.length < 2) continue;
            if (outcome === 'wrong-goal') hasWrongGoal = true;

            const colorName = spawn.props?.color || TRAIN_ID_TO_COLOR_NAME[trainId] || 'red';
            const trainColorHex = COLOR_NAME_TO_HEX[colorName] || 0xffffff;
            // Wrong-destination endpoints paint in fire-engine red so
            // the player's eye gets pulled to the mistake BEFORE the
            // train actually crashes into the wrong station.
            const isWrong = outcome === 'wrong-goal';
            const colorHex = isWrong ? 0xdc2626 : trainColorHex;

            // Endpoint marker — shape + colour communicates the
            // outcome of the route at a glance:
            //   reach → big bright disc with a white ring (success)
            //   crash → red ring with a black X
            //   loop  → orange ring with a curved arrow
            const last = path[path.length - 1];
            const cx = last.c * cs + cs / 2;
            const cy = last.r * cs + cs / 2;
            const tipR = lineWidth * 0.78;
            if (outcome === 'reach') {
                this._pathOverlay.circle(cx, cy, tipR);
                this._pathOverlay.fill({ color: colorHex, alpha: 0.95 });
                this._pathOverlay.circle(cx, cy, tipR);
                this._pathOverlay.stroke({ color: 0xffffff, width: 3, alpha: 0.95 });
                // Inner check-tick.
                this._pathOverlay.moveTo(cx - tipR * 0.45, cy);
                this._pathOverlay.lineTo(cx - tipR * 0.1,  cy + tipR * 0.4);
                this._pathOverlay.lineTo(cx + tipR * 0.5,  cy - tipR * 0.45);
                this._pathOverlay.stroke({ color: 0xffffff, width: Math.max(2.5, lineWidth * 0.18), alpha: 1, cap: 'round', join: 'round' });
            } else if (outcome === 'crash') {
                this._pathOverlay.circle(cx, cy, tipR);
                this._pathOverlay.fill({ color: 0xdc2626, alpha: 0.95 });
                this._pathOverlay.circle(cx, cy, tipR);
                this._pathOverlay.stroke({ color: 0xffffff, width: 3, alpha: 0.95 });
                // Big white X over the red bullseye.
                const xs = tipR * 0.5;
                this._pathOverlay.moveTo(cx - xs, cy - xs);
                this._pathOverlay.lineTo(cx + xs, cy + xs);
                this._pathOverlay.moveTo(cx + xs, cy - xs);
                this._pathOverlay.lineTo(cx - xs, cy + xs);
                this._pathOverlay.stroke({ color: 0xffffff, width: Math.max(3, lineWidth * 0.22), alpha: 1, cap: 'round' });
            } else if (outcome === 'wrong-goal') {
                // Pulsing yellow-red warning triangle on the wrong
                // goal cell — "you're about to deliver this train
                // to the wrong station!". Drawn larger than the
                // crash marker so it doesn't blend in with the
                // many other route endpoints when 5 trains all
                // have predictions on the board.
                const triR = tipR * 1.1;
                this._pathOverlay.poly([
                    cx,           cy - triR,
                    cx + triR,    cy + triR * 0.85,
                    cx - triR,    cy + triR * 0.85,
                ]);
                this._pathOverlay.fill({ color: 0xfacc15, alpha: 0.95 });
                this._pathOverlay.poly([
                    cx,           cy - triR,
                    cx + triR,    cy + triR * 0.85,
                    cx - triR,    cy + triR * 0.85,
                ]);
                this._pathOverlay.stroke({ color: 0xdc2626, width: Math.max(3, lineWidth * 0.22), alpha: 1, cap: 'round', join: 'round' });
                // Exclamation mark inside the triangle.
                this._pathOverlay.rect(cx - triR * 0.09, cy - triR * 0.35, triR * 0.18, triR * 0.55);
                this._pathOverlay.fill({ color: 0x111827, alpha: 1 });
                this._pathOverlay.circle(cx, cy + triR * 0.45, triR * 0.11);
                this._pathOverlay.fill({ color: 0x111827, alpha: 1 });
            } else {
                // loop
                this._pathOverlay.circle(cx, cy, tipR);
                this._pathOverlay.fill({ color: 0xf59e0b, alpha: 0.95 });
                this._pathOverlay.circle(cx, cy, tipR);
                this._pathOverlay.stroke({ color: 0xffffff, width: 3, alpha: 0.95 });
                // "∞" suggestion: two small circles inside.
                const ir = tipR * 0.32;
                this._pathOverlay.circle(cx - ir * 0.8, cy, ir);
                this._pathOverlay.circle(cx + ir * 0.8, cy, ir);
                this._pathOverlay.stroke({ color: 0xffffff, width: Math.max(2, lineWidth * 0.15), alpha: 1 });
            }
        }
        this._hasWrongGoal = hasWrongGoal;
    }

    /**
     * Spawn a celebratory confetti burst on the goal cell that
     * belongs to `trainId`. Called from the scene the first frame
     * a train transitions into `_reachedGoal === true`. No-ops if
     * the train has no goal entity (defensive — every authored
     * train should have one).
     *
     * Particles are coloured around the train's own colour with
     * a sprinkle of bright accents so it reads as a party popper
     * rather than a monochrome wash. ~36 particles per burst is
     * enough to fill the goal cell + neighbours without dropping
     * frame rate even with several trains arriving at once.
     */
    spawnGoalConfetti(trainId, opts = {}) {
        if (!this._confettiLayer || this._confettiLayer.destroyed) return;
        const goal = this.goals.find((g) => g.props?.trainId === trainId);
        if (!goal) return;
        const cs = this.cellSize;
        const cx = goal.c * cs + cs / 2;
        const cy = goal.r * cs + cs / 2;
        const baseColorName = goal.props?.color || TRAIN_ID_TO_COLOR_NAME[trainId] || 'red';
        const baseHex = COLOR_NAME_TO_HEX[baseColorName] || 0xffffff;
        // Accent palette mixes in bright "festival" colours so the
        // burst doesn't look like one solid blob of the train's hue.
        const palette = [
            baseHex,
            0xfde047, // yellow
            0xf472b6, // pink
            0x60a5fa, // sky
            0x86efac, // mint
            0xfb923c, // orange
            0xffffff, // white sparkle
        ];

        // `big` arrivals (a train successfully reaching its destination)
        // get a thicker burst with longer-lived flakes. Mid-game
        // chimes still call this with no opts and keep the old subtle
        // 36-particle burst so the screen doesn't drown in confetti
        // every time something cosmetic fires.
        const big = !!opts.big;
        const N = big ? 90 : 36;
        const speedScale  = big ? 1.6 : 1.0;
        const lifeBoost   = big ? 0.9 : 0.0;
        const spawnSpread = big ? 0.7 : 0.4;
        for (let i = 0; i < N; i++) {
            const color = palette[Math.floor(Math.random() * palette.length)];
            // Random sizes: a mix of small + large flakes reads as a
            // real popper. Square or thin-rectangle silhouettes.
            const w = Math.max(3, cs * (0.10 + Math.random() * 0.18));
            const h = Math.max(2, w * (0.35 + Math.random() * 0.45));
            const g = new Graphics();
            g.rect(-w / 2, -h / 2, w, h);
            g.fill({ color, alpha: 1 });
            // Subtle dark edge so the flake reads on bright backgrounds.
            g.rect(-w / 2, -h / 2, w, h);
            g.stroke({ color: 0x0b1220, width: 0.6, alpha: 0.5 });
            g.x = cx + (Math.random() - 0.5) * cs * spawnSpread;
            g.y = cy + (Math.random() - 0.5) * cs * spawnSpread;
            g.rotation = Math.random() * Math.PI * 2;
            this._confettiLayer.addChild(g);

            // Velocities: fan upward + outward, mostly up. Sideways
            // spread is wide so the cone fills a couple of cells.
            const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.05;
            const speed = cs * (1.2 + Math.random() * 2.0) * speedScale;
            this._confettiParticles.push({
                gfx: g,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                vrot: (Math.random() - 0.5) * 16,
                life: 0,
                maxLife: 1.1 + Math.random() * 0.7 + lifeBoost,
            });
        }
    }

    /**
     * Per-frame update for confetti particles: integrate velocity
     * + gravity, spin, fade the alpha, and recycle anything past
     * its maxLife. Cheap no-op when no particles are alive.
     */
    tickGoalConfetti(delta) {
        if (!this._confettiLayer || this._confettiLayer.destroyed) return;
        const dtSec = (delta || 1) / 60;
        const particles = this._confettiParticles;
        if (particles && particles.length > 0) {
            const cs = this.cellSize;
            const gravity = cs * 5.5; // px/sec² scaled to cellsize

            for (let i = particles.length - 1; i >= 0; i--) {
                const p = particles[i];
                p.life += dtSec;
                if (p.life >= p.maxLife) {
                    if (p.gfx) {
                        if (p.gfx.parent) p.gfx.parent.removeChild(p.gfx);
                        p.gfx.destroy?.();
                    }
                    particles.splice(i, 1);
                    continue;
                }
                p.vy += gravity * dtSec;
                // Light horizontal drag so the spread arc looks natural.
                p.vx *= (1 - 0.6 * dtSec);
                p.gfx.x += p.vx * dtSec;
                p.gfx.y += p.vy * dtSec;
                p.gfx.rotation += p.vrot * dtSec;
                // Fade out over the last 35 % of the lifespan.
                const tNorm = p.life / p.maxLife;
                p.gfx.alpha = tNorm < 0.65 ? 1 : Math.max(0, 1 - (tNorm - 0.65) / 0.35);
            }
        }
        this._tickArrivalBanners(dtSec);
        this._tickBoardingFx(dtSec);
    }

    /**
     * Per-frame boarding visuals — pulsing ring around the spawn
     * station of every train inside its 10-second pre-departure
     * window, plus a centred "5 / 4 / 3 / 2 / 1" countdown that
     * pops above the roof for the final 5 seconds. Pairs with the
     * existing audio bell (`TrainSimScene._playDepartureBell`) and
     * the menetrend row flash (`TrainSimHud`) so the player gets
     * three coordinated cues — visual on the BOARD, visual on the
     * TOP STRIP, and audio — pointing at the same train.
     *
     * Lifecycle:
     *   • First frame inside the window — lazy-build a Container
     *     anchored on the spawn cell, sized off the spawn slot's
     *     footprint (matching how `_drawSpawnEntity` lays the
     *     sprite). Holds a `Graphics` ring + a `Text` number.
     *   • Every subsequent boarding frame — re-stroke the ring with
     *     a sin-wave-driven width/alpha/inset (yellow for the
     *     first half of the window, switching to green when the
     *     countdown is ticking so it visibly says "GO"), update
     *     the countdown text whenever the ceil(remaining) changes,
     *     and run a quick scale-pop on each new digit.
     *   • Frame the train actually starts (or boarding ends) —
     *     remove + destroy the FX container, drop the map entry.
     */
    _tickBoardingFx(dtSec) {
        const layer = this._boardingFxLayer;
        if (!layer || layer.destroyed) return;
        if (!this._boardingFxByTrainId) this._boardingFxByTrainId = new Map();
        const map = this._boardingFxByTrainId;
        const trains = this._liveTrains || [];
        const cs = this.cellSize;

        // First pass — collect ids that ARE boarding right now so
        // we can detach FX for trains that left the window or
        // departed since the last frame.
        const stillBoarding = new Set();
        for (const train of trains) {
            if (train?._isBoarding && !train._started && train._alive) {
                stillBoarding.add(train.trainId);
            }
        }
        for (const [trainId, st] of map) {
            if (stillBoarding.has(trainId)) continue;
            if (st.wrap?.parent) st.wrap.parent.removeChild(st.wrap);
            try { st.wrap?.destroy?.({ children: true }); } catch (_) {}
            map.delete(trainId);
        }

        // Second pass — drive each live boarding train.
        for (const train of trains) {
            if (!train?._isBoarding || train._started || !train._alive) continue;

            const trainId = train.trainId;
            const remaining = Math.max(0, train.startDelay - train._waitTimer);
            const secondInt = Math.ceil(remaining);

            let st = map.get(trainId);
            if (!st) {
                const spawn = this._spawnByTrainId.get(trainId);
                if (!spawn) continue;
                const slotKey = `trainsim.spawn.${trainId}`;
                const el = this._themeManager?.getElement?.(slotKey) || null;
                const fw = Math.max(1, el?.footprint?.w || 1);
                const fh = Math.max(1, el?.footprint?.h || 1);
                const w = fw * cs * STATION_VISUAL_SCALE;
                const h = fh * cs * STATION_VISUAL_SCALE;

                const wrap = new Container();
                wrap.x = spawn.c * cs + cs / 2;
                wrap.y = spawn.r * cs + cs / 2;

                const ring = new Graphics();
                wrap.addChild(ring);

                const countdown = new Text({
                    text: '',
                    style: new TextStyle({
                        fontFamily: 'Arial Black, Arial, sans-serif',
                        fontSize: Math.max(28, Math.round(cs * 1.6)),
                        fontWeight: '900',
                        fill: '#fde047',
                        stroke: { color: 0x0b1220, width: Math.max(4, cs * 0.22) },
                        align: 'center',
                        dropShadow: {
                            color: 0x000000,
                            alpha: 0.55,
                            blur: 4,
                            angle: Math.PI / 2,
                            distance: 3,
                        },
                    }),
                });
                countdown.anchor.set(0.5);
                // Sit the number just above the spawn-cell centre so it
                // lands ON the station building and stays on-screen.
                // (Earlier it was offset by -h/2, but the 4-cell-tall
                // station footprint pushed it ~3 cells up — off the top
                // of the board / behind the menetrend strip, so the
                // player couldn't see the count down.)
                countdown.y = -cs * 0.35;
                countdown.visible = false;
                wrap.addChild(countdown);

                layer.addChild(wrap);
                st = { wrap, ring, countdown, w, h,
                    ringPhase: 0, lastSec: -1, popPhase: 0 };
                map.set(trainId, st);
            }

            // Animate ring (yellow during the first part of the
            // window, green once we're inside the countdown so the
            // visual "warms up" toward departure). Sin-wave drives
            // alpha + width + inset for a heartbeat feel.
            st.ringPhase += dtSec * 4.5; // ≈ 0.7 Hz
            const pulse = 0.5 + 0.5 * Math.sin(st.ringPhase);
            // Countdown covers the final 4 seconds (4·3·2·1) so it
            // lines up with the 4-beep `countdown.mp3` cue; 0 is the
            // departure instant.
            const inCountdown = secondInt <= 4;
            const ringColor = inCountdown ? 0x22c55e : 0xfde047;
            const padX = cs * (0.18 + pulse * 0.32);
            const padY = cs * (0.18 + pulse * 0.32);
            const halfW = st.w / 2 + padX;
            const halfH = st.h / 2 + padY;
            const stroke = Math.max(2, cs * (0.12 + 0.10 * pulse));

            const ring = st.ring;
            ring.clear();
            ring.roundRect(-halfW, -halfH, halfW * 2, halfH * 2, cs * 0.35);
            ring.stroke({
                color: ringColor,
                width: stroke,
                alpha: 0.40 + 0.50 * pulse,
            });
            // Faint inner echo so the ring reads on busy tile
            // backgrounds (track lines, road carriageway).
            ring.roundRect(-halfW * 0.92, -halfH * 0.92, halfW * 1.84, halfH * 1.84, cs * 0.30);
            ring.stroke({
                color: 0xffffff,
                width: Math.max(1, stroke * 0.35),
                alpha: 0.18 + 0.22 * pulse,
            });

            // Countdown — visible only during the final 4 s. New
            // digits get a quick scale-pop (1.6 → 1.0 over ~0.25 s).
            if (inCountdown && secondInt >= 1) {
                st.countdown.visible = true;
                if (st.lastSec !== secondInt) {
                    st.countdown.text = String(secondInt);
                    st.lastSec = secondInt;
                    st.popPhase = 1.0; // re-arm pop
                }
                const pop = st.popPhase;
                const scale = 1.0 + 0.65 * pop;
                st.countdown.scale.set(scale, scale);
                st.countdown.alpha = 0.85 + 0.15 * (1 - pop);
                if (st.popPhase > 0) {
                    st.popPhase = Math.max(0, st.popPhase - dtSec * 4.0);
                }
            } else {
                st.countdown.visible = false;
                st.lastSec = -1;
            }
        }
    }

    /**
     * Spawn a big "HAS ARRIVED" banner over a train's goal cell. Used
     * by the scene's arrival hook alongside `spawnGoalConfetti(...,
     * { big: true })` so each successful arrival fires three pieces
     * of feedback: confetti burst, banner, and the train's own
     * fade-out (handled inside `TrainSimTrain`).
     *
     * The banner scales up + drifts up + fades out across ~2.6 s so
     * the player gets ~0.5 s of fully-readable text even if several
     * trains arrive in quick succession.
     */
    showArrivalBanner(trainId, name = null, message = null) {
        if (!this._confettiLayer || this._confettiLayer.destroyed) return;
        const goal = this.goals.find((g) => g.props?.trainId === trainId);
        if (!goal) return;
        const cs = this.cellSize;
        const cx = goal.c * cs + cs / 2;
        const cy = goal.r * cs + cs / 2;
        // Headline — just the "HAS ARRIVED!" announcement (the train
        // identity goes on its own line below). A one-off `message`
        // override still wins for any custom announcement.
        const headlineTpl = message || (t('trainsim.has_arrived') || 'HAS ARRIVED!');
        const headlineText = headlineTpl.replace('{id}', (trainId || 't?').toUpperCase());
        // Resolve the human-readable train name (its destination
        // city, e.g. "Miskolc-Tiszai") so the player reads which train
        // arrived instead of a cryptic id like "T1". Prefer the name
        // the caller passed (the train's own chip city), fall back to
        // resolving it from the goal.
        const city = (name || '').trim() || this._goalDisplayCity?.(goal) || '';

        const wrap = new Container();
        wrap.x = cx;
        wrap.y = cy - cs * 1.4;
        wrap.alpha = 0;

        const label = new Text({
            text: headlineText,
            style: new TextStyle({
                fontFamily: 'Arial Black, Arial, sans-serif',
                fontSize: Math.max(20, Math.round(cs * 0.95)),
                fontWeight: '900',
                fill: '#fde047',
                stroke: { color: 0x0b1220, width: Math.max(4, cs * 0.18) },
                letterSpacing: 1.5,
                align: 'center',
                dropShadow: {
                    color: 0x000000,
                    alpha: 0.5,
                    blur: 4,
                    distance: 3,
                    angle: Math.PI / 2,
                },
            }),
        });
        label.anchor.set(0.5);
        wrap.addChild(label);

        // Train-name line UNDER the headline (e.g. "Miskolc-Tiszai
        // vonat"). Only when we actually resolved a name and there's
        // no custom message override that already carries its own text.
        if (city && !message) {
            const nameTpl = t('trainsim.arrived_train') || '{city}';
            const nameLabel = new Text({
                text: nameTpl.replace('{city}', city),
                style: new TextStyle({
                    fontFamily: 'Arial Black, Arial, sans-serif',
                    fontSize: Math.max(14, Math.round(cs * 0.6)),
                    fontWeight: '900',
                    fill: '#ffffff',
                    stroke: { color: 0x0b1220, width: Math.max(3, cs * 0.13) },
                    letterSpacing: 1,
                    align: 'center',
                    dropShadow: {
                        color: 0x000000,
                        alpha: 0.5,
                        blur: 4,
                        distance: 2,
                        angle: Math.PI / 2,
                    },
                }),
            });
            nameLabel.anchor.set(0.5, 0);
            nameLabel.y = label.height / 2 + cs * 0.10;
            wrap.addChild(nameLabel);
        }

        this._confettiLayer.addChild(wrap);
        this._arrivalBanners.push({
            gfx: wrap,
            life: 0,
            maxLife: 2.6,
            originY: wrap.y,
        });
    }

    /**
     * Spawn a fresh "greeters" crowd at the goal station of `trainId`.
     * Called once per train arrival from `TrainSimScene._tick`'s
     * `justArrived` block.
     *
     * Layout strategy (per client direction "show people next to each
     * other, 3 or 4 at 24 % of original size"):
     *   • Pick 3 or 4 figures at random from the two station-people
     *     PNGs (`PEOPLE_SLOT_KEYS`).
     *   • Size each at `PEOPLE_NATIVE_SCALE` of its original pixels.
     *   • Lay them out in a single row, edge-to-edge, centred on and
     *     standing just below the goal cell. Each subsequent arrival
     *     drops another row, so a busy stage progressively fills the
     *     platforms with people.
     *
     * Sprites are placed in BOARD coordinates (cell-grid space) inside
     * `_arrivedCrowdLayer` and live until the next stage reset. The
     * random count + image picks are RNG-seeded by the train id so the
     * same arrival always looks the same on rewind. Because the
     * figures are sized off the PNGs' natural dimensions (measured
     * async on first use), the actual row is built in `_measurePeople`'s
     * callback once those sizes are known.
     */
    addArrivedCrowd(trainId) {
        if (!this._arrivedCrowdLayer || this._arrivedCrowdLayer.destroyed) return;
        const goal = this._goalByTrainId.get(trainId)
            || this.goals.find((g) => g.props?.trainId === trainId);
        if (!goal) return;
        const cs = this.cellSize;
        const tm = this._themeManager;
        if (!tm?.createSprite || !tm.uri) return;

        // Anchor crowd around the GOAL cell. If the goal sits inside a
        // station building (the common case), people land on the
        // platform footprint; for stand-alone goals they cluster on
        // the goal cell itself.
        const cx = goal.c * cs + cs / 2;
        const cy = goal.r * cs + cs / 2;

        // Deterministic-but-varied per arrival: re-running the same
        // arrival (rewind + replay) reproduces the count + picks.
        const seed = (`${trainId || ''}:${this._arrivedCrowdSprites.length}`)
            .split('').reduce((h, ch) => ((h << 5) - h + ch.charCodeAt(0)) | 0, 1);
        let s = seed >>> 0;
        const rng = () => { s = (s * 1664525 + 1013904223) >>> 0; return (s >>> 0) / 0xffffffff; };

        // 3 or 4 figures, each a random pick from the two people PNGs.
        const count = 3 + Math.floor(rng() * 2);
        const picks = [];
        for (let i = 0; i < count; i++) {
            const slot = PEOPLE_SLOT_KEYS[Math.floor(rng() * PEOPLE_SLOT_KEYS.length)];
            // Split 'trainsim.station.people.large' → game + slot for `uri`.
            const dot = slot.indexOf('.');
            const src = tm.uri(slot.slice(0, dot), slot.slice(dot + 1));
            if (src) picks.push({ slot, src });
        }
        if (picks.length === 0) return;

        // Build the row once every picked PNG's natural size is known
        // (so figures sit edge-to-edge without overlap). Re-check the
        // layer in case the scene tore down during the async measure.
        this._measurePeople(picks.map((p) => p.src), (sizes) => {
            if (!this._arrivedCrowdLayer || this._arrivedCrowdLayer.destroyed) return;
            const gap = cs * 0.05;
            const widths = sizes.map((sz) => sz.w * PEOPLE_NATIVE_SCALE);
            const totalW = widths.reduce((a, b) => a + b, 0)
                + gap * Math.max(0, picks.length - 1);
            // Feet on a shared baseline just south of the goal pin, row
            // centred horizontally on the goal cell, then nudged down by
            // PEOPLE_Y_OFFSET_PX so the crowd lands on the platform.
            const baseY = cy + cs * 0.35 + PEOPLE_Y_OFFSET_PX;
            let x = cx - totalW / 2;
            for (let i = 0; i < picks.length; i++) {
                const sz = sizes[i];
                const spr = tm.createSprite(picks[i].slot, sz.w, sz.h, {
                    src: picks[i].src,
                    fitMode: 'native',
                    scaleMultiplier: PEOPLE_NATIVE_SCALE,
                    anchorX: 0,
                    anchorY: 1,
                });
                if (spr) {
                    spr.x = x;
                    spr.y = baseY;
                    this._arrivedCrowdLayer.addChild(spr);
                    this._arrivedCrowdSprites.push(spr);
                }
                x += widths[i] + gap;
            }
        });
    }

    /**
     * Resolve the natural {w,h} pixel size of each `src`, then invoke
     * `cb(sizes)` once all are known. Sizes are cached per-src in
     * `_peopleSizeCache`, so a fully-cached call runs `cb` synchronously
     * (every arrival after the first) and only the very first arrival
     * pays the one-frame image-decode cost.
     *
     * @param {string[]} srcs
     * @param {(sizes: {w:number,h:number}[]) => void} cb
     */
    _measurePeople(srcs, cb) {
        const sizes = new Array(srcs.length);
        let pending = 0;
        srcs.forEach((src, i) => {
            const cached = this._peopleSizeCache.get(src);
            if (cached) { sizes[i] = cached; return; }
            pending++;
            const img = new Image();
            const done = (sz) => {
                this._peopleSizeCache.set(src, sz);
                sizes[i] = sz;
                if (--pending === 0) cb(sizes);
            };
            img.onload = () => done({
                w: img.naturalWidth || img.width || 1,
                h: img.naturalHeight || img.height || 1,
            });
            img.onerror = () => done({ w: 1, h: 1 });
            img.src = src;
        });
        if (pending === 0) cb(sizes);
    }

    /**
     * Drive every live `showArrivalBanner` element. Two phases:
     *   1. 0..0.30 s — pop in: scale 0.4 → 1.18 → 1.0, alpha 0 → 1
     *   2. 0.30..maxLife — drift up + fade out over the final 1.0 s
     * Cheap no-op when `_arrivalBanners` is empty.
     */
    _tickArrivalBanners(dtSec) {
        const banners = this._arrivalBanners;
        if (!banners || banners.length === 0) return;
        const cs = this.cellSize;
        for (let i = banners.length - 1; i >= 0; i--) {
            const b = banners[i];
            b.life += dtSec;
            if (b.life >= b.maxLife) {
                if (b.gfx?.parent) b.gfx.parent.removeChild(b.gfx);
                b.gfx?.destroy?.();
                banners.splice(i, 1);
                continue;
            }
            const tt = b.life;
            let scale = 1.0;
            let alpha = 1.0;
            if (tt < 0.20) {
                scale = 0.4 + (1.18 - 0.4) * (tt / 0.20);
                alpha = tt / 0.20;
            } else if (tt < 0.30) {
                scale = 1.18 - (1.18 - 1.0) * ((tt - 0.20) / 0.10);
            }
            b.gfx.scale.set(scale);
            // Drift up gently across the whole life so the banner
            // floats away rather than just dissolving in place.
            b.gfx.y = b.originY - cs * 0.45 * (tt / b.maxLife);
            const fadeStart = b.maxLife - 1.0;
            if (tt > fadeStart) {
                alpha = Math.max(0, 1 - (tt - fadeStart) / 1.0);
            }
            b.gfx.alpha = alpha;
        }
    }

    /**
     * Drive the breathing animation on any "wrong destination" route
     * stroke. Cheap when no train is heading to the wrong goal: we
     * skip the per-frame redraw unless `_hasWrongGoal` is true. Called
     * by the scene's update loop next to `tickSwitches`.
     */
    tickPathOverlayPulse(delta) {
        if (!this._pathOverlay) return;
        if (!this._hasWrongGoal) return;
        this._wrongGoalPulse += (delta || 1) / 60;
        this._refreshPathOverlay();
    }

    /**
     * Pool-game-style aim line. Every frame, redraw a short solid-
     * magenta trajectory ahead of each LIVE, RUNNING, non-finished
     * train — AIM_AHEAD_CELLS cells (default 5) along the route
     * the switches + lamps will actually send it.
     *
     * SMOOTHNESS: the dots are placed at FIXED ARC DISTANCES from
     * the train's actual pixel position (read from `_headGfx` so we
     * pick up the catmull-spline smoothing the renderer applies),
     * not at static cell centres. As the head sprite slides through
     * a cell, every dot slides forward by the same amount along the
     * predicted polyline — eliminating the per-cell "snap" of the
     * earlier implementation.
     *
     * Color is always `COLOR_SWITCH_ACTIVE` (hot magenta) so the
     * forecast is unmistakable against any train colour, track
     * style, or background art. Alpha is solid (no distance fade).
     *
     * Respects the `ROUTES` HUD toggle (`_pathOverlayVisible`).
     *
     * @param {Array<{trainId:string,_col:number,_row:number,_exitDir:number,_alive:boolean,_started:boolean,_reachedGoal:boolean,_headGfx?:{x:number,y:number}}>} trains
     */
    /**
     * Wipe every "next 5 cells" aim-line dot. Used by the scene's
     * REWIND animation so a forecast anchored to the old cell
     * position doesn't hang on screen while the train slides
     * visually backwards. The normal `tickAimLines` loop will
     * repaint it on the next live frame.
     */
    clearAimLines() {
        if (!this._aimLineLayer || this._aimLineLayer.destroyed) return;
        this._aimLineLayer.clear();
    }

    tickAimLines(trains) {
        if (!this._aimLineLayer || this._aimLineLayer.destroyed) return;
        this._aimLineLayer.clear();
        if (!this._pathOverlayVisible) return;
        if (!Array.isArray(trains) || trains.length === 0) return;

        const cs = this.cellSize;
        const maxAhead = TrainSimBoard.AIM_AHEAD_CELLS;
        const aimColor = TRAINSIM_CONFIG.COLOR_SWITCH_ACTIVE;

        // Tuning: gap between the train's head sprite and the FIRST
        // dot (so the closest dot doesn't sit on top of the locomotive),
        // plus the gap between consecutive dots. Both are in CELLS so
        // they scale with `cs` automatically.
        const headOffset  = cs * 0.55;
        const dotSpacing  = cs * 0.95;
        const dotRadius   = Math.max(4, cs * 0.16);
        const lineWidth   = Math.max(3, cs * 0.12);
        const ringWidth   = Math.max(1.5, cs * 0.04);

        for (const train of trains) {
            if (!train || !train._alive || !train._started || train._reachedGoal) continue;
            if (this._hiddenPathTrains.has(train.trainId)) continue;

            // ── Predict the route the train will take from its
            //   CURRENT cell forward, then slice to the first
            //   `maxAhead` cells AFTER the train's location.
            const spawn = this._spawnByTrainId.get(train.trainId)
                || this.spawns.find((s) => s.props?.trainId === train.trainId);
            const { path } = this._predictTrainPath(spawn, {
                c: train._col,
                r: train._row,
                exitDir: train._exitDir,
                trainId: train.trainId,
            });
            const aheadCells = path.slice(1, maxAhead + 1);
            if (aheadCells.length === 0) continue;

            // ── Head pixel position: prefer the renderer's smoothed
            //   head sprite coords (catmull-spline through cell
            //   centres). Fallback to the current cell's centre on
            //   the first frame, before render() has run once.
            const headGfx = train._headGfx;
            const headX = Number.isFinite(headGfx?.x) && headGfx.x !== 0
                ? headGfx.x
                : train._col * cs + cs / 2;
            const headY = Number.isFinite(headGfx?.y) && headGfx.y !== 0
                ? headGfx.y
                : train._row * cs + cs / 2;

            // ── Build the polyline that the dots will ride on:
            //   [head sprite position, cell1 centre, cell2 centre, ...]
            //   The first segment from head → cell1 is what shrinks
            //   smoothly as the train rolls into cell1, which is
            //   exactly what gives the dots their continuous slide.
            const points = [{ x: headX, y: headY }];
            for (const cell of aheadCells) {
                points.push({ x: cell.c * cs + cs / 2, y: cell.r * cs + cs / 2 });
            }

            // ── Per-segment arc lengths + running total. Used to
            //   convert "dot N is at arc distance X" into an actual
            //   (x, y) by walking segments until we consume X.
            const segLens = new Array(points.length - 1);
            let totalLen = 0;
            for (let i = 0; i < segLens.length; i++) {
                const dx = points[i + 1].x - points[i].x;
                const dy = points[i + 1].y - points[i].y;
                segLens[i] = Math.hypot(dx, dy);
                totalLen += segLens[i];
            }
            if (totalLen <= 0) continue;

            // ── Connecting line: thin magenta stroke through every
            //   polyline point so the dots read as ONE trajectory
            //   even on long straight stretches.
            this._aimLineLayer.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) {
                this._aimLineLayer.lineTo(points[i].x, points[i].y);
            }
            this._aimLineLayer.stroke({
                color: aimColor,
                width: lineWidth,
                alpha: 1,
                cap: 'round',
                join: 'round',
            });

            // ── Dots: place exactly `maxAhead` dots at equal arc
            //   intervals starting `headOffset` ahead of the train.
            //   Walks each dot's target arc distance forward through
            //   the segment list and lerps inside whichever segment
            //   it falls in. Falls off the end when the path runs
            //   out (e.g. train is 3 cells from goal → 3 dots).
            for (let d = 0; d < maxAhead; d++) {
                const arcPos = headOffset + d * dotSpacing;
                if (arcPos > totalLen) break;

                let remaining = arcPos;
                let segIdx = 0;
                while (segIdx < segLens.length && remaining > segLens[segIdx]) {
                    remaining -= segLens[segIdx];
                    segIdx++;
                }
                if (segIdx >= segLens.length) break;

                const segLen = segLens[segIdx];
                const t = segLen > 0 ? remaining / segLen : 0;
                const p0 = points[segIdx];
                const p1 = points[segIdx + 1];
                const x = p0.x + (p1.x - p0.x) * t;
                const y = p0.y + (p1.y - p0.y) * t;

                this._aimLineLayer.circle(x, y, dotRadius);
                this._aimLineLayer.fill({ color: aimColor, alpha: 1 });
                this._aimLineLayer.circle(x, y, dotRadius);
                this._aimLineLayer.stroke({
                    color: 0xffffff,
                    width: ringWidth,
                    alpha: 1,
                });
            }
        }
    }

    _drawGoalEntity(gfx, entity) {
        const cs = this.cellSize;
        const trainId = entity.props?.trainId || 't1';
        const colorName = TRAIN_ID_TO_COLOR_NAME[trainId] || 'red';
        const colorHex = COLOR_NAME_TO_HEX[entity.props?.color] || COLOR_NAME_TO_HEX[colorName] || 0xffffff;

        // Goal cell carries ONLY a small coloured pin marker —
        // the FROM → TO route card lives on the SPAWN side
        // (`_drawSpawnEntity`) so each train contributes ONE card
        // to the board, never two. Keeping a tiny pin here lets
        // the designer still see which tile the goal is pinned
        // to and matches the train's colour for visual pairing.
        const pin = new Graphics();
        const pr = cs * 0.28;
        pin.circle(0, 0, pr);
        pin.fill({ color: 0xffffff, alpha: 0.90 });
        pin.circle(0, 0, pr);
        pin.stroke({ color: colorHex, width: Math.max(2, cs * 0.10) });
        pin.circle(0, 0, pr * 0.45);
        pin.fill({ color: colorHex, alpha: 0.95 });
        gfx.addChild(pin);

        if (this._editorMode) {
            // The route's FROM/TO card normally lives on the SPAWN
            // end. When a goal is dropped onto another train's
            // STATION BUILDING (the common pattern — e.g. "train
            // T3 from SZOMBATHELY ends at MISKOLC-TISZAI" puts
            // its goal pin on the MISKOLC-TISZAI building, which
            // is ALREADY the spawn for T5), the spawn at that
            // cell is already painting a card. Drawing the goal's
            // card too produced two stacked cards on the same
            // roof — the "strange duplication" the designer saw.
            //
            // So in the editor we only draw a card on a goal when
            // the goal is NOT sitting inside any spawn's building
            // footprint. Stand-alone goals (placed on bare track,
            // away from any station) still get their own card so
            // the designer can identify them; co-located goals
            // ride on the spawn's card.
            let coveredBySpawn = false;
            for (const spawn of this._spawnByTrainId.values()) {
                if (spawn.props?.trainId === trainId) continue;
                if (this._spawnFootprintContains(spawn, entity.c, entity.r)) {
                    coveredBySpawn = true;
                    break;
                }
            }
            if (!coveredBySpawn) {
                const spawnSelf = this._spawnByTrainId.get(trainId);
                gfx.addChild(this._drawStationInfoCard(cs, colorHex, {
                    from:    this._spawnDisplayCity(spawnSelf),
                    to:      this._goalDisplayCity(entity),
                    centerY: -cs * 0.75,
                    trainId,
                    anchorEntity: entity,
                }));
            }
        }
    }

    /**
     * Resolve the city name SHOWN on a spawn's info card. Honours
     * an explicit override in `props.originCity` (future-proof; not
     * yet exposed in the editor schema), otherwise falls back to
     * the hardcoded TRAIN_ID_TO_ORIGIN_CITY map that matches the
     * Hungarian station PNGs.
     */
    _spawnDisplayCity(spawn) {
        if (!spawn) return '';
        const trainId = spawn.props?.trainId || 't1';
        return (
            spawn.props?.originCity
            || TRAIN_ID_TO_ORIGIN_CITY[trainId]
            || ''
        );
    }

    /**
     * Resolve the city name SHOWN on a goal's info card.
     *
     * Priority order:
     *   1. `props.destinationCity` — designer's explicit pick.
     *   2. Auto-detect: if the goal cell sits inside ANOTHER
     *      train's spawn-building footprint, return THAT spawn's
     *      origin city. ("Goal pin on the NYUGATI building → the
     *      train is heading TO NYUGATI.") This is the natural
     *      designer workflow: drop a spawn on every station and
     *      a goal on whichever station each train should arrive
     *      at, no inspector dropdown required.
     *   3. Empty string — the renderer shows "?", which prompts
     *      the designer to either move the pin onto a station or
     *      pick a destination from the dropdown.
     *
     * The fallback to the train's OWN origin city was removed —
     * it produced confusing "FROM KELETI TO KELETI" cards when
     * `destinationCity` wasn't set.
     */
    _goalDisplayCity(goal) {
        if (!goal) return '';
        if (goal.props?.destinationCity) return goal.props.destinationCity;

        const goalTrainId = goal.props?.trainId || 't1';
        for (const spawn of this._spawnByTrainId.values()) {
            const spawnTrainId = spawn.props?.trainId || 't1';
            if (spawnTrainId === goalTrainId) continue; // skip self — that's the FROM end
            if (this._spawnFootprintContains(spawn, goal.c, goal.r)) {
                return this._spawnDisplayCity(spawn);
            }
        }
        return '';
    }

    /**
     * True iff the cell (c, r) is inside the building footprint of
     * `spawn`. Spawn sprites are centered on the spawn cell (anchor
     * 0.5), so the footprint extends ±fw/2 columns and ±fh/2 rows
     * around the anchor — matching how `_drawSpawnEntity` sizes
     * the sprite at `fw * cs × fh * cs`.
     */
    _spawnFootprintContains(spawn, c, r) {
        if (!spawn) return false;
        const slot = `trainsim.spawn.${spawn.props?.trainId || 't1'}`;
        const el = this._themeManager?.getElement?.(slot);
        const fw = Math.max(1, el?.footprint?.w || 1);
        const fh = Math.max(1, el?.footprint?.h || 1);
        const halfW = fw / 2;
        const halfH = fh / 2;
        // Inclusive bounds so a goal placed on the very edge of the
        // building footprint still counts as "inside it".
        return (
            c >= spawn.c - halfW && c <= spawn.c + halfW
            && r >= spawn.r - halfH && r <= spawn.r + halfH
        );
    }

    /**
     * Side-by-side "timetable" info card painted above station
     * artwork on spawns and goals. Replaces the old vertical
     * "title-over-subtitle" layout with two distinct sub-cards
     * laid out LEFT → RIGHT:
     *
     *   ┌─────────────┐   →   ┌──────────┐
     *   │ FROM:       │   →   │ TO:      │
     *   │ BP-KELETI   │   →   │ SZOLNOK  │
     *   └─────────────┘       └──────────┘
     *
     * Both spawn and goal cells show the SAME card so the player
     * can read the whole route from either endpoint. `from` is
     * the origin city (drawn on the LEFT), `to` is the destination
     * (drawn on the RIGHT). `centerY` is the vertical centre of
     * the whole composite (negative = above the cell). Auto-sizes
     * to fit the longest city name.
     */
    _drawStationInfoCard(cs, colorHex, { from = '', to = '', centerY = 0, trainId = null, anchorEntity = null, mode = 'route' } = {}) {
        const wrap = new Container();

        const labelFontSize = Math.max(8,  Math.round(cs * 0.22));
        const cityFontSize  = Math.max(11, Math.round(cs * 0.30));

        // Build each mini-card (one for the origin, one for the
        // destination) so they can share padding / sizing math.
        const buildMini = (header, city, headerColor) => {
            const c = new Container();

            const headerText = new Text({
                text: header,
                style: new TextStyle({
                    fontFamily: 'Arial Black, Arial, sans-serif',
                    fontSize: labelFontSize,
                    fontWeight: '900',
                    fill: headerColor,
                    stroke: { color: 0x0b1220, width: Math.max(2, cs * 0.05) },
                    letterSpacing: 1.2,
                }),
            });
            headerText.anchor.set(0.5);

            const cityText = new Text({
                text: city || '?',
                style: new TextStyle({
                    fontFamily: 'Arial Black, Arial, sans-serif',
                    fontSize: cityFontSize,
                    fontWeight: '900',
                    fill: '#ffffff',
                    stroke: { color: 0x0b1220, width: Math.max(2, cs * 0.06) },
                    letterSpacing: 1.0,
                    align: 'center',
                }),
            });
            cityText.anchor.set(0.5);

            const padX = Math.max(6, cs * 0.22);
            const padY = Math.max(3, cs * 0.10);
            const gap  = Math.max(1, cs * 0.04);
            const miniW = Math.max(headerText.width, cityText.width) + padX * 2;
            const miniH = headerText.height + cityText.height + gap + padY * 2;
            const radius = Math.max(5, cs * 0.16);

            const card = new Graphics();
            card.roundRect(-miniW / 2, -miniH / 2, miniW, miniH, radius);
            card.fill({ color: 0x0b1220, alpha: 0.94 });
            card.roundRect(-miniW / 2, -miniH / 2, miniW, miniH, radius);
            card.stroke({ color: colorHex, width: Math.max(2, cs * 0.07) });
            card.roundRect(-miniW / 2 + 3, -miniH / 2 + 3, miniW - 6, miniH - 6, Math.max(3, radius - 3));
            card.stroke({ color: 0xffffff, width: 1, alpha: 0.18 });
            c.addChild(card);

            headerText.y = -miniH / 2 + padY + headerText.height / 2;
            cityText.y   = headerText.y + headerText.height / 2 + gap + cityText.height / 2;
            c.addChild(headerText);
            c.addChild(cityText);

            // Cache width/height on the container itself so the
            // composite layout can position both minis without
            // recomputing.
            c.__miniW = miniW;
            c.__miniH = miniH;
            return c;
        };

        // Localised header labels — fall back to English caps if the
        // i18n bundle hasn't loaded yet so the cards always render
        // something legible.
        const fromHeader = (t('trainsim.col_from') || 'FROM').toUpperCase();
        const toHeader   = (t('trainsim.col_to')   || 'TO').toUpperCase();
        const fromMini = buildMini(fromHeader, from, '#86efac');
        // origin-only mode (used by the in-game station roof) skips
        // the destination mini + arrow entirely. The "WHERE" half of
        // the route now lives on the train itself, so painting it
        // here too would just duplicate information and re-clutter
        // the roofline. Editors still use the full FROM → TO layout.
        const isOriginOnly = mode === 'origin-only';
        const toMini = isOriginOnly ? null : buildMini(toHeader, to, '#fbbf24');

        if (isOriginOnly) {
            // Single-mini layout: centre fromMini at x=0; no arrow.
            fromMini.x = 0;
            wrap.addChild(fromMini);
        } else {
            // Composite layout: LEFT mini, arrow, RIGHT mini, all
            // baseline-aligned at y = 0.
            const arrowGap   = Math.max(8, cs * 0.34);
            const totalW     = fromMini.__miniW + arrowGap + toMini.__miniW;
            const arrowW     = arrowGap;
            const arrowH     = Math.max(6, cs * 0.32);
            const halfTotalW = totalW / 2;

            fromMini.x = -halfTotalW + fromMini.__miniW / 2;
            toMini.x   =  halfTotalW - toMini.__miniW / 2;
            const arrowCenterX = (fromMini.x + fromMini.__miniW / 2 + toMini.x - toMini.__miniW / 2) / 2;

            // Arrow — chunky chevron in the train's colour so it ties
            // the two minis visually into one route.
            const arrow = new Graphics();
            const arrowLeftX  = arrowCenterX - arrowW / 2 + 2;
            const arrowRightX = arrowCenterX + arrowW / 2 - 2;
            const arrowMidY   = 0;
            // Shaft
            arrow.rect(arrowLeftX, arrowMidY - arrowH * 0.18, arrowRightX - arrowLeftX - arrowH * 0.45, arrowH * 0.36);
            arrow.fill({ color: colorHex, alpha: 0.95 });
            // Head
            arrow.moveTo(arrowRightX,                arrowMidY);
            arrow.lineTo(arrowRightX - arrowH * 0.7, arrowMidY - arrowH * 0.55);
            arrow.lineTo(arrowRightX - arrowH * 0.7, arrowMidY + arrowH * 0.55);
            arrow.closePath();
            arrow.fill({ color: colorHex, alpha: 0.95 });
            wrap.addChild(arrow);

            wrap.addChild(fromMini);
            wrap.addChild(toMini);
        }

        // Stash board-local bounding rects for each mini on the
        // anchor entity so `hitTestEntity` can route taps through
        // the existing grid hit-area handler. We can't make the
        // minis directly interactive — the board-wide invisible hit
        // rect (`getHitArea`) lives at zIndex 9999 above every
        // entity layer and would always win the Pixi event hit-test,
        // so child `eventMode = 'static'` does nothing here. Routing
        // through `hitTestEntity` keeps the click-dispatch logic in
        // one place AND respects the existing cell-vs-card priority.
        //
        // `anchorEntity` is the spawn / goal whose gfx hosts this
        // card; its (c, r) plus the card's local layout gives us
        // absolute bounds in the same coordinate space the hit
        // tester receives.
        if (anchorEntity && trainId) {
            // JSON-safe data lives on the entity itself (plain strings
            // and number rects survive JSON.stringify cleanly).
            anchorEntity._cardTrainId = trainId;
            const anchorX = anchorEntity.c * cs + cs / 2;
            const anchorY = anchorEntity.r * cs + cs / 2 + centerY;
            const rectFor = (mini) => ({
                x: anchorX + mini.x - mini.__miniW / 2,
                y: anchorY              - mini.__miniH / 2,
                w: mini.__miniW,
                h: mini.__miniH,
            });
            anchorEntity._fromCardRect = rectFor(fromMini);
            anchorEntity._toCardRect   = toMini ? rectFor(toMini) : null;
            if (!this._cardAnchors) this._cardAnchors = new Set();
            this._cardAnchors.add(anchorEntity);
            // Pixi Container refs are kept in a side map — see the
            // `_cardMinisById` field doc for why this MUST live off
            // the entity (renderGroup circular-ref kills JSON saves
            // otherwise).
            this._cardMinisById.set(anchorEntity.id, {
                from: fromMini,
                to:   toMini,
            });
        }

        // Small notch/peg under the wider mini so the composite
        // visually "plants" onto the building roof below.
        const tallerH = toMini ? Math.max(fromMini.__miniH, toMini.__miniH) : fromMini.__miniH;
        const peg = new Graphics();
        const pegW = Math.max(4, cs * 0.18);
        const pegH = Math.max(4, cs * 0.16);
        peg.moveTo(-pegW / 2, tallerH / 2);
        peg.lineTo( pegW / 2, tallerH / 2);
        peg.lineTo( 0,        tallerH / 2 + pegH);
        peg.closePath();
        peg.fill({ color: 0x0b1220, alpha: 0.94 });
        peg.stroke({ color: colorHex, width: Math.max(1, cs * 0.04) });
        wrap.addChild(peg);

        wrap.y = centerY;
        return wrap;
    }

    /**
     * Short, decoupled press-pulse on a HONNAN / HOVA mini badge.
     * Piggybacks on the same `_endpointYoyos` driver as the cell
     * bounce so a single per-frame routine animates both — the
     * mini gets a quicker, smaller pulse (~0.30 s, amplitude 0.18)
     * so the badge "presses in and snaps back" while the distant
     * cell does its bigger 0.65 s yoyo.
     */
    _pulseInfoMini(mini) {
        if (!mini) return;
        for (let i = this._endpointYoyos.length - 1; i >= 0; i--) {
            if (this._endpointYoyos[i].gfx === mini) {
                const old = this._endpointYoyos[i];
                if (old.gfx && !old.gfx.destroyed) {
                    old.gfx.scale.set(old.baseScale.x, old.baseScale.y);
                }
                this._endpointYoyos.splice(i, 1);
            }
        }
        this._endpointYoyos.push({
            gfx: mini,
            t: 0,
            duration: 0.30,
            baseScale: { x: mini.scale.x || 1, y: mini.scale.y || 1 },
            amplitude: 0.18,
        });
    }

    /**
     * Find the road cell a lamp's level-crossing barrier should live
     * on. Prefers the lamp's OWN cell (the classic level-crossing
     * case where track and road overlap) so the barrier sits right on
     * top of the rails it controls. Falls back to the first cardinal
     * neighbour that has a road tile, so designers who placed the
     * lamp on a pure-track cell next to a parallel road still get a
     * working barrier without re-editing the stage.
     *
     * Returns `{ c, r, dc, dr, axis }` where `dc/dr` are the offset
     * from the lamp's cell (used to position the barrier graphics)
     * and `axis` is 'horizontal' or 'vertical' — the orientation of
     * the road's travel direction at that cell, used to compute the
     * arm's "closed" base rotation.
     */
    /**
     * Walk every layer's entity list ONCE and pre-index spawns +
     * goals by their `trainId` so the per-entity painters can look
     * up the matching counterpart cheaply. Called right after the
     * tile pass in `init()`, BEFORE any entity is drawn — so the
     * spawn renderer for T1 can always find T1's goal (and vice
     * versa) regardless of the order they appear in the stage JSON.
     */
    _buildRouteIndex() {
        this._spawnByTrainId.clear();
        this._goalByTrainId.clear();
        for (const layer of this._layers) {
            if (!Array.isArray(layer.entities)) continue;
            for (const ent of layer.entities) {
                const trainId = ent?.props?.trainId || 't1';
                if (ent.type === 'spawn') this._spawnByTrainId.set(trainId, ent);
                else if (ent.type === 'goal') this._goalByTrainId.set(trainId, ent);
            }
        }
    }

    _findLampBarrierCell(lamp) {
        if (!lamp) return null;
        const has = (c, r) => this._roadTileGrid.has(`${c},${r}`);
        const axisAt = (c, r) => this._roadAxisAt(c, r);
        if (has(lamp.c, lamp.r)) {
            return { c: lamp.c, r: lamp.r, dc: 0, dr: 0, axis: axisAt(lamp.c, lamp.r) || 'horizontal' };
        }
        // Walk the 4 cardinal neighbours; first hit wins. Order
        // (N, E, S, W) is arbitrary but deterministic so a stage
        // re-loads always picks the same barrier cell.
        const offsets = [
            { dc:  0, dr: -1 },
            { dc:  1, dr:  0 },
            { dc:  0, dr:  1 },
            { dc: -1, dr:  0 },
        ];
        for (const o of offsets) {
            const nc = lamp.c + o.dc, nr = lamp.r + o.dr;
            if (has(nc, nr)) {
                return { c: nc, r: nr, dc: o.dc, dr: o.dr, axis: axisAt(nc, nr) || 'horizontal' };
            }
        }
        return null;
    }

    /**
     * Returns the dominant travel axis of the road tile at (c, r):
     *   'horizontal' — road runs east↔west (cars travel E or W)
     *   'vertical'   — road runs north↔south (cars travel N or S)
     *   null         — no road tile at that cell
     *
     * Used by `_findLampBarrierCell` to figure out which way the
     * barrier arm should fall when closed (always perpendicular to
     * the road so it actually blocks traffic).
     */
    _roadAxisAt(c, r) {
        const tile = this._roadTileGrid.get(`${c},${r}`);
        if (!tile) return null;
        const conns = tile._connections || tile.connections;
        if (!conns) return null;
        const hasEW = !!(conns[2] || conns[6]);
        const hasNS = !!(conns[0] || conns[4]);
        if (hasEW && !hasNS) return 'horizontal';
        if (hasNS && !hasEW) return 'vertical';
        if (hasEW) return 'horizontal';
        if (hasNS) return 'vertical';
        return null;
    }

    _drawLampEntity(gfx, entity) {
        const cs = this.cellSize;

        // Preserve the live animation phase + arm angle across redraws
        // so a repaint never snaps the barrier arm back to its rest
        // position mid-tween.
        const prevAnim = this._lampAnim?.get(entity.id);
        const carriedPhase = prevAnim?.phase || 0;

        // ── Resolve where the level-crossing barrier should sit ────
        // This entity is now a pure, car-only AUTOMATIC barrier
        // (sorompó). It no longer carries a trackside train signal —
        // trains are stopped by per-switch signals. We put the arm on
        // a ROAD cell (preferring the entity's own cell when it's a
        // level-crossing tile, falling back to a cardinal neighbour
        // with a road on it).
        const barrierCell = this._findLampBarrierCell(entity);
        // Compute the "closed" base rotation for the arm: it should
        // ALWAYS fall perpendicular to road traffic so it actually
        // blocks the lane. Default arm geometry points east (+x at
        // rotation 0). A horizontal (E-W) road wants the arm to fall
        // N-S, so closed = -π/2 (pointing north). A vertical (N-S)
        // road wants the arm horizontal across the lane, so
        // closed = 0 (pointing east).
        const closedRot = (barrierCell?.axis === 'horizontal')
            ? -Math.PI / 2
            : 0;
        // Open position: rotate the arm 75° away from the closed
        // direction so it visibly lifts off the road.
        const openRot = closedRot - Math.PI * 0.42;
        // The arm is driven by train proximity each frame in
        // `tickLamps`; on a fresh paint default to OPEN (raised) unless
        // a live closed flag says otherwise, and carry the in-flight
        // angle across redraws.
        const closedNow = !!entity._barrierClosed;
        const targetArmRot = closedNow ? closedRot : openRot;
        const carriedArmRot = (typeof prevAnim?.armRot === 'number')
            ? prevAnim.armRot
            : targetArmRot;

        // (No trackside marker. The barrier is a pure car-only gate —
        // the arm + alarm ring below are the only things it draws, so
        // it never clutters the rails. The old decorative crossbuck/X
        // was removed at the player's request.)

        // ── LEVEL-CROSSING BARRIER (lives on the ROAD cell) ────────
        // Built only when a road cell is reachable from this lamp —
        // a pure-track lamp with no adjacent road still works as a
        // train signal, it just doesn't have anything to gate cars
        // with.
        let armPivot = null;
        let stopLight = null;
        let barrierKey = null;
        if (barrierCell) {
            barrierKey = `${barrierCell.c},${barrierCell.r}`;
            // Register the reverse mapping so cars can look up
            // "what's the barrier on my next cell?" with a single
            // Map.get — cheap and works even when multiple lamps
            // gate the same crossing tile.
            this._barrierByRoadCell.set(barrierKey, entity);

            // Container parented to the lamp's gfx but offset to
            // the road cell. Doing it this way keeps everything
            // under the lamp's existing entity registry — no new
            // child of the layer container to manage at destroy.
            const barrierGroup = new Container();
            barrierGroup.x = barrierCell.dc * cs;
            barrierGroup.y = barrierCell.dr * cs;

            // Pivot post — short fat stub the arm rotates around,
            // placed on the EDGE of the road so the arm crosses
            // the lane rather than starting in the middle.
            // For a horizontal road, the pole sits on the south
            // shoulder (positive y). For a vertical road, on the
            // west shoulder (negative x).
            const onHorizontalRoad = barrierCell.axis === 'horizontal';
            const pivotPx = onHorizontalRoad ? 0                : -cs * 0.34;
            const pivotPy = onHorizontalRoad ? cs * 0.34         : 0;

            // Slide the WHOLE barrier off the rails and onto the road.
            // At a true level crossing (track + road share this cell)
            // the arm would otherwise sit right along the rails; nudging
            // it the road's travel axis seats it on the tarmac just
            // beside the tracks — where a real crossing barrier stands,
            // blocking cars on the road rather than straddling the track.
            // Only the shared-cell case needs it; a barrier already on an
            // adjacent road cell is clear of the rails.
            if (barrierCell.dc === 0 && barrierCell.dr === 0) {
                const roadShift = cs * 0.34;
                if (onHorizontalRoad) barrierGroup.x += roadShift;
                else                  barrierGroup.y += roadShift;
            }

            // Pivot post itself — a compact box only. NO vertical mast
            // poking out of it any more: that thin stub read as a "line
            // connecting" the barrier to the track/crossroad.
            const pivotPost = new Graphics();
            pivotPost.rect(pivotPx - cs * 0.07, pivotPy - cs * 0.07,
                           cs * 0.14, cs * 0.14)
                     .fill({ color: 0x1f2937 })
                     .stroke({ color: 0x0b1220, width: Math.max(1.5, cs * 0.05) });
            barrierGroup.addChild(pivotPost);

            // Stop-light lantern on the post (visible only when the
            // barrier is CLOSED). Animated by `tickLamps` — flashes
            // red while the arm is down so drivers see "DANGER" the
            // same way they do at every real crossing.
            stopLight = new Graphics();
            const sLx = pivotPx + (onHorizontalRoad ? cs * 0.17 : 0);
            const sLy = pivotPy + (onHorizontalRoad ? 0 : cs * 0.17);
            const sLr = cs * 0.10;
            stopLight.circle(sLx, sLy, sLr + cs * 0.025)
                     .fill({ color: 0x0b1220 });
            stopLight.circle(sLx, sLy, sLr)
                     .fill({ color: 0xdc2626 })
                     .stroke({ color: 0xffffff, width: Math.max(1, cs * 0.03), alpha: 0.9 });
            barrierGroup.addChild(stopLight);
            stopLight.alpha = 0;

            // Rotating arm container — geometry baked at rotation 0
            // pointing along +x; `tickLamps` mutates `.rotation` to
            // animate between `closedRot` and `openRot`.
            armPivot = new Container();
            armPivot.x = pivotPx;
            armPivot.y = pivotPy;
            armPivot.rotation = carriedArmRot;

            const armLen = cs * 0.95;
            const armW   = Math.max(5, cs * 0.16);
            const stripeW = armLen / 6;
            const arm = new Graphics();
            arm.rect(2, -armW * 0.5 + 2, armLen, armW)
               .fill({ color: 0x000000, alpha: 0.55 });
            for (let i = 0; i < 6; i++) {
                const col = (i % 2 === 0) ? 0xffffff : 0xdc2626;
                arm.rect(i * stripeW, -armW * 0.5, stripeW, armW)
                   .fill(col);
            }
            arm.rect(0, -armW * 0.5, armLen, armW)
               .stroke({ color: 0x0b1220, width: Math.max(1.5, cs * 0.05), alignment: 0 });
            arm.circle(armLen, 0, armW * 0.55)
               .fill({ color: 0xdc2626 })
               .stroke({ color: 0x0b1220, width: 2 });
            armPivot.addChild(arm);

            // Counter-weight on the back side of the pivot — small
            // dark ball mimicking real barrier mechanics.
            const counter = new Graphics();
            counter.circle(-armW * 1.3, 0, armW * 0.65)
                   .fill({ color: 0x1f2937 })
                   .stroke({ color: 0x0b1220, width: 2 });
            armPivot.addChild(counter);

            barrierGroup.addChild(armPivot);
            gfx.addChild(barrierGroup);
        }

        // (No more concentric "alarm ring" glyph over the crossing —
        // it read as a big circle covering the junction/road. The
        // level-crossing bell still rings via audio and the red
        // stop-light + dropped arm carry the "DANGER" read on their
        // own.) `tickLamps` skips the ring whenever this ref is null.
        const ring = null;

        // Stash the moving pieces so `tickLamps` can animate them every
        // frame without ever touching the static Graphics geometry
        // again. No more bulb/core/halo — this is a barrier, not a
        // signal, so those refs are null and the tick guards skip them.
        if (!this._lampAnim) this._lampAnim = new Map();
        this._lampAnim.set(entity.id, {
            halo: null,
            bulb: null,
            core: null,
            ring,
            arm: armPivot,
            stopLight,
            armRot: carriedArmRot,
            armTargetRot: targetArmRot,
            closedRot,
            openRot,
            closed: closedNow,
            closeHold: prevAnim?.closeHold || 0,
            phase: carriedPhase,
            barrierKey,
        });
    }

    _drawSwitchEntity(gfx, entity) {
        const cs = this.cellSize;
        const half = cs / 2;

        // Resolve which routing is live and which is "coming next"
        // when the player taps. Both fall back gracefully so old
        // 2-state stages keep working.
        const routes = Array.isArray(entity._routes) ? entity._routes : [];
        const stateCount = routes.length || 0;
        const activeIdx = stateCount > 0
            ? TrainSimBoard._normalizeSwitchIndex(entity.active, stateCount)
            : 0;
        const nextIdx = stateCount > 1 ? (activeIdx + 1) % stateCount : activeIdx;
        const activeRoute = stateCount > 0
            ? routes[activeIdx]
            : (entity._routeA || null);
        const nextRoute = stateCount > 1 ? routes[nextIdx] : null;

        // Every active pair paints in the SAME hot magenta so the
        // player has a single, unambiguous "active direction" colour
        // to read. Rotation states stay visually distinct via
        // GEOMETRY rather than colour:
        //   • opposite ports → straight bar through centre
        //   • perpendicular cardinals → quarter-arc around the
        //     shared corner (NE / SE / SW / NW scoops)
        // so e.g. a 4-port cross's three states (NS+EW, NE+SW,
        // NW+SE) all read as obviously different rotations even
        // though they share one colour.
        const ACTIVE_COLOR = TRAINSIM_CONFIG.COLOR_SWITCH_ACTIVE;
        const colorForPairIdx = () => ACTIVE_COLOR;
        const inactiveColor = TRAINSIM_CONFIG.COLOR_SWITCH_A;      // amber ghost

        // Preserve any in-flight click-flash timer across redraws so
        // a toggle-triggered flash doesn't get stomped by the redraw
        // it caused. Everything else (Graphics nodes) gets rebuilt
        // below and re-registered in the side map.
        const prevAnim = this._switchAnim.get(entity.id);
        const carriedFlashTime = prevAnim?.flashTime || 0;
        // The signal lamp lives in the SEPARATE `_signalOverlayLayer`
        // (above the trains), so `_redrawEntity`'s `gfx.removeChildren()`
        // can't reach it. Tear the previous lamp group down by hand here
        // before we build a fresh one, or redraws would stack duplicate
        // lenses in the overlay.
        if (prevAnim?.sigGroup && !prevAnim.sigGroup.destroyed) {
            prevAnim.sigGroup.destroy({ children: true });
        }

        // ── Underlay: the FULL junction faded WAY down so the
        //   active route below pops as the dominant visual.
        //   You can still see "this is a 4-way junction" but the
        //   eye doesn't fight to figure out which two ports are
        //   wired up.
        const full = entity._underlyingConn
            || entity._stateA
            || [];
        const underlay = new Graphics();
        underlay.x = -cs / 2;
        underlay.y = -cs / 2;
        drawTrainsimTrack(underlay, cs, full);
        underlay.alpha = 0.28;
        gfx.addChild(underlay);

        // Walk every (entry → exit) mapping in the active route,
        // but only draw EACH unordered pair once — `{N→S, S→N}` is
        // the same physical line, so the highlight shouldn't paint
        // it twice (the second pass would just thicken the line and
        // double the arrowhead). Same dedup rule applies to ghost +
        // arrow passes below so the next-state preview reads cleanly
        // even on a 4-port cross with two simultaneous through-pairs.
        const collectPairs = (route) => {
            if (!route || route.size === 0) return [];
            const seen = new Set();
            const pairs = [];
            for (const [from, to] of route.entries()) {
                if (from < 0 || to < 0) continue;
                // ONLY mutual pairs count as "through-lines" — the
                // route Map encodes the main pair bidirectionally
                // (a→b AND b→a) but stores extras-redirects as
                // one-way mappings (e→endpoint with no e on the
                // far side). Without this guard, a T-junction's
                // three states all collapse to the same primary
                // pair because the redirect entry (e.g. S→E)
                // creates a phantom {2,4} pair that wins the
                // "smallest min" sort every state — so the magenta
                // bar never moves when the player taps.
                if (route.get(to) !== from) continue;
                const key = from < to ? `${from}-${to}` : `${to}-${from}`;
                if (seen.has(key)) continue;
                seen.add(key);
                pairs.push({ from, to });
            }
            // Sort by lexicographic (min, max) so the "primary"
            // choice is STABLE across redraws — without this,
            // the Map's iteration order could shuffle the pairs
            // and the magenta bar would jitter to a different
            // through-line every time the player toggled.
            pairs.sort((p, q) => {
                const pMin = Math.min(p.from, p.to);
                const qMin = Math.min(q.from, q.to);
                if (pMin !== qMin) return pMin - qMin;
                return Math.max(p.from, p.to) - Math.max(q.from, q.to);
            });
            return pairs;
        };
        const allActivePairs = collectPairs(activeRoute);
        const allNextPairs   = collectPairs(nextRoute);
        // Always highlight exactly ONE pair — the primary, which is
        // the lexicographically smallest (sort happens above). With
        // a single bright bar on screen, every tap of the switch
        // makes the magenta line visibly rotate to a new geometric
        // position:
        //
        //   • 4-port cross: NE arc → N-S straight → NW arc, all
        //     anchored on the N port so rotation reads at a glance.
        //   • T / Y / 2-port junctions: bar swings around the hub
        //     because the primary pair changes with each state.
        //
        // The faded underlay below still shows the full junction
        // outline so the player knows every port exists; the
        // primary pair is the "active direction" they're picking.
        const activePairs = allActivePairs.slice(0, 1);
        const nextPairs   = allNextPairs.slice(0, 1);

        // ── Ghost spokes: the NEXT state's routing, drawn dashed
        //   in the alternate colour. Tells the player "tap and the
        //   trains will go here instead". Suppressed when the
        //   switch has only one state (degenerate) or when the
        //   next-state geometry coincides exactly with the active
        //   state (no actual divergence to preview).
        // ── PAIR PATH TRACER ─────────────────────────────────
        // The geometry of each (from → to) pair on the switch
        // visualisation depends on the angular relationship of
        // its two ports:
        //
        //   • OPPOSITE ports (arc == 4: N-S, E-W, NE-SW, NW-SE)
        //     → straight line through the cell centre.
        //
        //   • PERPENDICULAR CARDINALS (arc == 2 and both cardinal
        //     ports: N-E, E-S, S-W, W-N) → quarter-arc bending
        //     around the SHARED CORNER of the cell. This is the
        //     critical bit for cross switches: when matching {N-E,
        //     S-W} is active, drawing two LINE segments meeting
        //     at the centre makes the result look indistinguishable
        //     from matching {N-S, E-W} — both end up as a fat "+".
        //     The arc-on-corner rendering gives matching 1 two
        //     opposite SCOOPED curves and matching 3 the mirrored
        //     scoops, so all three states read as visually
        //     distinct rotations of the same junction.
        //
        //   • Everything else (diagonals, mixed cardinal/diagonal)
        //     falls back to the legacy bent line through centre.
        //
        // Both the SOLID active layers AND the dashed ghost preview
        // use this same path so they line up exactly on top / below.
        const ARC_SEGS = 16;
        const buildPairPolyline = (from, to, lenScale = 1.0) => {
            const f = DIR_DELTA[from];
            const t = DIR_DELTA[to];
            if (!f || !t) return null;
            const angularArc = Math.min(Math.abs(from - to), 8 - Math.abs(from - to));
            const isCardF = (from % 2 === 0);
            const isCardT = (to % 2 === 0);
            const px = f.dc * half * lenScale;
            const py = f.dr * half * lenScale;
            const qx = t.dc * half * lenScale;
            const qy = t.dr * half * lenScale;

            // Opposite — single straight segment.
            if (angularArc === 4) return [[px, py], [qx, qy]];

            // Perpendicular cardinals — quarter-arc around the
            // shared corner. Corner position = sum of unit dir
            // vectors scaled by `half` (so N+E lands at (half, -half)
            // in local cell-centred coords).
            if (angularArc === 2 && isCardF && isCardT) {
                const cx = (f.dc + t.dc) * half;
                const cy = (f.dr + t.dr) * half;
                const radius = half * lenScale;
                const startAng = Math.atan2(py - cy, px - cx);
                const endAng   = Math.atan2(qy - cy, qx - cx);
                let delta = endAng - startAng;
                // Normalise to (-π, π] so we always sweep the SHORT
                // way around the corner (90°), never the long 270°.
                while (delta >  Math.PI) delta -= 2 * Math.PI;
                while (delta <= -Math.PI) delta += 2 * Math.PI;
                const pts = [[px, py]];
                for (let i = 1; i <= ARC_SEGS; i++) {
                    const a = startAng + delta * (i / ARC_SEGS);
                    pts.push([cx + Math.cos(a) * radius, cy + Math.sin(a) * radius]);
                }
                return pts;
            }

            // Fallback — bent line through centre (legacy behaviour
            // for diagonal or mixed-axis pairs).
            return [[px, py], [0, 0], [qx, qy]];
        };
        const tracePairPath = (g, from, to, lenScale = 1.0) => {
            const pts = buildPairPolyline(from, to, lenScale);
            if (!pts || pts.length < 2) return;
            g.moveTo(pts[0][0], pts[0][1]);
            for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
        };

        const pairKey = (p) => p.from < p.to ? `${p.from}-${p.to}` : `${p.to}-${p.from}`;
        const activePairSet = new Set(activePairs.map(pairKey));
        const ghostPairs = nextPairs.filter((p) => !activePairSet.has(pairKey(p)));
        if (ghostPairs.length > 0) {
            const ghost = new Graphics();
            const dashLen = Math.max(3, cs * 0.12);
            const gapLen  = Math.max(2, cs * 0.09);
            // Walk an arbitrary polyline as a dashed stroke. Used
            // by the ghost preview so arc-shaped pair previews dash
            // smoothly around their corner without breaking into
            // visible "facets" at the polyline joints.
            const dashPolyline = (pts) => {
                if (!pts || pts.length < 2) return;
                let drawing = true;
                let remaining = dashLen;
                for (let i = 0; i < pts.length - 1; i++) {
                    let x1 = pts[i][0], y1 = pts[i][1];
                    const x2 = pts[i + 1][0], y2 = pts[i + 1][1];
                    let segLen = Math.hypot(x2 - x1, y2 - y1);
                    if (segLen < 0.001) continue;
                    let ux = (x2 - x1) / segLen;
                    let uy = (y2 - y1) / segLen;
                    let pos = 0;
                    while (pos < segLen) {
                        const step = Math.min(remaining, segLen - pos);
                        if (drawing) {
                            ghost.moveTo(x1 + ux * pos, y1 + uy * pos);
                            ghost.lineTo(x1 + ux * (pos + step), y1 + uy * (pos + step));
                        }
                        pos += step;
                        remaining -= step;
                        if (remaining <= 0) {
                            drawing = !drawing;
                            remaining = drawing ? dashLen : gapLen;
                        }
                    }
                }
            };
            for (const { from, to } of ghostPairs) {
                dashPolyline(buildPairPolyline(from, to, 1.0));
            }
            ghost.stroke({
                color: inactiveColor,
                width: Math.max(2, cs * 0.13),
                alpha: 0.55,
                cap: 'round',
                join: 'round',
            });
            gfx.addChild(ghost);
        }

        // ── Active highlight: a CHUNKY bright bar through every
        //   connected port pair so the player can see at a glance
        //   which lines are wired up right now. Several layers
        //   stacked for maximum legibility — wide dark halo, then
        //   the coloured bar, then a white pinstripe, finishing
        //   with big endpoint discs + outward-pointing arrowheads
        //   at every active port so "the train will exit HERE"
        //   reads from any zoom level.
        if (activePairs.length > 0) {
            // (a) Dark halo BEHIND every coloured bar — a single
            //     wide stroke for ALL pairs, drawn once so the
            //     drop-shadow reads as a continuous outline rather
            //     than two stacked halos with a seam.
            const haloLine = new Graphics();
            for (const { from, to } of activePairs) {
                tracePairPath(haloLine, from, to, 1.0);
            }
            haloLine.stroke({
                color: 0x0b1220,
                width: Math.max(6, cs * 0.52),
                alpha: 0.55,
                cap: 'round',
                join: 'round',
            });
            gfx.addChild(haloLine);

            // (b–e) Per-pair layers — bar + pinstripe + discs +
            //       arrows. Every active pair shares the SAME
            //       magenta so the switch reads as one clean
            //       "this is the active direction" colour. The
            //       three rotation states of a 4-port cross stay
            //       distinguishable because their geometries
            //       differ (straight bar vs corner-scooping arc),
            //       not because of a colour split.
            const discR  = Math.max(4, cs * 0.16);
            const aLen   = cs * 0.28;
            const aHalfW = cs * 0.20;

            activePairs.forEach((pair, idx) => {
                const col = colorForPairIdx(idx);

                // (b) The active bar for this pair.
                const highlight = new Graphics();
                tracePairPath(highlight, pair.from, pair.to, 1.0);
                highlight.stroke({
                    color: col,
                    width: Math.max(5, cs * 0.42),
                    alpha: 1.0,
                    cap: 'round',
                    join: 'round',
                });
                gfx.addChild(highlight);

                // (c) White pinstripe down the centre of this pair,
                //     shortened slightly so the cap stays INSIDE
                //     the coloured bar.
                const pinstripe = new Graphics();
                tracePairPath(pinstripe, pair.from, pair.to, 0.92);
                pinstripe.stroke({
                    color: 0xffffff,
                    width: Math.max(1.5, cs * 0.10),
                    alpha: 0.65,
                    cap: 'round',
                    join: 'round',
                });
                gfx.addChild(pinstripe);

                // (d + e) Endpoint disc + arrowhead at each of the
                //         pair's two ports, both painted with this
                //         pair's colour.
                for (const dir of [pair.from, pair.to]) {
                    const d = DIR_DELTA[dir];
                    if (!d) continue;

                    // Disc just inside the cell edge.
                    const ex = d.dc * (half - discR * 0.35);
                    const ey = d.dr * (half - discR * 0.35);
                    const disc = new Graphics();
                    disc.circle(ex, ey, discR);
                    disc.fill({ color: col, alpha: 1 });
                    disc.circle(ex, ey, discR);
                    disc.stroke({ color: 0xffffff, width: Math.max(1.5, cs * 0.06), alpha: 0.95 });
                    disc.circle(ex, ey, discR * 0.45);
                    disc.fill({ color: 0xffffff, alpha: 0.85 });
                    gfx.addChild(disc);

                    // Outward-pointing arrowhead riding on the disc.
                    const tipX = d.dc * (half + cs * 0.04);
                    const tipY = d.dr * (half + cs * 0.04);
                    const baseCX = tipX - d.dc * aLen;
                    const baseCY = tipY - d.dr * aLen;
                    const px = -d.dr;
                    const py =  d.dc;
                    const w1x = baseCX + px * aHalfW;
                    const w1y = baseCY + py * aHalfW;
                    const w2x = baseCX - px * aHalfW;
                    const w2y = baseCY - py * aHalfW;
                    const arrowG = new Graphics();
                    arrowG.moveTo(tipX, tipY);
                    arrowG.lineTo(w1x, w1y);
                    arrowG.lineTo(w2x, w2y);
                    arrowG.closePath();
                    arrowG.fill({ color: col, alpha: 1 });
                    arrowG.moveTo(tipX, tipY);
                    arrowG.lineTo(w1x, w1y);
                    arrowG.lineTo(w2x, w2y);
                    arrowG.closePath();
                    arrowG.stroke({ color: 0xffffff, width: Math.max(1.5, cs * 0.05), alpha: 0.95 });
                    gfx.addChild(arrowG);
                }
            });
        }

        // ── Small central hub: a compact gem-shaped marker that
        //   sits exactly on the junction's crossing point. Far
        //   smaller than the old full-cell circle so the track
        //   underneath (and any decoration sprites in adjacent
        //   cells) stays readable. The active-route lines drawn
        //   above ALREADY communicate the routing direction —
        //   the hub just marks "this junction is interactive".
        const hubR = cs * 0.16;
        // Hub matches the bars so the whole switch reads in one
        // colour, regardless of how many pairs are active.
        const hubColor = ACTIVE_COLOR;
        const hub = new Graphics();
        // Diamond / rotated-square silhouette reads as "switch
        // lever" without needing a big ring around it.
        hub.poly([0, -hubR, hubR, 0, 0, hubR, -hubR, 0]);
        hub.fill({ color: hubColor, alpha: 1 });
        hub.poly([0, -hubR, hubR, 0, 0, hubR, -hubR, 0]);
        hub.stroke({ color: 0x0b1220, width: Math.max(1.4, cs * 0.05) });
        // Thin white inner highlight gives the gem a soft 3-D
        // feel without inflating its footprint.
        hub.poly([0, -hubR * 0.7, hubR * 0.7, 0, 0, hubR * 0.7, -hubR * 0.7, 0]);
        hub.stroke({ color: 0xffffff, width: Math.max(0.8, cs * 0.025), alpha: 0.7 });
        gfx.addChild(hub);

        // (No pulsing halo ring on the switch any more — it read as a
        // "circle covering the switcher". The hot magenta route bars +
        // arrows + the gem hub already make the junction obviously
        // interactive, and the click-flash below gives tap feedback.)
        const halo = null;

        // Per the latest UI pass we no longer paint a "1/3" chip
        // under each switch — the active coloured line + arrows
        // already encode the current state, and the chip was just
        // adding clutter on dense boards. The cycle index lives on
        // the entity (`active`) so the schema inspector can still
        // edit it in the editor.

        // ── Click-flash overlay: hot white ring scaled to the
        //   hub so it pops without flooding the whole cell.
        const flash = new Graphics();
        flash.circle(0, 0, hubR * 1.85);
        flash.stroke({ color: 0xffffff, width: Math.max(2, cs * 0.08), alpha: 1 });
        flash.alpha = 0;
        gfx.addChild(flash);

        // The pulsing alpha animation in `tickSwitches` used to
        // poke a small chevron icon. We removed the chevron with
        // the rest of the heavy circle furniture — the halo pulse
        // alone is plenty of "this is interactive" cue. Passing
        // `tap: null` below makes the tick a no-op for that ref.
        const tap = null;

        // ── Per-switch SIGNAL LAMP (the train stop/go control) ──────
        // A simple round lens — WHITE rim (neutral frame), GREEN core =
        // go, RED core = stop. Its direction is chosen SMARTLY from the
        // junction's open ports: it sits in a free quadrant/edge (an open
        // diagonal for a 4-port cross, the free arm of a T, the open
        // corner of an L) FULLY off the switch cell, so it never covers
        // the rails and its tap zone is physically separate from the
        // in-cell routing cycle. `tickSwitches` strobes the red aspect.
        const sigGreen = entity.signal === 'green';
        const sigColor = sigGreen
            ? TRAINSIM_CONFIG.COLOR_LAMP_GREEN
            : TRAINSIM_CONFIG.COLOR_LAMP_RED;
        // Big, standalone lens — NO mast, NO connector, and it sits
        // COMPLETELY off the switch cell (in the adjacent free cell)
        // with a clear gap, so it never overlaps the rails or the
        // routing bar. Large enough to see + tap at a glance.
        const sigR = cs * 0.26;
        // Placement avoids any neighbour cell that carries a rail/switch,
        // so the lens floats over EMPTY ground — nothing can sit between
        // it and the junction and read as a "connecting line".
        const place = chooseSignalPlacement(
            entity._underlyingConn, entity.c, entity.r, this.cols, this.rows,
            (cc, rr) => this._trackTileGrid.has(`${cc},${rr}`)
                     || this._switchByCell.has(`${cc},${rr}`),
        );
        // Centre the lens fully PAST the cell edge/corner along the
        // chosen free step: the whole body (white frame included) clears
        // the cell boundary, so nothing of the switch can read as
        // "connected" to it.
        const sigX = place.dc * (half + sigR * 1.55);
        const sigY = place.dr * (half + sigR * 1.55);

        // The lamp lens is parented to the high-z `_signalOverlayLayer`
        // (board-absolute coords) instead of the switch gfx, so it floats
        // ABOVE the trains — a train body / destination chip can no
        // longer cover the stop/go signal at a junction. We keep using
        // the same cell-centred `sigX/sigY` locals by positioning the
        // group at the switch cell's centre in board space. Falls back to
        // the entity gfx if the overlay isn't available (degenerate init).
        const cellX = entity.c * cs;
        const cellY = entity.r * cs;
        const sigGroup = new Container();
        sigGroup.x = cellX + half;
        sigGroup.y = cellY + half;
        if (this._signalOverlayLayer && !this._signalOverlayLayer.destroyed) {
            this._signalOverlayLayer.addChild(sigGroup);
        } else {
            gfx.addChild(sigGroup);
        }

        // TIGHT coloured glow hugging the lens only — radius barely past
        // the white frame so it can NEVER reach back toward the switch
        // cell (a wide glow used to bridge the gap and read as a faint
        // connector / "circle over the switch").
        const signalGlow = new Graphics();
        signalGlow.circle(sigX, sigY, sigR * 1.45);
        signalGlow.fill({ color: sigColor, alpha: 0.32 });
        sigGroup.addChild(signalGlow);

        // White neutral frame (the constant "shape") with a thin dark
        // outline so it pops on any background.
        const sigHousing = new Graphics();
        sigHousing.circle(sigX, sigY, sigR * 1.28).fill({ color: 0xffffff });
        sigHousing.circle(sigX, sigY, sigR * 1.28)
                  .stroke({ color: 0x0b1220, width: Math.max(1.5, cs * 0.06) });
        sigGroup.addChild(sigHousing);

        // Coloured body — green = go, red = stop.
        const signalBulb = new Graphics();
        signalBulb.circle(sigX, sigY, sigR).fill({ color: sigColor });
        // Glossy highlight arc so it reads as a lit lens.
        signalBulb.arc(sigX - sigR * 0.28, sigY - sigR * 0.28, sigR * 0.52, Math.PI, Math.PI * 1.6)
                  .stroke({ color: 0xffffff, width: Math.max(1.4, cs * 0.045), alpha: 0.8 });
        sigGroup.addChild(signalBulb);

        // White hot core — visible (and strobed by `tickSwitches`) only
        // on the red aspect; hidden on green.
        const signalCore = new Graphics();
        signalCore.circle(sigX, sigY, sigR * 0.42).fill({ color: 0xffffff, alpha: 1 });
        signalCore.alpha = sigGreen ? 0 : 1;
        sigGroup.addChild(signalCore);

        // Board-absolute hit-rect. The lens sits OUTSIDE the cell, so
        // `hitTestEntity` scans these rects up front (see its switch-
        // signal pass) — a tap here always routes to `toggleSwitchSignal`
        // and can never be mistaken for the in-cell routing cycle.
        const hitHalf = sigR * 1.28;
        entity._signalRect = {
            x: cellX + half + sigX - hitHalf,
            y: cellY + half + sigY - hitHalf,
            w: hitHalf * 2,
            h: hitHalf * 2,
        };

        // Register all animated refs in the side map. If a previous
        // flash was still in flight when this redraw happened, carry
        // its remaining time over to the new flash node so the user-
        // visible animation keeps running smoothly through the
        // redraw (otherwise the toggle visual would cut out).
        if (carriedFlashTime > 0) {
            flash.alpha = 0.85 * Math.min(1, carriedFlashTime / 0.35);
        }
        this._switchAnim.set(entity.id, {
            halo,
            flash,
            tap,
            flashTime: carriedFlashTime,
            sigGroup,
            signalGlow,
            signalBulb,
            signalCore,
            sigX,
            sigY,
            sigR,
            signal: entity.signal,
        });
    }

    _drawStationEntity(gfx, entity) {
        const cs = this.cellSize;
        const sprite = this._themeManager?.createSprite?.('trainsim.station', cs * STATION_VISUAL_SCALE, cs * STATION_VISUAL_SCALE, { fitMode: 'contain' });
        if (sprite) {
            gfx.addChild(sprite);
            return;
        }
        const g = new Graphics();
        const r = cs * 0.26;
        g.circle(0, 0, r);
        g.fill({ color: TRAINSIM_CONFIG.COLOR_STATION, alpha: 0.4 });
        g.stroke({ color: TRAINSIM_CONFIG.COLOR_STATION, width: 3 });
        gfx.addChild(g);
        const label = new Text({ text: 'S', style: ENTITY_LABEL_STYLE });
        label.anchor.set(0.5);
        gfx.addChild(label);
    }
}
